import { useEffect, useState, useCallback, useRef } from 'react';
import { formatDate } from '../utils/dateUtils';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../App';
import { fillColor, fillPercent, isFullyStaffed } from '../utils/fillColor';
import { statusAge } from '../utils/statusAge';
import EventDetailSheet from '../components/EventDetailSheet';

const Icon = ({ name, size=18 }) => {
  const s={width:size,height:size};
  const p={fill:'none',stroke:'currentColor',strokeWidth:1.8,strokeLinecap:'round'};
  const icons = {
    dashboard:<svg viewBox="0 0 24 24" style={s} {...p}><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>,
    staff:<svg viewBox="0 0 24 24" style={s} {...p}><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>,
    events:<svg viewBox="0 0 24 24" style={s} {...p}><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>,
    applications:<svg viewBox="0 0 24 24" style={s} {...p}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
    updates:<svg viewBox="0 0 24 24" style={s} {...p}><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>,
    logout:<svg viewBox="0 0 24 24" style={s} {...p}><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9"/></svg>,
    plus:<svg viewBox="0 0 24 24" style={s} {...p}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
    check:<svg viewBox="0 0 24 24" style={s} {...p}><path d="M20 6L9 17l-5-5"/></svg>,
    eye:<svg viewBox="0 0 24 24" style={s} {...p}><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>,
    x:<svg viewBox="0 0 24 24" style={s} {...p}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
    edit:<svg viewBox="0 0 24 24" style={s} {...p}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
    sun:<svg viewBox="0 0 24 24" style={s} {...p}><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>,
    moon:<svg viewBox="0 0 24 24" style={s} {...p}><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>,
    standby:<svg viewBox="0 0 24 24" style={s} {...p}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
    arrow:<svg viewBox="0 0 24 24" style={s} {...p}><path d="M5 12h14M12 5l7 7-7 7"/></svg>,
    trash:<svg viewBox="0 0 24 24" style={s} {...p}><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>,
    payroll:<svg viewBox="0 0 24 24" style={s} {...p}><rect x="2" y="6" width="20" height="12" rx="2"/><circle cx="12" cy="12" r="2.5"/><path d="M6 12h.01M18 12h.01"/></svg>,
  };
  return icons[name]||null;
};

// ── Staffing bar ───────────────────────────────────────────────────────────────
function StaffingBar({ filled, total, standby=0, pending=0 }) {
  const pct = fillPercent(filled, total);
  const color = fillColor(filled, total);
  // Full is decided on the counts, not on pct===100. Rounding used to let
  // 199/200 print "Fully staffed" and "1 still needed" in the same line.
  const full = isFullyStaffed(filled, total);
  const remaining = Math.max(0,total-filled);
  return (
    <div style={{marginTop:10}}>
      <div style={{display:'flex',justifyContent:'space-between',marginBottom:5}}>
        <span style={{fontSize:12,color:'var(--text-secondary)'}}>
          <strong style={{color:full?'var(--green)':'var(--text-primary)'}}>{filled}/{total}</strong> filled
          {remaining>0 && <span style={{color:'var(--danger)',marginLeft:8}}>· {remaining} still needed</span>}
          {full && <span style={{color:'var(--green)',marginLeft:8}}>· Fully staffed ✓</span>}
        </span>
        <span style={{fontSize:11,color:'var(--text-muted)'}}>{pct}%</span>
      </div>
      <div style={{height:7,background:'var(--bg-elevated)',borderRadius:4,overflow:'hidden'}}>
        <div style={{height:'100%',width:`${pct}%`,background:color,borderRadius:4,transition:'width 0.4s'}}/>
      </div>
      <div style={{display:'flex',gap:12,marginTop:4}}>
        {standby>0 && <span style={{fontSize:11,color:'var(--amber)'}}>⏱ {standby} standby</span>}
        {pending>0 && <span style={{fontSize:11,color:'var(--text-muted)'}}>{pending} pending</span>}
      </div>
    </div>
  );
}

// ── Role staffing (per role) ───────────────────────────────────────────────────
function RoleStaffingBar({ role }) {
  return (
    <div style={{marginBottom:10}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:2}}>
        <div>
          <span style={{fontSize:12,fontWeight:600,color:'var(--text-primary)'}}>{role.role}</span>
          {role.pay_rate && <span style={{fontSize:11,color:'var(--green)',fontWeight:600,marginLeft:8}}>{role.pay_rate}</span>}
        </div>
        <span style={{fontSize:11,color:'var(--text-secondary)'}}>{role.slots_filled}/{role.spots_available}</span>
      </div>
      <div style={{height:5,background:'var(--bg-elevated)',borderRadius:3,overflow:'hidden'}}>
        <div style={{height:'100%',width:`${fillPercent(role.slots_filled, role.spots_available)}%`,background:fillColor(role.slots_filled, role.spots_available),borderRadius:3,transition:'width 0.4s'}}/>
      </div>
    </div>
  );
}

// ── Skills (per-environment data from GET /api/skills) ────────────────────────
// One fetch per page load, shared by the event-form role picker and SkillEditor.
let _skillsPromise = null;
const fetchSkills = () => {
  if (!_skillsPromise) {
    _skillsPromise = axios.get('/api/skills').then(r => r.data).catch(() => { _skillsPromise = null; return []; });
  }
  return _skillsPromise;
};
function useSkills() {
  const [skills, setSkills] = useState([]);
  useEffect(() => { let on = true; fetchSkills().then(s => { if (on) setSkills(s); }); return () => { on = false; }; }, []);
  return skills;
}
// Group into [{category, items:[skill]}] preserving sort_order-driven order.
const groupSkills = (skills) => {
  const groups = [];
  skills.forEach(s => {
    let g = groups.find(x => x.category === s.category);
    if (!g) { g = { category: s.category, items: [] }; groups.push(g); }
    g.items.push(s);
  });
  return groups;
};

// ── Event Form (shared by Post + Edit) ────────────────────────────────────────
const emptyForm = {title:'',date:'',time_start:'',time_end:'',location:'',street_address:'',dress_code:'',description:'',transport:''};
const emptyRole = () => ({role:'',spots:1,pay_amount:'',pay_unit:'hourly',pay_rate:'',required_tiers:[]});

// Session M1: map a role row to the API payload — shared by day 1 and every
// additional day of a multi-day event.
const roleToPayload = r=>({role:r.role,spots:parseInt(r.spots)||1,pay_amount:r.pay_amount,pay_unit:r.pay_unit||'hourly',pay_rate:r.pay_rate||'',required_tiers:(r.required_tiers||[]).join(',')});

// Session M1: next calendar day of an ISO date (noon-anchored — the dateUtils
// trick — so the UTC conversion in toISOString can never shift the day).
const nextIsoDay = iso => {
  try { const d = new Date(`${iso}T12:00`); d.setDate(d.getDate()+1); const s=d.toISOString().slice(0,10); return /^\d{4}-\d{2}-\d{2}$/.test(s)?s:''; } catch { return ''; }
};

// ── Roles editor (Session M1: extracted from EventForm) ───────────────────────
// One instance per DAY of the event — day 1 and every additional day carry
// their own roles, so different roles, quantities, rates and tiers per day are
// supported natively. Behaviour and markup are identical to the pre-M1 inline
// version.
function RolesEditor({ roles, setRoles, skillList }) {
  const setRole = (i,k,v) => setRoles(r=>{const n=[...r];n[i]={...n[i],[k]:v};return n;});
  const addRole = () => setRoles(r=>[...r,emptyRole()]);
  const removeRole = i => setRoles(r=>r.filter((_,idx)=>idx!==i));
  return (
    <div style={{marginBottom:0}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
        <label className="form-label" style={{marginBottom:0}}>Roles Needed</label>
        <button type="button" className="btn btn-ghost btn-sm" onClick={addRole}>+ Add role</button>
      </div>
      <div style={{background:'var(--bg-surface)',border:'1px solid var(--border)',borderRadius:8,padding:12}}>
        {roles.map((r,i)=>(
          <div key={i} style={{marginBottom:10,background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:8,padding:'10px 12px'}}>
            <div style={{display:'grid',gridTemplateColumns:'1fr 80px auto',gap:8,marginBottom:8,alignItems:'center'}}>
              <select className="form-select" value={r.role} onChange={e=>setRole(i,'role',e.target.value)} required>
                <option value="">Select role...</option>
                {/* Legacy guard: editing an event whose role predates the current
                    skill set must not silently blank the role field. */}
                {r.role && !skillList.some(s=>s.name===r.role) && <option value={r.role}>{r.role}</option>}
                {groupSkills(skillList).map(g=>(
                  <optgroup key={g.category} label={g.category}>
                    {g.items.map(s=><option key={s.id} value={s.name}>{s.name}</option>)}
                  </optgroup>
                ))}
              </select>
              <div style={{position:'relative'}}>
                <input className="form-input" type="number" min="1" max="50" value={r.spots} onChange={e=>setRole(i,'spots',e.target.value)} placeholder="Slots"/>
                <span style={{position:'absolute',right:6,top:'50%',transform:'translateY(-50%)',fontSize:10,color:'var(--text-muted)',pointerEvents:'none'}}>slots</span>
              </div>
              {roles.length>1
                ? <button type="button" style={{background:'none',border:'none',cursor:'pointer',color:'var(--text-muted)',padding:4}} onClick={()=>removeRole(i)}><Icon name="trash" size={15}/></button>
                : <div/>
              }
            </div>
            <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
              <label style={{fontSize:11,fontWeight:600,color:'var(--text-secondary)',whiteSpace:'nowrap'}}>Pay (R):</label>
              <input
                className="form-input"
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                disabled={r.pay_unit==='per_staff'}
                style={{width:90,fontSize:12,padding:'7px 10px',opacity:r.pay_unit==='per_staff'?0.5:1}}
                placeholder={r.pay_unit==='per_staff'?'per staff':'e.g. 250'}
                value={r.pay_unit==='per_staff'?'':(r.pay_amount??'')}
                onChange={e=>setRole(i,'pay_amount',e.target.value)}
              />
              <div style={{display:'flex',border:'1px solid var(--border)',borderRadius:6,overflow:'hidden'}}>
                {[['hourly','Per hour'],['daily','Per day'],['per_staff','Per staff'],['per_event','Per event']].map(([u,lbl])=>(
                  <button
                    key={u}
                    type="button"
                    onClick={()=>setRole(i,'pay_unit',u)}
                    style={{padding:'7px 12px',fontSize:11,fontWeight:600,cursor:'pointer',border:'none',background:(r.pay_unit||'hourly')===u?'var(--accent)':'var(--bg-card)',color:(r.pay_unit||'hourly')===u?'#fff':'var(--text-secondary)'}}
                  >
                    {lbl}
                  </button>
                ))}
              </div>
            </div>
            {r.pay_unit==='per_staff' && (
              <div style={{fontSize:11,color:'var(--text-muted)',marginTop:6,lineHeight:1.5,background:'var(--bg-surface)',border:'1px solid var(--border)',borderRadius:6,padding:'7px 10px'}}>
                💡 Each staff member is paid their own hourly rate from the Staff Portal. Staff without a rate set won't be able to apply for this role until you assign one.
              </div>
            )}
            {r.pay_unit==='per_event' && (
              <div style={{fontSize:11,color:'var(--text-muted)',marginTop:6,lineHeight:1.5,background:'var(--bg-surface)',border:'1px solid var(--border)',borderRadius:6,padding:'7px 10px'}}>
                💡 A flat amount for the whole booking — each confirmed staff member earns this once for the event, regardless of hours worked. No-shows are still paid nothing.
              </div>
            )}
            <div style={{marginTop:8}}>
              <label style={{fontSize:11,fontWeight:600,color:'var(--text-secondary)',display:'block',marginBottom:4}}>Restrict to tiers <span style={{fontWeight:400,color:'var(--text-muted)'}}>(leave empty to show to all staff)</span></label>
              <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                {TIER_OPTIONS.map(t=>{
                  const selected=(r.required_tiers||[]).includes(t);
                  return (
                    <button
                      key={t} type="button"
                      onClick={()=>{
                        const current=r.required_tiers||[];
                        const updated=selected?current.filter(x=>x!==t):[...current,t];
                        setRole(i,'required_tiers',updated);
                      }}
                      style={{padding:'3px 10px',borderRadius:20,border:`1px solid ${selected?'var(--accent)':'var(--border)'}`,background:selected?'rgba(var(--accent-rgb),0.08)':'var(--bg-elevated)',color:selected?'var(--accent)':'var(--text-secondary)',fontSize:11,fontWeight:600,cursor:'pointer',textTransform:'capitalize'}}
                    >{t}</button>
                  );
                })}
              </div>
            </div>
          </div>
        ))}
        <div style={{fontSize:11,color:'var(--text-muted)',marginTop:8}}>
          Total staff needed: <strong>{roles.reduce((a,r)=>a+(parseInt(r.spots)||0),0)}</strong>
        </div>
      </div>
    </div>
  );
}

function EventForm({ initial, initialRoles, onSave, onDone, onCancel, isEdit=false, eventId=null, existingBriefUrl=null, seriesInfo=null }) {
  const skillList = useSkills();
  const [form, setForm] = useState(initial || emptyForm);
  const [roles, setRoles] = useState(initialRoles || [emptyRole()]);
  // Session M1: additional days of a multi-day event (create only — editing
  // always edits ONE day). Each entry is a full day: own date, times and roles.
  const [extraDays, setExtraDays] = useState([]);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState('');
  const [briefFile, setBriefFile] = useState(null);
  const [briefUploading, setBriefUploading] = useState(false);
  const [currentBriefUrl, setCurrentBriefUrl] = useState(existingBriefUrl || null);
  const briefInputRef = useRef(null);

  const set = k => e => setForm(f=>({...f,[k]:e.target.value}));

  // Session M1: "Add another day" duplicates the PREVIOUS day's times and
  // roles (day 1 if none yet) and steps the date forward one day — every field
  // stays editable per day afterwards.
  const addDay = () => setExtraDays(days=>{
    const prev = days.length>0 ? days[days.length-1] : { date: form.date, time_start: form.time_start, time_end: form.time_end, roles };
    return [...days, {
      date: prev.date ? nextIsoDay(prev.date) : '',
      time_start: prev.time_start,
      time_end: prev.time_end,
      roles: (prev.roles && prev.roles.length>0 ? prev.roles : roles).map(r=>({ ...r, required_tiers: [...(r.required_tiers||[])] })),
    }];
  });
  const removeDay = i => setExtraDays(days=>days.filter((_,idx)=>idx!==i));
  const setDayField = (i,k) => e => { const v=e.target.value; setExtraDays(days=>{const n=[...days];n[i]={...n[i],[k]:v};return n;}); };
  const setDayRoles = i => updater => setExtraDays(days=>{
    const n=[...days];
    const cur=n[i].roles;
    n[i]={...n[i], roles: typeof updater==='function' ? updater(cur) : updater};
    return n;
  });

  const submit = async e => {
    e.preventDefault();
    if (roles.some(r=>!r.role) || extraDays.some(d=>d.roles.some(r=>!r.role))) return alert('Please select a role for each row');
    if (extraDays.some(d=>!d.date)) return alert('Please set a date for each day');
    // Session AL: EITHER field satisfies this. `required` used to sit on
    // Location alone, so a venue with an address but no name could not be
    // saved and agents invented one — which staff then read as the venue name.
    // ⚠️ Non-empty only. Whether the place is findable on a map is the agent's
    // responsibility, not something the platform should try to decide.
    if (!String(form.location||'').trim() && !String(form.street_address||'').trim()) {
      return alert('Please give either a location or a street address so staff know where to go.');
    }
    setSaving(true);
    try {
      const payload = {...form, roles: roles.map(roleToPayload)};
      if (!isEdit && extraDays.length > 0) {
        payload.extra_days = extraDays.map(d=>({ date:d.date, time_start:d.time_start, time_end:d.time_end, roles:d.roles.map(roleToPayload) }));
      }
      const savedId = await onSave(payload);
      if (briefFile && savedId) {
        setBriefUploading(true);
        const fd = new FormData();
        fd.append('brief', briefFile);
        const { data } = await axios.post(`/api/agent/events/${savedId}/brief`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setCurrentBriefUrl(data.brief_url);
        setBriefFile(null);
        setBriefUploading(false);
      }
      setDone(isEdit ? 'Event updated! Affected staff have been notified.' : extraDays.length>0 ? `${extraDays.length+1}-day event created!` : 'Event created!');
      if (onDone) { onDone(); return; }
      setTimeout(()=>setDone(''),3000);
    } catch(err) {
      // Session V (V-x): this used to alert err.message, which for an axios
      // rejection is the useless string "Request failed with status code 409" —
      // the server's actual explanation sits in err.response.data.error and was
      // being thrown away. Session U's save guard names the locked field and
      // tells the agent to un-settle in Payroll; none of that ever reached her.
      alert(err.response?.data?.error || err.message || 'Could not save the event. Please try again.');
    }
    setSaving(false);
  };

  const removeBrief = async () => {
    if (eventId) await axios.delete(`/api/agent/events/${eventId}/brief`);
    setCurrentBriefUrl(null);
    setBriefFile(null);
    if (briefInputRef.current) briefInputRef.current.value = '';
  };

  return (
    <form onSubmit={submit}>
      {done && <div style={{padding:12,background:'rgba(22,163,74,0.1)',border:'1px solid rgba(22,163,74,0.3)',borderRadius:8,color:'var(--green)',marginBottom:16,fontSize:13}}>✓ {done}</div>}
      <div className="form-group">
        <label className="form-label">Event Title</label>
        <input className="form-input" placeholder="e.g. Smith Wedding Reception" value={form.title} onChange={set('title')} required/>
      </div>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12}}>
        <div className="form-group"><label className="form-label">Date</label><input className="form-input" type="date" value={form.date} onChange={set('date')} required/></div>
        <div className="form-group"><label className="form-label">Start</label><input className="form-input" type="time" value={form.time_start} onChange={set('time_start')}/></div>
        <div className="form-group"><label className="form-label">End</label><input className="form-input" type="time" value={form.time_end} onChange={set('time_end')}/></div>
      </div>
      <div className="form-group"><label className="form-label">Location</label><input className="form-input" placeholder="e.g. Greenpoint Stadium (or leave blank if it has no name)" value={form.location} onChange={set('location')}/></div>
      <div className="form-group"><label className="form-label">Street Address</label><input className="form-input" placeholder="e.g. Fritz Sonnenberg Road, Green Point, Cape Town, 8051" value={form.street_address} onChange={set('street_address')}/></div>
      <div className="form-group"><label className="form-label">Dress Code</label><input className="form-input" placeholder="e.g. Black & White" value={form.dress_code} onChange={set('dress_code')}/></div>
      <div className="form-group"><label className="form-label">Transport / Parking</label><input className="form-input" placeholder="e.g. Parking on site" value={form.transport} onChange={set('transport')}/></div>
      <div className="form-group"><label className="form-label">Event Brief / Description</label><textarea className="form-textarea" placeholder="Describe expectations..." value={form.description} onChange={set('description')}/></div>

      <div className="form-group">
        <label className="form-label">Event Brief PDF <span style={{fontWeight:400,color:'var(--text-muted)'}}>(optional)</span></label>
        {currentBriefUrl ? (
          <div style={{display:'flex',alignItems:'center',gap:10,padding:'10px 12px',background:'var(--bg-surface)',border:'1px solid var(--border)',borderRadius:8}}>
            <span style={{fontSize:13,color:'var(--text-primary)',flex:1}}>📄 Brief uploaded</span>
            <button type="button" onClick={()=>window.open(currentBriefUrl,'_blank','noopener,noreferrer')} style={{fontSize:12,color:'var(--accent)',background:'none',border:'none',cursor:'pointer',fontWeight:600}}>View ↗</button>
            <button type="button" onClick={removeBrief} style={{fontSize:12,color:'var(--text-muted)',background:'none',border:'none',cursor:'pointer'}}>Remove</button>
          </div>
        ) : briefFile ? (
          <div style={{display:'flex',alignItems:'center',gap:10,padding:'10px 12px',background:'var(--bg-surface)',border:'1px solid var(--border)',borderRadius:8}}>
            <span style={{fontSize:13,color:'var(--text-primary)',flex:1}}>📄 {briefFile.name}</span>
            <button type="button" onClick={removeBrief} style={{fontSize:12,color:'var(--text-muted)',background:'none',border:'none',cursor:'pointer'}}>Remove</button>
          </div>
        ) : (
          <div onClick={()=>briefInputRef.current?.click()} style={{padding:'12px 16px',background:'var(--bg-surface)',border:'1px dashed var(--border)',borderRadius:8,cursor:'pointer',textAlign:'center',fontSize:13,color:'var(--text-secondary)'}}>
            + Upload PDF brief
          </div>
        )}
        <input ref={briefInputRef} type="file" accept="application/pdf" style={{display:'none'}} onChange={e=>{if(e.target.files[0]) setBriefFile(e.target.files[0]);}}/>
        {briefUploading && <div style={{fontSize:12,color:'var(--text-muted)',marginTop:6}}>Uploading brief...</div>}
      </div>

      {/* Session M1: edit mode edits ONE day of a series — make that explicit */}
      {isEdit && seriesInfo && seriesInfo.series_size > 1 && (
        <div style={{marginBottom:16,padding:'10px 14px',background:'rgba(37,99,235,0.06)',border:'1px solid rgba(37,99,235,0.25)',borderRadius:8,fontSize:12,color:'var(--text-secondary)',lineHeight:1.5}}>
          📆 <strong style={{color:'var(--text-primary)'}}>Day {seriesInfo.day_number} of {seriesInfo.series_size}</strong> — changes here apply to this day only. Edit the other days from their own cards.
        </div>
      )}

      {/* Day 1 roles (Session M1: shared RolesEditor, one per day) */}
      <div style={{marginBottom:16}}>
        {!isEdit && extraDays.length>0 && (
          <div style={{fontSize:12,fontWeight:700,color:'var(--text-primary)',marginBottom:8,paddingBottom:6,borderBottom:'1px solid var(--border-light)'}}>
            Day 1 — {form.date || 'set the date above'}{form.time_start?` · ${form.time_start}–${form.time_end}`:''}
          </div>
        )}
        <RolesEditor roles={roles} setRoles={setRoles} skillList={skillList}/>
      </div>

      {/* Session M1: additional days (create only). Each day is a full event —
          own date, times and roles — grouped under one series card after save. */}
      {!isEdit && (
        <div style={{marginBottom:16}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
            <label className="form-label" style={{marginBottom:0}}>Multi-Day Event <span style={{fontWeight:400,color:'var(--text-muted)'}}>(optional)</span></label>
            <button type="button" className="btn btn-ghost btn-sm" onClick={addDay}>+ Add another day</button>
          </div>
          {extraDays.length===0 ? (
            <div style={{fontSize:11,color:'var(--text-muted)',lineHeight:1.6,padding:'10px 12px',background:'var(--bg-surface)',border:'1px dashed var(--border)',borderRadius:8}}>
              Running over several days? Each added day becomes its own shift — staff apply, check in and are paid <strong>per day</strong>, and the days group under one card. Adding a day copies the previous day's times and roles; everything stays editable per day.
            </div>
          ) : (
            <div style={{display:'grid',gap:12}}>
              {extraDays.map((d,i)=>(
                <div key={i} style={{background:'var(--bg-surface)',border:'1px solid var(--border)',borderRadius:10,padding:'12px 14px'}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
                    <div style={{fontSize:12,fontWeight:700,color:'var(--text-primary)'}}>Day {i+2}</div>
                    <button type="button" title="Remove this day" style={{background:'none',border:'none',cursor:'pointer',color:'var(--text-muted)',padding:4}} onClick={()=>removeDay(i)}><Icon name="trash" size={15}/></button>
                  </div>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12}}>
                    <div className="form-group"><label className="form-label">Date</label><input className="form-input" type="date" value={d.date} onChange={setDayField(i,'date')} required/></div>
                    <div className="form-group"><label className="form-label">Start</label><input className="form-input" type="time" value={d.time_start} onChange={setDayField(i,'time_start')}/></div>
                    <div className="form-group"><label className="form-label">End</label><input className="form-input" type="time" value={d.time_end} onChange={setDayField(i,'time_end')}/></div>
                  </div>
                  <RolesEditor roles={d.roles} setRoles={setDayRoles(i)} skillList={skillList}/>
                </div>
              ))}
              <div style={{fontSize:11,color:'var(--text-muted)',lineHeight:1.6}}>
                {extraDays.length+1} days total. Staff apply per day; each day has its own roster, check-in and payroll line. The PDF brief above attaches to every day.
              </div>
              {[roles,...extraDays.map(d=>d.roles)].some(list=>(list||[]).some(r=>r.pay_unit==='per_event')) && (
                <div style={{fontSize:11,color:'#a16207',background:'rgba(234,179,8,0.08)',border:'1px solid rgba(234,179,8,0.35)',borderRadius:6,padding:'7px 10px',lineHeight:1.6}}>
                  💡 <strong>"Per event" on a multi-day event means per day:</strong> staff earn that flat amount for each day they work, and their app shows it as "for the day". One flat rate spanning the whole series is planned for a later phase.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div style={{display:'flex',gap:10}}>
        <button className="btn btn-primary" type="submit" disabled={saving||briefUploading} style={{flex:4}}>
          {briefUploading ? 'Uploading brief...' : saving ? 'Saving...' : isEdit ? 'Save Changes & Notify Staff' : extraDays.length>0 ? `Create ${extraDays.length+1}-Day Event (draft)` : 'Create Event (draft)'}
        </button>
        <button className="btn btn-ghost" type="button" onClick={onCancel} style={{flex:1}}>Cancel</button>
      </div>
    </form>
  );
}

// ── Fast-Assign Panel (slide up when clicking an event) ───────────────────────
function FastAssignPanel({ event, onClose, onUpdate }) {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  // Session AA: the same ApplicantInfoFlash the Applications page uses. Declared
  // below this component, but function declarations hoist, so it resolves fine.
  const [infoApp, setInfoApp] = useState(null);

  const loadApps = useCallback(async () => {
    const r = await axios.get(`/api/agent/events/${event.id}/applications`);
    setApps(r.data);
    setLoading(false);
  }, [event.id]);

  useEffect(()=>{ loadApps(); },[loadApps]);


  // After any status change, recalculate already_approved_for_event for all
  // sibling applications (same user, same event, different application)
  const recalcApproved = (apps, changedId, newStatus) => {
    // First apply the status change
    const updated = apps.map(x => x.id === changedId ? {...x, status: newStatus} : x);
    // Then recalculate the flag for every app based on updated state
    return updated.map(x => {
      const approvedElsewhere = updated.filter(
        y => y.user_id === x.user_id && y.event_id === x.event_id && y.id !== x.id && y.status === 'accepted'
      ).length;
      return {...x, already_approved_for_event: approvedElsewhere};
    });
  };

  const [conflictMsg, setConflictMsg] = useState('');
  // Session M2: accept this person across every day of the series they applied
  // to. The server runs every guard per day and reports what it could and
  // couldn't do — the report renders below so a half-filled series is never
  // silent.
  const [seriesReport, setSeriesReport] = useState(null);
  const [seriesBusyId, setSeriesBusyId] = useState(null);
  const acceptSeries = async (id) => {
    setSeriesBusyId(id); setConflictMsg('');
    try {
      const r = await axios.post(`/api/agent/applications/${id}/accept-series`);
      setSeriesReport(r.data);
      await loadApps();
      onUpdate && onUpdate();
    } catch(e) {
      setConflictMsg(e.response?.data?.error || 'Could not accept across the series. Please try again.');
    }
    setSeriesBusyId(null);
  };
  const update = async (id, status) => {
    try {
      await axios.patch(`/api/agent/applications/${id}`,{status});
      if (status === 'accepted') {
        // Re-fetch so silently rejected conflicting apps disappear immediately
        await loadApps();
      } else {
        setApps(a => recalcApproved(a, id, status));
      }
      setConflictMsg('');
      onUpdate && onUpdate();
    } catch(e) {
      // Session V (V-x): was `if (status === 409)`, which silently swallowed
      // every other server refusal — including D1's new 400. Show whatever
      // reason the server gave; a network drop carries no .error and still
      // shows nothing, exactly as before.
      const msg = e.response?.data?.error;
      if (msg) setConflictMsg(msg);
    }
  };
  const promote = async (id) => {
    try {
      await axios.patch(`/api/agent/applications/${id}/promote`);
      await loadApps();
      setConflictMsg('');
      onUpdate && onUpdate();
    } catch(e) {
      const msg = e.response?.data?.error;
      if (msg) setConflictMsg(msg);
    }
  };

  // Group by role
  const byRole = {};
  apps.forEach(a=>{ if(!byRole[a.role]) byRole[a.role]=[]; byRole[a.role].push(a); });

  return (
    <div onClick={e=>{if(e.target===e.currentTarget)onClose();}} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:100,padding:'20px',backdropFilter:'blur(2px)'}}>
      <div style={{background:'var(--bg-card)',borderRadius:'var(--radius-lg)',padding:'28px 32px',width:'100%',maxWidth:720,maxHeight:'88vh',overflowY:'auto',boxShadow:'0 8px 40px rgba(0,0,0,0.25)'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:4}}>
          <div>
            <div className="modal-title" style={{display:'flex',alignItems:'center',flexWrap:'wrap',gap:8}}>
              <span>{event.title}</span>
              {(event.series_size||1)>1&&<span className="badge badge-series" style={{fontSize:9}}>DAY {event.day_number}/{event.series_size}</span>}
            </div>
            <div className="modal-sub">{formatDate(event.date)} · {[event.location, event.street_address].filter(Boolean).join(', ')} — fast assign applicants</div>
            {/* Session M1 tweak: rosters stay manageable while unpublished (deliberate —
                that's how a roster is rebuilt before republishing), so make the
                visibility state explicit instead of leaving it implied. */}
            {!event.published && (
              <div style={{margin:'8px 0 0',padding:'7px 12px',background:'rgba(217,119,6,0.08)',border:'1px solid rgba(217,119,6,0.3)',borderRadius:6,fontSize:12,color:'#a16207',lineHeight:1.5}}>
                Unpublished — staff can't see this in Book a Shift or apply, but anyone already confirmed still sees it in their Schedule (with a note suggesting they check in with you).
              </div>
            )}
            {conflictMsg && <div style={{margin:'8px 0',padding:'8px 12px',background:'rgba(var(--danger-rgb),0.07)',border:'1px solid rgba(var(--danger-rgb),0.25)',borderRadius:6,fontSize:12,color:'var(--danger)'}}>{conflictMsg}</div>}
          </div>
          <button style={{background:'none',border:'none',cursor:'pointer',color:'var(--text-secondary)',padding:4}} onClick={onClose}><Icon name="x" size={20}/></button>
        </div>

        {/* Per-role staffing */}
        {event.roles && event.roles.length>0 && (
          <div style={{background:'var(--bg-surface)',border:'1px solid var(--border)',borderRadius:8,padding:14,marginBottom:20}}>
            {event.roles.map(r=><RoleStaffingBar key={r.id} role={r}/>)}
          </div>
        )}

        {seriesReport && (
          <div style={{margin:'8px 0 12px',padding:'10px 14px',background:seriesReport.skipped?.length?'rgba(202,138,4,0.08)':'rgba(22,163,74,0.08)',border:`1px solid ${seriesReport.skipped?.length?'rgba(202,138,4,0.4)':'rgba(22,163,74,0.3)'}`,borderRadius:8,fontSize:12,color:'var(--text-primary)',lineHeight:1.6}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:10}}>
              <div>
                {seriesReport.accepted?.length>0 && <div>✓ <strong>Confirmed:</strong> {seriesReport.accepted.map(x=>`Day ${x.day_number} (${x.role})`).join(' · ')}</div>}
                {seriesReport.skipped?.length>0 && <div style={{color:'#a16207',marginTop:seriesReport.accepted?.length?4:0}}>⚠ <strong>Not filled:</strong> {seriesReport.skipped.map(x=>`Day ${x.day_number} — ${x.reason}`).join(' · ')}</div>}
              </div>
              <button onClick={()=>setSeriesReport(null)} style={{background:'none',border:'none',cursor:'pointer',color:'var(--text-muted)',fontSize:14,lineHeight:1,padding:2}}>✕</button>
            </div>
          </div>
        )}
        {loading ? <div className="loading">Loading applicants...</div> : apps.length===0 ? (
          <div className="empty-state">No applications yet for this event.</div>
        ) : (
          Object.entries(byRole).map(([role, roleApps])=>(
            <div key={role} style={{marginBottom:20}}>
              <div style={{fontWeight:700,fontSize:13,color:'var(--text-primary)',textTransform:'uppercase',letterSpacing:'0.8px',marginBottom:10,paddingBottom:6,borderBottom:'1.5px solid var(--text-primary)'}}>{role}</div>
              {roleApps.map(a=>(
                <div key={a.id} style={{display:'flex',alignItems:'center',gap:12,padding:'10px 0',borderBottom:'1px solid var(--border)',flexWrap:'wrap'}}>
                  <div style={{flex:1,minWidth:160}}>
                    <div style={{fontWeight:600,fontSize:14,display:'flex',gap:8,alignItems:'center',color:'var(--text-primary)'}}>
                      {a.name}
                      {/* Session AA: decide without leaving the panel */}
                      <InfoButton onClick={()=>setInfoApp(a)}/>
                      {a.tier && <TierBadge tier={a.tier}/>}
                      {a.is_standby===1 && <span className="badge badge-agency" style={{fontSize:9}}>STANDBY</span>}
                    </div>
                    <div style={{fontSize:11,color:'var(--text-secondary)'}}>{a.shifts_completed > 0 ? `${Number(a.reliability_score).toFixed(1)} · ${a.shifts_completed} shift${a.shifts_completed===1?'':'s'}` : 'New'}</div>
                  </div>
                  <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:2}}>
                    <span className={`badge badge-${a.status}`}>{a.status}</span>
                    {statusAge(a.status_changed_at) && <div style={{fontSize:10,color:'var(--text-muted)',marginTop:3}}>{statusAge(a.status_changed_at)}</div>}
                  </div>
                  <div style={{display:'flex',gap:6}}>
                    {a.status==='pending' && a.is_standby===0 && <>
                      <button className="btn btn-reject btn-sm" onClick={()=>update(a.id,'rejected')}>Reject</button>
                      <button className="btn btn-accept btn-sm" onClick={()=>update(a.id,'accepted')}>Accept</button>
                      {/* Session M2: appears when they've applied to other days of this
                          series too. Same .btn-accept styling as Accept — brand-neutral
                          (COUNT() arrives as a string from Postgres, hence Number()). */}
                      {(event.series_size||1)>1 && Number(a.series_pending_count||0)>0 && (
                        <button className="btn btn-accept btn-sm" disabled={seriesBusyId===a.id}
                          onClick={()=>acceptSeries(a.id)}>
                          {seriesBusyId===a.id?'Accepting…':`Accept all ${Number(a.series_pending_count||0)+1} days`}
                        </button>
                      )}
                    </>}
                    {a.status==='pending' && a.is_standby===1 && <>
                      <button className="btn btn-reject btn-sm" onClick={()=>update(a.id,'rejected')}>Reject</button>
                      <button className="btn btn-accept btn-sm" onClick={()=>update(a.id,'accepted')}>Approve Standby</button>
                    </>}
                    {a.status==='accepted' && a.is_standby===1 && (
                      <button className="btn btn-accept btn-sm" onClick={()=>promote(a.id)}>Promote to confirmed</button>
                    )}
                    {a.status==='accepted' && a.is_standby===0 && (
                      <button className="btn btn-reject btn-sm" onClick={()=>update(a.id,'rejected')}>Remove</button>
                    )}
                    {a.status==='rejected' && (
                      <button className="btn btn-ghost btn-sm" onClick={()=>update(a.id,'pending')} style={{fontSize:11}}>↩ Reinstate to pending</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ))
        )}
      </div>
      {infoApp && <ApplicantInfoFlash app={infoApp} onClose={()=>setInfoApp(null)}/>}
    </div>
  );
}

// ── Sidebar ────────────────────────────────────────────────────────────────────
function Sidebar({ active, setActive, logout }) {
  const { dark, toggle } = useTheme();
  const items = [
    {key:'dashboard',label:'Dashboard'},{key:'applications',label:'Event Applications'},
    {key:'staff',label:'Staff Portal'},{key:'events',label:'My Events'},
    {key:'payroll',label:'Payroll'},
    {key:'post',label:'Post Event'},{key:'updates',label:'Send Update'},
  ];
  return (
    <aside className="agent-sidebar">
      <div className="sidebar-brand">
        <h1>
          <span style={{color:'var(--text-primary)',fontWeight:800}}>{process.env.REACT_APP_BRAND_PREFIX||'SHIFT'}</span>
          <span style={{color:'var(--accent)',fontWeight:800}}>{process.env.REACT_APP_BRAND_SUFFIX||'on'}</span>
        </h1>
        <span>Agent Dashboard</span>
      </div>
      {items.map(it=>(
        <button key={it.key} className={`sidebar-nav-item ${active===it.key?'active':''}`} onClick={()=>setActive(it.key)}>
          <Icon name={it.key==='post'?'plus':it.key==='updates'?'updates':it.key}/>
          {it.label}
        </button>
      ))}
      <div style={{height:1,background:'var(--border)',margin:'12px 0'}}/>
      <button className="theme-toggle" onClick={toggle}>
        <Icon name={dark?'sun':'moon'} size={16}/>
        {dark ? 'Switch to light mode' : 'Switch to dark mode'}
      </button>
      <button className="sidebar-nav-item" style={{position:'absolute',bottom:20}} onClick={logout}>
        <Icon name="logout"/> Sign Out
      </button>
    </aside>
  );
}

// ── Dashboard ─────────────────────────────────────────────────────────────────
function DashboardHome({ setActive }) {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [fastEvent, setFastEvent] = useState(null);
  const [draftCount, setDraftCount] = useState(0);
  const [refreshing, setRefreshing] = useState(false); // manual refresh (17 Jul)
  const load = () => Promise.all([
    axios.get('/api/agent/stats').then(r=>setStats(r.data)),
    // Session AK: the event being previewed as staff would see it, or null.
    axios.get('/api/events').then(r=>{
      const all = r.data;
      const now = new Date();
      // overnight-aware end: if end < start the shift ends the next day
      const eventEnd = e => {
        const start = e.time_start || '00:00';
        const end = (e.time_end && e.time_end.trim() !== '') ? e.time_end : '23:59';
        const d = new Date(`${e.date}T${end}`);
        const [sh, sm] = start.split(':').map(Number);
        const [eh, em] = end.split(':').map(Number);
        if ((eh * 60 + em) < (sh * 60 + sm)) d.setDate(d.getDate() + 1);
        return d;
      };
      setDraftCount(all.filter(e => {
        if (e.published) return false;
        try { return eventEnd(e) >= now; } catch { return true; }
      }).length);
      // Show all upcoming events (published + draft) on dashboard, soonest first
      const upcoming = all.filter(e=>{
        try { return eventEnd(e) >= now; } catch { return true; }
      }).sort((a,b)=>new Date(`${a.date}T${a.time_start||'00:00'}`)-new Date(`${b.date}T${b.time_start||'00:00'}`));
      setEvents(upcoming.slice(0,5));
    }),
  ]);
  useEffect(()=>{ load(); },[]);
  const manualLoad = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try { await load(); } finally { setRefreshing(false); }
  };
  return (
    <>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div className="agent-page-title">Dashboard</div>
        <button title="Refresh" onClick={manualLoad} disabled={refreshing}
          style={{background:'none',border:'none',cursor:'pointer',color:'var(--text-secondary)',padding:4,fontSize:18,lineHeight:1,fontFamily:'var(--font)',opacity:refreshing?0.4:1}}>
          {'\u21BB'}
        </button>
      </div>
      <div className="agent-page-sub">Welcome back, {stats?.name}. Here's today's overview.</div>
      {stats && (
        <div className="agent-stats" style={{gridTemplateColumns:'repeat(3,1fr)'}}>
          {[
            {label:'Live Events', value:stats.totalEvents, nav:'events'},
            {label:'Draft Events', value:draftCount, nav:'events'},
            {label:'Pending Applications', value:stats.pendingApplications, accent:stats.pendingApplications>0, nav:'applications'}
          ].map(s=>(
            <div
              className="agent-stat"
              key={s.label}
              onClick={() => setActive(s.nav)}
              style={{cursor:'pointer', transition:'transform 0.1s, box-shadow 0.1s'}}
              onMouseEnter={e => { e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow='0 4px 12px rgba(0,0,0,0.1)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform='translateY(0)'; e.currentTarget.style.boxShadow=''; }}
            >
              <div className={`agent-stat-value ${s.accent?'text-accent':''}`}>{s.value}</div>
              <div className="agent-stat-label">{s.label}</div>
              <div style={{fontSize:10,color:'var(--text-muted)',marginTop:4,letterSpacing:'0.3px'}}>tap to view →</div>
            </div>
          ))}
        </div>
      )}
      <div className="agent-section-card">
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
          <div style={{fontWeight:700,fontSize:15,color:'var(--text-primary)',letterSpacing:'-0.2px'}}>Event Staffing Status</div>
          <button className="btn btn-ghost btn-sm" onClick={()=>setActive('events')}>View all →</button>
        </div>
        {events.map(e=>(
          <div key={e.id} className="agent-event-row" onClick={()=>setFastEvent(e)}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
              <div>
                <div style={{fontWeight:600,fontSize:14,color:'var(--text-primary)'}}>
                  {e.title}
                  {!e.published&&<span style={{fontSize:11,color:'#ca8a04',fontWeight:600,marginLeft:8}}>(Draft)</span>}
                </div>
                <div style={{fontSize:12,color:'var(--text-secondary)'}}>{formatDate(e.date)}{e.time_start ? ` · ${e.time_start}–${e.time_end}` : ''}{e.location ? ` · ${e.location}` : ''} · {e.roles?.map(r=>r.role).join(', ')}</div>
              </div>
              <span style={{fontSize:11,color:'var(--accent)',fontWeight:600}}>click to assign →</span>
            </div>
            <StaffingBar filled={e.slots_filled||0} total={e.slots_total||0} standby={e.standby_count||0} pending={e.pending_count||0}/>
          </div>
        ))}
        {events.length===0 && <div className="empty-state">No events yet.</div>}
      </div>
      {fastEvent && <FastAssignPanel event={fastEvent} onClose={()=>setFastEvent(null)}/>}
    </>
  );
}

// ── Applications ──────────────────────────────────────────────────────────────
// ── Applicant info flash-view (photo + demographics for approval decisions) ───
// Minimal pre-approval check: height, weight, race — the fields Catherine's
// event demographic requirements run on. NEVER bank details (those are locked
// to /api/staff/me and are not in the applications payload at all).
// Session AF: Cloudinary serves the ORIGINAL upload unless the URL asks for a
// transform. Every avatar in this app renders at 72px or smaller, so without
// this the browser downloads a multi-megabyte phone photo and throws away
// almost all of it — 120 times on the Staff Portal. Inserting w_/h_/c_fill
// after /upload/ makes Cloudinary do the resizing and cache the result.
// Passes through anything that is not a Cloudinary upload URL, and anything
// already carrying a transform segment (e.g. the w_300 bank-export path).
function thumbUrl(url, px) {
  if (!url || typeof url !== 'string') return url;
  if (!url.includes('/upload/')) return url;
  if (/\/upload\/[a-z]{1,2}_[^/]*\//.test(url)) return url;
  return url.replace('/upload/', `/upload/w_${px},h_${px},c_fill,f_auto,q_auto/`);
}

function StaffAvatar({ picture, name, size = 36 }) {
  return (
    <div style={{width:size,height:size,borderRadius:'50%',background:'var(--accent)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:size*0.4,fontWeight:700,color:'#fff',overflow:'hidden',flexShrink:0}}>
      {picture ? <img src={thumbUrl(picture, size * 2)} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}}/> : (name||'S').charAt(0).toUpperCase()}
    </div>
  );
}

function InfoButton({ onClick }) {
  return (
    <button onClick={onClick} title="View staff info"
      style={{background:'none',border:'1px solid var(--border)',borderRadius:'50%',width:22,height:22,display:'inline-flex',alignItems:'center',justifyContent:'center',cursor:'pointer',color:'var(--text-muted)',padding:0,flexShrink:0}}>
      <Icon name="eye" size={12}/>
    </button>
  );
}

function ApplicantInfoFlash({ app, onClose }) {
  const fmt = (v, unit) => (v !== null && v !== undefined && v !== '') ? `${Number(v)} ${unit}` : '—';
  // Session AA: two groups with two different rules, deliberately not merged.
  //   Height / Weight / Race — POPIA special personal information, only shown
  //     while profile_consent stands. staff-export has blanked these since
  //     Session W (P1); this view could not, because the route never returned
  //     profile_consent. Blanked rather than hidden so the row reads "not
  //     shared" instead of "never asked".
  //   Gender / T-shirt / Pants size — ordinary personal information, no gate.
  // ⚠️ Withdrawal already nulls the first three in the same statement (P1), so
  // this check is defence in depth for legacy and imported rows, not the only
  // thing standing between a withdrawal and the data.
  const consented = Number(app.profile_consent) === 1;
  const rows = [
    ['Height', consented ? fmt(app.height_cm, 'cm') : '—'],
    ['Weight', consented ? fmt(app.weight_kg, 'kg') : '—'],
    ['Race', consented ? (app.race || '—') : '—'],
    ['Gender', app.gender || '—'],
    ['T-shirt', app.tshirt_size || '—'],
    ['Pants size', app.pants_size != null ? String(app.pants_size) : '—'],
  ];
  return (
    <div onClick={onClose} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.45)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
      <div onClick={e=>e.stopPropagation()} style={{background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:14,padding:'22px 24px',width:'100%',maxWidth:300,boxShadow:'0 12px 40px rgba(0,0,0,0.25)'}}>
        <div style={{display:'flex',flexDirection:'column',alignItems:'center',marginBottom:14}}>
          <StaffAvatar picture={app.picture} name={app.name} size={72}/>
          <div style={{fontWeight:700,fontSize:15,color:'var(--text-primary)',marginTop:10,textAlign:'center'}}>{app.name}</div>
        </div>
        {rows.map(([label,value])=>(
          <div key={label} style={{display:'flex',justifyContent:'space-between',padding:'8px 0',borderTop:'1px solid var(--border)',fontSize:13}}>
            <span style={{color:'var(--text-secondary)'}}>{label}</span>
            <span style={{color:'var(--text-primary)',fontWeight:600}}>{value}</span>
          </div>
        ))}
        {!consented && (
          <div style={{marginTop:10,fontSize:11,color:'var(--text-muted, var(--text-secondary))',lineHeight:1.5}}>
            Height, weight and race are hidden — this staff member has not consented to sharing them.
          </div>
        )}
        <button onClick={onClose} className="btn btn-ghost btn-sm" style={{width:'100%',marginTop:14}}>Close</button>
      </div>
    </div>
  );
}

function Applications() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterEvent, setFilterEvent] = useState('all');
  const [infoApp, setInfoApp] = useState(null);
  useEffect(()=>{ axios.get('/api/agent/applications').then(r=>setApps(r.data)).finally(()=>setLoading(false)); },[]);

  const recalcApproved = (apps, changedId, newStatus) => {
    const updated = apps.map(x => x.id === changedId ? {...x, status: newStatus} : x);
    return updated.map(x => {
      const approvedElsewhere = updated.filter(
        y => y.user_id === x.user_id && y.event_id === x.event_id && y.id !== x.id && y.status === 'accepted'
      ).length;
      return {...x, already_approved_for_event: approvedElsewhere};
    });
  };
  const loadApps = async () => {
    const r = await axios.get('/api/agent/applications');
    setApps(r.data);
  };
  const [refreshing, setRefreshing] = useState(false); // manual refresh (17 Jul)
  const manualLoad = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try { await loadApps(); } finally { setRefreshing(false); }
  };
  const [conflictMsg, setConflictMsg] = useState('');
  const update = async (id,status) => {
    try {
      await axios.patch(`/api/agent/applications/${id}`,{status});
      if (status === 'accepted') {
        await loadApps();
      } else {
        setApps(a=>recalcApproved(a,id,status));
      }
      setConflictMsg('');
    } catch(e) {
      // Session V (V-x): see the note on the modal copy above.
      const msg = e.response?.data?.error;
      if (msg) setConflictMsg(msg);
    }
  };
  const promote = async (id) => {
    await axios.patch(`/api/agent/applications/${id}/promote`);
    await loadApps();
  };

  // Status colours (16 Jul): accepted/withdrawn have no App.css badge class, so
  // they rendered colourless. Inline here: green = on the event, danger red =
  // NOT on the event (auto-withdrawn on fill/clash) — status meaning, so it
  // stays red on brand-themed clients. Rejected keeps its muted .badge class.
  const statusStyle = s =>
    s==='accepted' ? {background:'rgba(22,163,74,0.12)',color:'var(--green)',border:'1px solid rgba(22,163,74,0.3)'} :
    s==='withdrawn' ? {background:'rgba(var(--danger-rgb),0.1)',color:'var(--danger)',border:'1px solid rgba(var(--danger-rgb),0.3)'} :
    undefined;

  // Only show events that haven't passed (API already filters, but sort by date)
  const eventNames=[...new Set(apps.map(a=>a.event_title))];
  const filtered=filterEvent==='all'?apps:apps.filter(a=>a.event_title===filterEvent);
  const pending=filtered.filter(a=>a.status==='pending');
  const standbys=filtered.filter(a=>a.is_standby===1&&a.status==='accepted');
  const decided=filtered.filter(a=>a.status!=='pending'&&a.is_standby===0);

  return (
    <>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div className="agent-page-title">Event Applications</div>
        <button title="Refresh" onClick={manualLoad} disabled={refreshing}
          style={{background:'none',border:'none',cursor:'pointer',color:'var(--text-secondary)',padding:4,fontSize:18,lineHeight:1,fontFamily:'var(--font)',opacity:refreshing?0.4:1}}>
          {'\u21BB'}
        </button>
      </div>
      {conflictMsg && <div style={{margin:'0 0 12px',padding:'10px 14px',background:'rgba(var(--danger-rgb),0.07)',border:'1px solid rgba(var(--danger-rgb),0.25)',borderRadius:8,fontSize:13,color:'var(--danger)'}}>{conflictMsg}</div>}
      <div className="agent-page-sub">For quick assign, click an event card on the Dashboard. Here you can review all applications in detail.</div>
      <div style={{display:'flex',gap:8,marginBottom:20,flexWrap:'wrap'}}>
        <button className={`filter-pill ${filterEvent==='all'?'active':''}`} onClick={()=>setFilterEvent('all')}>All Events</button>
        {eventNames.map(n=><button key={n} className={`filter-pill ${filterEvent===n?'active':''}`} onClick={()=>setFilterEvent(n)}>{n.length>20?n.slice(0,20)+'…':n}</button>)}
      </div>
      {loading?<div className="loading">Loading...</div>:<>
        {pending.length>0&&(
          <div className="agent-section-card" style={{marginBottom:20}}>
            <div style={{fontWeight:700,fontSize:14,marginBottom:14,display:'flex',alignItems:'center',gap:8,color:'var(--text-primary)'}}>
              <span style={{background:'var(--danger)',color:'#fff',width:20,height:20,borderRadius:'50%',display:'inline-flex',alignItems:'center',justifyContent:'center',fontSize:11}}>{pending.length}</span>
              Pending Applications
            </div>
            {pending.map(a=>{
              return (
              <div key={a.id} className="agent-applicant-row">
                <StaffAvatar picture={a.picture} name={a.name}/>
                <div style={{flex:1,minWidth:200}}>
                  <div style={{fontWeight:600,fontSize:14,color:'var(--text-primary)',display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
                    {a.name}
                    <InfoButton onClick={()=>setInfoApp(a)}/>
                    {a.tier && <TierBadge tier={a.tier}/>}
                    {a.is_standby===1&&<span className="badge badge-agency" style={{fontSize:9}}>STANDBY</span>}
                  </div>
                  <div style={{fontSize:12,color:'var(--text-secondary)'}}><strong>{a.role}</strong> · {a.event_title} · {formatDate(a.date)}</div>
                  <div style={{fontSize:11,color:'var(--text-muted)',marginTop:2}}>{a.shifts_completed > 0 ? `${Number(a.reliability_score).toFixed(1)} · ${a.shifts_completed} shift${a.shifts_completed===1?'':'s'}` : 'New'}</div>
                </div>
                <div style={{display:'flex',gap:8}}>
                  <button className="btn btn-reject btn-sm" onClick={()=>update(a.id,'rejected')}>Reject</button>
                  <button className="btn btn-accept btn-sm" onClick={()=>update(a.id,'accepted')}>{a.is_standby===1?'Approve Standby':'Accept'}</button>
                </div>
              </div>
              );
            })}
          </div>
        )}
        {standbys.length>0&&(
          <div className="agent-section-card" style={{marginBottom:20,borderColor:'rgba(217,119,6,0.3)'}}>
            <div style={{fontWeight:700,fontSize:14,marginBottom:6,color:'var(--amber)',display:'flex',alignItems:'center',gap:8}}>
              <Icon name="standby" size={16}/> Approved Standbys
            </div>
            <div style={{fontSize:12,color:'var(--text-secondary)',marginBottom:14}}>Promote a standby to confirmed when a slot opens.</div>
            {standbys.map(a=>(
              <div key={a.id} className="agent-applicant-row">
                <StaffAvatar picture={a.picture} name={a.name}/>
                <div style={{flex:1}}>
                  <div style={{fontWeight:600,color:'var(--text-primary)',display:'flex',gap:8,alignItems:'center'}}>
                    {a.name}
                    <InfoButton onClick={()=>setInfoApp(a)}/>
                    {a.tier && <TierBadge tier={a.tier}/>}
                  </div>
                  <div style={{fontSize:12,color:'var(--text-secondary)'}}>{a.role} · {a.event_title} · {formatDate(a.date)}</div>
                  <div style={{fontSize:11,color:'var(--text-muted)'}}>{a.shifts_completed > 0 ? `${Number(a.reliability_score).toFixed(1)} · ${a.shifts_completed} shift${a.shifts_completed===1?'':'s'}` : 'New'}</div>
                </div>
                <span className="badge badge-agency">STANDBY</span>
                <button className="btn btn-accept btn-sm" onClick={()=>promote(a.id)}><Icon name="arrow" size={13}/> Promote</button>
              </div>
            ))}
          </div>
        )}
        {decided.length>0&&(
          <div className="agent-section-card">
            <div style={{fontWeight:700,fontSize:14,marginBottom:14,color:'var(--text-primary)'}}>Confirmed & Rejected</div>
            {/* Step 3.5 Pass B: swipes horizontally on iPad portrait instead of clipping */}
            <div className="table-scroll">
            <table className="data-table">
              <thead><tr><th>Staff</th><th>Event</th><th>Role</th><th>Score</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>{decided.map(a=>(
                <tr key={a.id}>
                  {/* 16 Jul: same quick-view as the pending cards — photo + eye button */}
                  <td>
                    <div style={{display:'flex',alignItems:'center',gap:10}}>
                      <StaffAvatar picture={a.picture} name={a.name} size={32}/>
                      <div>
                        <div style={{display:'flex',alignItems:'center',gap:6,flexWrap:'wrap'}}>
                          <strong>{a.name}</strong>
                          <InfoButton onClick={()=>setInfoApp(a)}/>
                          {a.tier && <TierBadge tier={a.tier}/>}
                        </div>
                        <span style={{fontSize:11,color:'var(--text-secondary)'}}>{a.phone || a.email}</span>
                      </div>
                    </div>
                  </td>
                  <td style={{fontSize:12}}>{a.event_title}</td>
                  <td><span className="badge badge-info">{a.role}</span></td>
                  <td style={{fontSize:12}}>{a.shifts_completed > 0 ? <span>{Number(a.reliability_score).toFixed(1)} <span style={{fontSize:10,color:'var(--text-muted)'}}>({a.shifts_completed} shift{a.shifts_completed===1?'':'s'})</span></span> : <span style={{fontSize:11,color:'var(--text-muted)',fontStyle:'italic'}}>New</span>}</td>
                  {/* Session AM: the age sits INSIDE the existing Status cell,
                      under the pill. A new column would push the table wider on
                      a standard screen and risk horizontal scroll — which the UX
                      rules rule out. Plain muted text, no pill, no effect. */}
                  <td>
                    <span className={`badge badge-${a.status}`} style={statusStyle(a.status)}>{a.status}</span>
                    {statusAge(a.status_changed_at) && <div style={{fontSize:10,color:'var(--text-muted)',marginTop:3}}>{statusAge(a.status_changed_at)}</div>}
                  </td>
                  <td>
                    {a.status==='accepted'&&<button className="btn btn-reject btn-sm" onClick={()=>update(a.id,'rejected')}>Remove</button>}
                    {a.status==='rejected'&&<button className="btn btn-ghost btn-sm" onClick={()=>update(a.id,'pending')} style={{fontSize:11}}>↩ Reinstate</button>}
                  </td>
                </tr>
              ))}</tbody>
            </table>
            </div>
          </div>
        )}
        {apps.length===0&&<div className="empty-state">No applications yet.</div>}
      </>}
      {infoApp && <ApplicantInfoFlash app={infoApp} onClose={()=>setInfoApp(null)}/>}
    </>
  );
}

// ── Skill Editor (multi-skill selector per staff member) ─────────────────────
function SkillEditor({ staffId, current, onUpdate }) {
  const skillList = useSkills();
  const currentArr = current ? current.split(',').map(s=>s.trim()).filter(Boolean) : [];
  const [adding, setAdding] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const available = skillList.filter(s => !currentArr.includes(s.name));

  const addSkill = async (skill) => {
    if (!skill) return;
    const updated = [...currentArr, skill].join(',');
    await onUpdate(updated);
    setAdding(false);
  };

  const removeSkill = async (skill) => {
    const updated = currentArr.filter(s => s !== skill).join(',');
    await onUpdate(updated);
    setConfirmRemove(null);
  };

  // Session AF: most staff have 1-3 skills and fit on one line. The vertical
  // cost is concentrated in the few with 10+, so cap rather than shrink alone.
  const SKILL_CAP = 6;
  const shownArr = showAll ? currentArr : currentArr.slice(0, SKILL_CAP);
  const hiddenCount = currentArr.length - shownArr.length;

  return (
    <div style={{display:'flex',flexWrap:'wrap',gap:4,alignItems:'center'}}>
      {shownArr.map(skill => (
        <span key={skill} style={{display:'inline-flex',alignItems:'center',gap:2,background:'var(--bg-elevated)',border:'1px solid var(--border)',borderRadius:20,padding:'1px 7px',fontSize:10,fontWeight:500,color:'var(--text-secondary)'}}>
          {skill}
          {confirmRemove === skill ? (
            <>
              <button onClick={()=>removeSkill(skill)} style={{background:'none',border:'1px solid var(--border)',borderRadius:4,padding:'0px 5px',fontSize:10,cursor:'pointer',color:'var(--text-muted)',marginLeft:2}}>Remove</button>
              <button onClick={()=>setConfirmRemove(null)} style={{background:'none',border:'none',color:'var(--text-muted)',cursor:'pointer',fontSize:10}}>✕</button>
            </>
          ) : (
            <button onClick={()=>setConfirmRemove(skill)} style={{background:'none',border:'none',color:'var(--text-muted)',cursor:'pointer',fontSize:12,padding:'0 2px',lineHeight:1,opacity:0.6}} title="Remove skill">×</button>
          )}
        </span>
      ))}
      {hiddenCount > 0 && (
        <button onClick={()=>setShowAll(true)}
          style={{background:'none',border:'1px solid var(--border)',borderRadius:20,padding:'1px 7px',fontSize:10,fontWeight:600,color:'var(--text-secondary)',cursor:'pointer',fontFamily:'var(--font)'}}
        >+{hiddenCount} more</button>
      )}
      {showAll && currentArr.length > SKILL_CAP && (
        <button onClick={()=>setShowAll(false)}
          style={{background:'none',border:'none',padding:'1px 4px',fontSize:10,color:'var(--text-muted)',cursor:'pointer',fontFamily:'var(--font)'}}
        >Show less</button>
      )}
      {available.length > 0 && (
        adding ? (
          <select
            className="form-select"
            autoFocus
            style={{fontSize:11,padding:'4px 8px',width:'auto',minWidth:160}}
            defaultValue=""
            onChange={e=>addSkill(e.target.value)}
            onBlur={()=>setAdding(false)}
          >
            <option value="">Select skill...</option>
            {groupSkills(available).map(g=>(
              <optgroup key={g.category} label={g.category}>
                {g.items.map(s=><option key={s.id} value={s.name}>{s.name}</option>)}
              </optgroup>
            ))}
          </select>
        ) : (
          <button
            onClick={()=>setAdding(true)}
            style={{
              background: currentArr.length === 0 ? 'rgba(234,179,8,0.1)' : 'none',
              border: currentArr.length === 0 ? '1px dashed rgba(234,179,8,0.8)' : '1px dashed var(--border)',
              borderRadius: 20,
              padding: '1px 7px',
              fontSize: 10,
              color: currentArr.length === 0 ? '#b45309' : 'var(--text-muted)',
              cursor: 'pointer',
              fontFamily: 'var(--font)',
              fontWeight: currentArr.length === 0 ? 600 : 400,
            }}
          >+ Add skill</button>
        )
      )}
    </div>
  );
}

// ── Tier label helper (used across the dashboard) ─────────────────────────────
const TIER_OPTIONS = ['platinum', 'gold', 'silver', 'bronze'];

function TierBadge({ tier }) {
  if (!tier) return null;
  const label = tier.charAt(0).toUpperCase() + tier.slice(1);
  return (
    <span style={{fontSize:10,fontWeight:700,color:'var(--text-secondary)',letterSpacing:'0.3px'}}>[{label}]</span>
  );
}

// ── Tier Editor (single select per staff member) ──────────────────────────────
function TierEditor({ staffId, current, onUpdate }) {
  const [editing, setEditing] = useState(false);

  const setTier = async (tier) => {
    await onUpdate(tier);
    setEditing(false);
  };

  if (editing) {
    return (
      <div style={{display:'flex',flexWrap:'wrap',gap:4,alignItems:'center'}}>
        {TIER_OPTIONS.map(t => (
          <button
            key={t}
            onClick={() => setTier(t)}
            style={{padding:'3px 10px',borderRadius:20,border:'1px solid var(--border)',background: current===t ? 'var(--accent)' : 'var(--bg-elevated)',color: current===t ? '#fff' : 'var(--text-secondary)',fontSize:11,fontWeight:600,cursor:'pointer',textTransform:'capitalize'}}
          >{t}</button>
        ))}
        {current && (
          <button
            onClick={() => setTier(null)}
            style={{padding:'3px 10px',borderRadius:20,border:'1px dashed var(--border)',background:'none',color:'var(--text-muted)',fontSize:11,cursor:'pointer'}}
          >Clear</button>
        )}
        <button onClick={() => setEditing(false)} style={{background:'none',border:'none',color:'var(--text-muted)',cursor:'pointer',fontSize:12,padding:'0 2px'}}>✕</button>
      </div>
    );
  }

  return (
    <div style={{display:'flex',alignItems:'center',gap:6}}>
      {current
        ? <span style={{fontSize:11,fontWeight:700,color:'var(--text-secondary)',textTransform:'capitalize'}}>[{current.charAt(0).toUpperCase()+current.slice(1)}]</span>
        : <span style={{fontSize:11,color:'var(--text-muted)',fontStyle:'italic'}}>No tier</span>
      }
      <button
        onClick={() => setEditing(true)}
        style={{background:'none',border:'1px dashed var(--border)',borderRadius:20,padding:'2px 8px',fontSize:10,color:'var(--text-secondary)',cursor:'pointer'}}
      >{current ? 'Change' : 'Set tier'}</button>
    </div>
  );
}

// ── Personal Rate Editor (per-staff hourly rate, used by per-staff-paid events) ─
function RateEditor({ staffId, current, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(current ?? '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try { await onUpdate(val); setEditing(false); }
    catch (e) { alert(e.response?.data?.error || 'Could not save rate'); }
    setSaving(false);
  };
  const clear = async () => {
    setSaving(true);
    try { await onUpdate(''); setVal(''); setEditing(false); }
    catch (e) { alert(e.response?.data?.error || 'Could not clear rate'); }
    setSaving(false);
  };

  if (editing) {
    return (
      <div style={{display:'flex',alignItems:'center',gap:4}}>
        <span style={{fontSize:11,color:'var(--text-muted)'}}>R</span>
        <input
          type="number" min="0" step="0.01" inputMode="decimal" autoFocus
          value={val}
          onChange={e=>setVal(e.target.value)}
          onKeyDown={e=>{if(e.key==='Enter')save();if(e.key==='Escape')setEditing(false);}}
          placeholder="e.g. 250"
          style={{width:70,fontSize:12,padding:'5px 8px',borderRadius:6,border:'1px solid var(--border)',background:'var(--bg-main)',color:'var(--text-primary)'}}
        />
        <span style={{fontSize:10,color:'var(--text-muted)'}}>/hr</span>
        <button onClick={save} disabled={saving} style={{padding:'4px 8px',borderRadius:6,border:'none',background:'var(--accent)',color:'#fff',fontSize:11,fontWeight:600,cursor:'pointer'}}>{saving?'…':'Save'}</button>
        {current != null && <button onClick={clear} disabled={saving} style={{padding:'4px 6px',borderRadius:6,border:'1px dashed var(--border)',background:'none',color:'var(--text-muted)',fontSize:10,cursor:'pointer'}}>Clear</button>}
        <button onClick={()=>setEditing(false)} style={{background:'none',border:'none',color:'var(--text-muted)',cursor:'pointer',fontSize:12,padding:'0 2px'}}>✕</button>
      </div>
    );
  }

  return (
    <div style={{display:'flex',alignItems:'center',gap:6}}>
      {current != null
        ? <span style={{fontSize:12,fontWeight:700,color:'var(--text-secondary)'}}>R{Number(current)}/hr</span>
        : <span style={{fontSize:11,color:'var(--text-muted)',fontStyle:'italic'}}>Not set</span>
      }
      <button
        onClick={()=>{setVal(current ?? '');setEditing(true);}}
        style={{background:'none',border:'1px dashed var(--border)',borderRadius:20,padding:'2px 8px',fontSize:10,color:'var(--text-secondary)',cursor:'pointer'}}
      >{current != null ? 'Change' : 'Set rate'}</button>
    </div>
  );
}

// ── Staff List ────────────────────────────────────────────────────────────────
// Session AB: inline name correction, same shape as RateEditor above.
// Read-only until the pencil is clicked, so a name cannot be changed by a
// stray click in a 454-row table. Enter saves, Escape cancels.
function NameEditor({ current, phone, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(current ?? '');
  const [saving, setSaving] = useState(false);

  // Re-sync from the saved value on open so a cancelled edit never lingers.
  const open = () => { setVal(current ?? ''); setEditing(true); };
  const save = async () => {
    if (!String(val).trim()) { alert('Name cannot be empty.'); return; }
    setSaving(true);
    try { await onUpdate(val); setEditing(false); }
    catch (e) { alert(e.response?.data?.error || 'Could not save name'); }
    setSaving(false);
  };

  if (editing) {
    return (
      <div style={{display:'flex',alignItems:'center',gap:4,flexWrap:'wrap'}}>
        <input
          type="text" autoFocus value={val}
          onChange={e=>setVal(e.target.value)}
          onKeyDown={e=>{if(e.key==='Enter')save();if(e.key==='Escape')setEditing(false);}}
          placeholder="Full name"
          style={{width:150,fontSize:12,padding:'5px 8px',borderRadius:6,border:'1px solid var(--border)',background:'var(--bg-main)',color:'var(--text-primary)',fontFamily:'var(--font)'}}
        />
        <button onClick={save} disabled={saving}
          style={{padding:'4px 8px',borderRadius:6,border:'none',background:'var(--accent)',color:'#fff',fontSize:11,fontWeight:600,cursor:'pointer',fontFamily:'var(--font)'}}>{saving?'…':'Save'}</button>
        <button onClick={()=>setEditing(false)}
          style={{background:'none',border:'none',color:'var(--text-muted)',cursor:'pointer',fontSize:12,padding:'0 2px'}}>✕</button>
      </div>
    );
  }
  return (
    <>
      <div style={{display:'flex',alignItems:'center',gap:6}}>
        {/* Session AF: flex:1 on the name pushes the button to the column's
            right edge, so it sits at the same x on every row instead of
            drifting with name length. Styled to match the Set tier / Change
            buttons already on this table rather than inventing a third look. */}
        <strong style={{flex:1,minWidth:0,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{current||'—'}</strong>
        <button onClick={open} title="Correct this name"
          style={{background:'none',border:'1px dashed var(--border)',borderRadius:20,padding:'2px 8px',fontSize:10,color:'var(--text-secondary)',cursor:'pointer',fontFamily:'var(--font)',flexShrink:0}}>Edit</button>
      </div>
      <span style={{fontSize:11,color:'var(--text-secondary)'}}>{phone||''}</span>
    </>
  );
}

function StaffList({ setActive, setPayrollFocus }) {
  const [staff,setStaff]=useState([]);
  const [filter,setFilter]=useState('all');
  const [tierFilter,setTierFilter]=useState('all');
  const [staffSearch,setStaffSearch]=useState('');
  const [loading,setLoading]=useState(true);
  const [inviteLink,setInviteLink]=useState('');
  const [inviteCopied,setInviteCopied]=useState(false);
  useEffect(()=>{axios.get('/api/agent/staff').then(r=>setStaff(r.data)).finally(()=>setLoading(false));},[]);

  const generateInviteLink = async () => {
    const { data } = await axios.post('/api/agent/invite-code');
    const link = `${window.location.origin}/join/${data.code}`;
    setInviteLink(link);
  };
  // Session B: new code, old link dies instantly — the leaked-link escape hatch
  const regenerateInviteLink = async () => {
    if (!window.confirm('Generate a new invite link? The current link will stop working immediately — anyone still holding it will not be able to register.')) return;
    try {
      const { data } = await axios.post('/api/agent/invite-code/regenerate');
      setInviteLink(`${window.location.origin}/join/${data.code}`);
      setInviteCopied(false);
    } catch (e) {
      alert(e.response?.data?.error || 'Could not regenerate the invite link. Please try again.');
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(inviteLink);
    setInviteCopied(true);
    setTimeout(() => setInviteCopied(false), 2000);
  };

  const updateStatus=async(id,status)=>{await axios.patch(`/api/agent/staff/${id}/status`,{status});setStaff(s=>s.map(x=>x.id===id?{...x,status}:x));};
  const updateSkills=async(id,skills)=>{await axios.patch(`/api/agent/staff/${id}/skills`,{skills});setStaff(s=>s.map(x=>x.id===id?{...x,skills}:x));};
  const updateTier=async(id,tier)=>{await axios.patch(`/api/agent/staff/${id}/tier`,{tier});setStaff(s=>s.map(x=>x.id===id?{...x,tier}:x));};
  const updateRate=async(id,personal_rate)=>{const{data}=await axios.patch(`/api/agent/staff/${id}/rate`,{personal_rate});setStaff(s=>s.map(x=>x.id===id?{...x,personal_rate:data.personal_rate}:x));};
  // Session AB: patches the row from data.name — the SERVER's trimmed and
  // whitespace-collapsed value, not what was typed — so the table shows what is
  // actually stored. Same shape as updateRate above.
  const updateName=async(id,name)=>{const{data}=await axios.patch(`/api/agent/staff/${id}/name`,{name});setStaff(s=>s.map(x=>x.id===id?{...x,name:data.name}:x));};

  const filtered=(filter==='all'?staff:staff.filter(s=>s.status===filter))
    .filter(s=>{
      if(tierFilter==='unassigned') return !s.tier;
      if(tierFilter!=='all') return s.tier===tierFilter;
      return true;
    })
    .filter(s=>{
      if(!staffSearch.trim()) return true;
      const q=staffSearch.toLowerCase();
      return (s.name||'').toLowerCase().includes(q)||(s.email||'').toLowerCase().includes(q);
    });
  return (
    <>
      <div className="agent-page-title">Staff Portal</div>
      <div className="agent-page-sub">Manage your pipeline. Set skills so staff only see relevant roles.</div>

      {/* Invite Link */}
      <div style={{background:'var(--bg-surface)',border:'1px solid var(--border)',borderRadius:10,padding:'14px 16px',marginBottom:20}}>
        <div style={{fontWeight:600,fontSize:13,marginBottom:6}}>Staff Invite Link</div>
        <div style={{fontSize:12,color:'var(--text-secondary)',marginBottom:10}}>Share this link with staff. They'll register and land in your pending queue automatically.</div>
        {!inviteLink ? (
          <button className="btn btn-primary" style={{fontSize:12,padding:'7px 14px'}} onClick={generateInviteLink}>Generate Invite Link</button>
        ) : (
          <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
            <input readOnly value={inviteLink} style={{flex:1,minWidth:0,padding:'7px 10px',borderRadius:6,border:'1px solid var(--border)',background:'var(--bg-main)',color:'var(--text-secondary)',fontSize:12,fontFamily:'monospace'}}/>
            <button className="btn btn-primary" style={{fontSize:12,padding:'7px 14px',whiteSpace:'nowrap'}} onClick={copyLink}>
              {inviteCopied ? '✓ Copied!' : 'Copy Link'}
            </button>
            <button onClick={regenerateInviteLink} title="Issue a new link and disable the current one" style={{fontSize:12,padding:'7px 14px',whiteSpace:'nowrap',borderRadius:6,border:'1px solid var(--border)',background:'var(--bg-main)',color:'var(--text-secondary)',fontWeight:600,cursor:'pointer',fontFamily:'var(--font)'}}>
              ↻ Regenerate
            </button>
          </div>
        )}
      </div>

      <input
        type="text"
        placeholder="Search by name or email..."
        value={staffSearch}
        onChange={e=>setStaffSearch(e.target.value)}
        style={{width:'100%',padding:'10px 14px',borderRadius:8,border:'1px solid var(--border)',background:'var(--bg-surface)',color:'var(--text-primary)',fontSize:13,marginBottom:12,boxSizing:'border-box',fontFamily:'var(--font)'}}
      />
      <div style={{display:'flex',gap:8,marginBottom:12}}>
        {['all','pending','approved','rejected'].map(f=><button key={f} className={`filter-pill ${filter===f?'active':''}`} onClick={()=>setFilter(f)}>{f.charAt(0).toUpperCase()+f.slice(1)}</button>)}
      </div>
      <div style={{display:'flex',gap:8,marginBottom:20,flexWrap:'wrap'}}>
        {['all',...TIER_OPTIONS,'unassigned'].map(t=>(
          <button key={t} className={`filter-pill ${tierFilter===t?'active':''}`} onClick={()=>setTierFilter(t)} style={{textTransform:'capitalize'}}>
            {t==='all'?'All Tiers':t==='unassigned'?'Unassigned':t.charAt(0).toUpperCase()+t.slice(1)}
          </button>
        ))}
      </div>
      {loading?<div className="loading">Loading...</div>:(
        <div className="agent-table-wrap">
          <table className="data-table table-tight">
            <thead><tr><th>Name</th><th>Email</th><th>Skills</th><th>Tier</th><th>Rate</th><th>Score</th><th>Status</th><th className="col-actions">Actions</th></tr></thead>
            <tbody>{filtered.map(s=>(
              <tr key={s.id}>
                <td style={{minWidth:200}}>
                  <div style={{display:'flex',alignItems:'center',gap:10}}>
                    <StaffAvatar picture={s.picture} name={s.name} size={32}/>
                    <div style={{flex:1,minWidth:0}}>
                      <NameEditor current={s.name} phone={s.phone} onUpdate={name=>updateName(s.id,name)}/>
                    </div>
                  </div>
                </td>
                {/* Session AF: the ellipsis lives on an inner div, not the td.
                    maxWidth on a table cell is advisory under table-layout:auto
                    and browsers may ignore it. Full address on hover. */}
                <td style={{fontSize:12,color:'var(--text-secondary)'}}>
                  <div style={{maxWidth:190,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={s.email}>{s.email}</div>
                </td>
                <td style={{minWidth:220}}>
                  <SkillEditor staffId={s.id} current={s.skills||''} onUpdate={skills=>updateSkills(s.id,skills)}/>
                </td>
                <td style={{minWidth:140}}>
                  <TierEditor staffId={s.id} current={s.tier||null} onUpdate={tier=>updateTier(s.id,tier)}/>
                </td>
                <td style={{minWidth:130}}>
                  <RateEditor staffId={s.id} current={s.personal_rate ?? null} onUpdate={rate=>updateRate(s.id,rate)}/>
                </td>
                <td>{s.shifts_completed > 0 ? <span>{Number(s.reliability_score).toFixed(1)} <span style={{fontSize:10,color:'var(--text-muted)'}}>({s.shifts_completed} shift{s.shifts_completed===1?'':'s'})</span></span> : <span style={{fontSize:11,color:'var(--text-muted)',fontStyle:'italic'}}>New</span>}</td>
                <td><span className={`badge badge-${s.status}`}>{s.status}</span></td>
                <td className="col-actions">
                  <div style={{display:'flex',gap:6}}>
                    {s.status==='pending' ? (
                      <>
                        <button className="btn btn-accept btn-sm" onClick={()=>updateStatus(s.id,'approved')}>Approve</button>
                        <button className="btn btn-reject btn-sm" onClick={()=>updateStatus(s.id,'rejected')}>Reject</button>
                      </>
                    ) : (
                      <button
                        className="btn btn-sm"
                        onClick={()=>updateStatus(s.id,'pending')}
                        title="Change this person's status"
                        style={{border:'1px solid var(--border)',color:'var(--text-muted)',background:'none',fontWeight:500}}
                      >Change</button>
                    )}
                    <button
                      className="btn btn-sm"
                      onClick={()=>{ setPayrollFocus&&setPayrollFocus(s.id); setActive&&setActive('payroll'); }}
                      style={{border:'1px solid var(--border)',color:'var(--text-secondary)',background:'none',fontWeight:500}}
                    >View pay</button>
                  </div>
                </td>
              </tr>
            ))}</tbody>
          </table>
          {filtered.length===0&&<div className="empty-state">No staff in this category.</div>}
        </div>
      )}
    </>
  );
}


// ── Past Event Rating Panel ───────────────────────────────────────────────────
function PastEventRatingPanel({ event, onClose }) {
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [autoRated, setAutoRated] = useState(false);
  // Payroll step 3: per-shift pay adjustment editing
  const [adjEditing, setAdjEditing] = useState(null);   // idx currently being edited
  const [adjDraft, setAdjDraft] = useState({ amount: '', note: '' });
  const [adjSaving, setAdjSaving] = useState(false);
  const [adjSavedIdx, setAdjSavedIdx] = useState(null); // Step 3.5 Pass B: transient ✓ Saved flash

  const load = async () => {
    setLoading(true);
    const r = await axios.get(`/api/agent/events/${event.id}/ratings`);
    setRatings(r.data);
    setLoading(false);
  };

  useEffect(() => {
    // Auto-rate on open if not already rated
    axios.post(`/api/agent/events/${event.id}/auto-rate`)
      .then(() => { setAutoRated(true); load(); })
      .catch(() => load());
  }, [event.id]);

  const setScore = (idx, score) => {
    setRatings(r => r.map((x, i) => i === idx ? {...x, _pendingScore: score} : x));
  };

  const saveScore = async (r, idx) => {
    const score = r._pendingScore ?? Math.round((r.punctuality + r.presentation + r.skill) / 3);
    if (!r.feedback_id) return;
    setSaving(s => ({...s, [idx]: true}));
    try {
      await axios.patch(`/api/agent/feedback/${r.feedback_id}`, { score, notes: r.notes || '' });
      // Update local state — no reload needed, avoids race condition
      setRatings(prev => prev.map((x, i) =>
        i === idx ? {...x, _pendingScore: undefined, punctuality: score, presentation: score, skill: score} : x
      ));
      setSaving(s => ({...s, [idx]: 'saved'}));
      setTimeout(() => setSaving(s => ({...s, [idx]: false})), 2000);
    } catch {
      setSaving(s => ({...s, [idx]: false}));
    }
  };

  const openAdjust = (idx, r) => {
    // Session M11: settlement locks pay. The button is replaced by a lock chip
    // when paid=1, so this is belt-and-braces — but the server 409s either way.
    if (Number(r.paid) === 1) return;
    setAdjEditing(idx);
    setAdjDraft({ amount: (r.adjustment != null && Number(r.adjustment) !== 0) ? String(r.adjustment) : '', note: r.adjustment_note || '' });
  };

  const saveAdjustment = async (r, idx) => {
    setAdjSaving(true);
    const amount = (adjDraft.amount === '' || adjDraft.amount === '-' || isNaN(Number(adjDraft.amount))) ? 0 : Number(adjDraft.amount);
    try {
      await axios.patch(`/api/agent/applications/${r.application_id}/adjustment`, { adjustment: amount, adjustment_note: adjDraft.note || '' });
      setRatings(prev => prev.map((x, i) => i === idx ? { ...x, adjustment: amount, adjustment_note: (adjDraft.note || '').trim() || null } : x));
      setAdjEditing(null);
      // Step 3.5 Pass B: visible confirmation — especially when clearing to 0,
      // where the adjustment line disappears and nothing else changes on screen.
      setAdjSavedIdx(idx);
      setTimeout(() => setAdjSavedIdx(s => (s === idx ? null : s)), 2000);
    } catch (e) {
      alert(e.response?.data?.error || 'Could not save the adjustment.');
    }
    setAdjSaving(false);
  };

  const visibleAfterLabel = (r) => {
    if (!r.rating_visible_after) return null;
    const va = new Date(r.rating_visible_after);
    const now = new Date();
    if (va <= now) return <span style={{fontSize:10,color:'var(--green)',fontWeight:600}}>✓ Visible to staff</span>;
    const hrs = Math.ceil((va - now) / (1000 * 60 * 60));
    return <span style={{fontSize:10,color:'var(--amber)',fontWeight:600}}>Visible in ~{hrs}h</span>;
  };

  const StarEditor = ({ current, pending, onChange, locked }) => {
    const display = pending ?? current ?? 5;
    return (
      <div style={{display:'flex',gap:4,alignItems:'center'}}>
        {[1,2,3,4,5].map(i => (
          <span
            key={i}
            onClick={() => !locked && onChange(i)}
            style={{fontSize:22,cursor:locked?'default':'pointer',color:i<=display?'var(--amber)':'var(--border)',transition:'color 0.1s',lineHeight:1,opacity:locked?0.65:1}}
            title={locked?'Rating window closed':''}
          >★</span>
        ))}
        {locked && <span style={{fontSize:11,color:'var(--text-muted)',marginLeft:6}}>Window closed</span>}
      </div>
    );
  };

  const fmtMoney = n => { const v = Number(n); return Number.isInteger(v) ? String(v) : v.toFixed(2); };
  const fmtHours = n => { const v = Number(n); return Number.isInteger(v) ? String(v) : String(Math.round(v * 100) / 100); };
  // Session M11: settled-on date. Same shape as fmtDate in the payroll view.
  // Display-only in the browser, so locale data is present — this is not the
  // bank-file path, where dates are built explicitly (Session U).
  const adjDateFmt = d => { if (!d) return ''; const dt = new Date(d); return isNaN(dt.getTime()) ? String(d) : dt.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' }); };
  const LBL = { fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 2 };
  const VAL = { fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' };
  // BOSSE Step 4 fix: group totals must apply the no-show rule too (row display
  // already did; the sums didn't — caught in the 12 July payment test).
  const toPayOf = r => r.attendance === 'no_show' ? 0 : (r.earned != null ? Number(r.earned) + Number(r.adjustment || 0) : 0);

  // Payroll step 2: group confirmed staff by role, preserving the original index for rating saves
  const grouped = (() => {
    const groups = {};
    ratings.forEach((r, idx) => {
      const key = r.role || 'Unassigned';
      (groups[key] = groups[key] || []).push({ r, idx });
    });
    return Object.keys(groups).sort().map(role => ({ role, items: groups[role] }));
  })();
  const eventTotal = ratings.reduce((s, r) => s + toPayOf(r), 0);
  const eventTotalPartial = ratings.some(r => r.earned == null);

  return (
    <div onClick={e=>{if(e.target===e.currentTarget)onClose();}} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:100,padding:'20px',backdropFilter:'blur(2px)'}}>
      <div style={{background:'var(--bg-card)',borderRadius:'var(--radius-lg)',padding:'28px 32px',width:'100%',maxWidth:640,maxHeight:'88vh',overflowY:'auto',boxShadow:'0 8px 40px rgba(0,0,0,0.25)'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:6}}>
          <div>
            <div style={{fontWeight:700,fontSize:20,color:'var(--text-primary)',letterSpacing:'-0.3px'}}>{event.title}</div>
            <div style={{fontSize:13,color:'var(--text-secondary)',marginTop:2}}>{formatDate(event.date)} · {event.time_start}–{event.time_end}</div>
          </div>
          <button style={{background:'none',border:'none',cursor:'pointer',color:'var(--text-secondary)',padding:4,fontSize:20}} onClick={onClose}>✕</button>
        </div>

        <div style={{padding:'10px 14px',background:'rgba(217,119,6,0.06)',border:'1px solid rgba(217,119,6,0.2)',borderRadius:8,marginBottom:20,fontSize:12,color:'var(--amber)',lineHeight:1.6}}>
          <strong>Rating window:</strong> All staff automatically receive 5 stars. Ratings become visible to staff 48 hours after the event ends. Adjust any ratings below before then.
        </div>

        {loading ? <div className="loading">Loading staff...</div> : ratings.length === 0 ? (
          <div className="empty-state">No confirmed staff found for this event.</div>
        ) : (
          <>
          {grouped.map(group => {
            const roleTotal = group.items.reduce((s, {r}) => s + toPayOf(r), 0);
            const roleMissing = group.items.some(({r}) => r.earned == null);
            return (
            <div key={group.role} style={{marginBottom:4}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'baseline',padding:'16px 0 8px',borderBottom:'2px solid var(--border)'}}>
                <span style={{fontWeight:800,fontSize:12,letterSpacing:'0.5px',textTransform:'uppercase',color:'var(--text-secondary)'}}>{group.role}</span>
                <span style={{fontSize:12,color:'var(--text-muted)'}}>{group.items.length} staff · <span style={{color:'var(--green)',fontWeight:700}}>{roleMissing ? '—' : `R${fmtMoney(roleTotal)}`}</span></span>
              </div>
              {group.items.map(({ r, idx }) => {
                const currentScore = r.feedback_id ? Math.round((r.punctuality + r.presentation + r.skill) / 3) : 5;
                const pending = r._pendingScore;
                const isDirty = pending !== undefined && pending !== currentScore;
                return (
                  <div key={r.user_id} style={{padding:'14px 0',borderBottom:'1px solid var(--border)'}}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:8}}>
                      <div>
                        <div style={{fontWeight:700,fontSize:14,color:'var(--text-primary)'}}>{r.name}</div>
                        <div style={{fontSize:12,color:'var(--text-secondary)',marginTop:1}}>
                          {r.shifts_completed > 0 ? `${Number(r.reliability_score).toFixed(1)} avg · ${r.shifts_completed} shift${r.shifts_completed===1?'':'s'}` : 'New staff'}
                        </div>
                        <div style={{marginTop:4}}>{visibleAfterLabel(r)}</div>
                      </div>
                      {/* Step 3.5 Pass B: the button used to unmount the instant a save
                          succeeded (isDirty flips false), so "✓ Saved" never showed —
                          it just vanished. Keep it mounted while saving[idx] is set. */}
                      {(isDirty || saving[idx]) && !(r.rating_visible_after && new Date(r.rating_visible_after) <= new Date()) && (
                        <button
                          onClick={() => saveScore(r, idx)}
                          disabled={!!saving[idx]}
                          style={{padding:'6px 16px',borderRadius:6,fontSize:12,fontWeight:700,cursor:'pointer',border:'none',
                            background: saving[idx]==='saved' ? 'rgba(22,163,74,0.12)' : 'var(--accent)',
                            color: saving[idx]==='saved' ? '#16a34a' : '#fff'}}
                        >
                          {saving[idx] === 'saved' ? '✓ Saved' : saving[idx] ? 'Saving...' : 'Save'}
                        </button>
                      )}
                    </div>

                    {/* Payroll step 2/3 + BOSSE Step 4: per-staff cost breakdown, attendance-aware */}
                    {(() => {
                      const adj = Number(r.adjustment || 0);
                      const isNoShow = r.attendance === 'no_show';
                      // Session M11: paid=1 means the money has gone. Pay is frozen
                      // from here — gating `editing` closes the editor as well as
                      // hiding the button, so there is no route to the Save call.
                      const isSettled = Number(r.paid) === 1;
                      const isLateUnreviewed = r.attendance === 'late' && adj === 0; // badge clears once any non-zero adjustment is saved
                      const toPay = isNoShow ? 0 : (r.earned != null ? Number(r.earned) + adj : null);
                      const editing = adjEditing === idx && !isSettled;
                      return (
                        <div style={{margin:'2px 0 12px'}}>
                          <div style={{display:'flex',gap:24,alignItems:'center',flexWrap:'wrap'}}>
                            <div>
                              <div style={LBL}>Rate</div>
                              <div style={VAL}>{r.rate_display || '—'}</div>
                            </div>
                            <div>
                              <div style={LBL}>{(r.pay_unit==='daily'||r.pay_unit==='per_event') ? 'Freq' : 'Hours'}</div>
                              <div style={VAL}>{r.pay_unit==='daily' ? '1 day' : r.pay_unit==='per_event' ? 'Flat (event)' : (r.hours!=null ? `${fmtHours(r.hours)}h` : '—')}</div>
                            </div>
                            <div>
                              <div style={LBL}>Earned</div>
                              <div style={{...VAL,...(isNoShow?{textDecoration:'line-through',color:'var(--text-muted)'}:{})}}>{r.earned!=null ? `R${fmtMoney(r.earned)}` : '—'}</div>
                            </div>
                            <div>
                              <div style={LBL}>To Pay</div>
                              {isNoShow
                                ? <div style={{...VAL,color:'#dc2626',fontWeight:700}}>No show — R0</div>
                                : <div style={{...VAL,color:'var(--green)',fontWeight:700}}>{toPay!=null ? `R${fmtMoney(toPay)}` : '—'}</div>}
                            </div>
                            {isLateUnreviewed && (
                              <span style={{padding:'4px 10px',borderRadius:6,background:'rgba(202,138,4,0.12)',border:'1px solid rgba(202,138,4,0.45)',color:'#ca8a04',fontSize:11,fontWeight:700}}>
                                ⏰ Late — review pay{r.attendance_note ? ` · ${r.attendance_note}` : ''}
                              </span>
                            )}
                            {!editing && !isNoShow && (
                              <div style={{marginLeft:'auto',display:'flex',alignItems:'center',gap:8}}>
                                {adjSavedIdx === idx && !isSettled && (
                                  <span style={{fontSize:11,fontWeight:700,color:'#16a34a'}}>✓ Saved</span>
                                )}
                                {/* Session M11: mirrors the roster's SETTLED chip — same
                                    lock, same wording, the other half of the same rule. */}
                                {isSettled ? (
                                  <span title="Settled — pay is locked. Open the event roster and un-settle this person to correct it."
                                    style={{display:'inline-flex',alignItems:'center',padding:'5px 12px',borderRadius:6,fontSize:11,fontWeight:800,letterSpacing:'0.2px',
                                      background:'var(--bg-card)',border:'1px solid var(--border)',color:'var(--text-secondary)',whiteSpace:'nowrap'}}>
                                    {'\u{1F512}'} SETTLED — pay locked
                                  </span>
                                ) : (
                                  <button onClick={()=>openAdjust(idx,r)} style={{padding:'5px 12px',borderRadius:6,border:'1px solid var(--border)',background:'var(--bg-surface)',color:'var(--text-secondary)',fontSize:11,fontWeight:600,cursor:'pointer'}}>
                                    {adj!==0 ? 'Edit adjustment' : 'Adjust'}
                                  </button>
                                )}
                              </div>
                            )}
                          </div>

                          {isNoShow && r.attendance_note && (
                            <div style={{fontSize:11,marginTop:6,color:'#dc2626'}}>No-show note: {r.attendance_note}</div>
                          )}

                          {/* Session M11: a lock needs a visible escape hatch or it
                              becomes a support message. Names the date paid and where
                              to go, without putting an un-settle button on this panel. */}
                          {isSettled && (
                            <div style={{fontSize:11,marginTop:6,color:'var(--text-muted)'}}>
                              Paid{r.paid_at ? ` on ${adjDateFmt(r.paid_at)}` : ''} — un-settle this person on the event roster to change their pay.
                            </div>
                          )}

                          {!editing && adj!==0 && !isNoShow && (
                            <div style={{fontSize:11,marginTop:6,color:adj<0?'var(--danger)':'var(--green)'}}>
                              Adjustment {adj<0?'−':'+'}R{fmtMoney(Math.abs(adj))}{r.adjustment_note ? ` · ${r.adjustment_note}` : ''}
                            </div>
                          )}

                          {editing && !isNoShow && (
                            <div style={{marginTop:8,padding:'10px 12px',background:'var(--bg-surface)',border:'1px solid var(--border)',borderRadius:8}}>
                              <div style={{display:'flex',gap:10,alignItems:'flex-end',flexWrap:'wrap'}}>
                                <div>
                                  <div style={LBL}>Adjustment (R)</div>
                                  <input type="number" step="0.01" inputMode="decimal" placeholder="e.g. -50" value={adjDraft.amount}
                                    onChange={e=>setAdjDraft(d=>({...d,amount:e.target.value}))}
                                    className="form-input" style={{width:110,fontSize:12,padding:'6px 8px'}} />
                                </div>
                                <div style={{flex:1,minWidth:150}}>
                                  <div style={LBL}>Reason (optional)</div>
                                  <input type="text" placeholder="e.g. late 30 min" value={adjDraft.note}
                                    onChange={e=>setAdjDraft(d=>({...d,note:e.target.value}))}
                                    className="form-input" style={{width:'100%',fontSize:12,padding:'6px 8px'}} />
                                </div>
                              </div>
                              <div style={{fontSize:10,color:'var(--text-muted)',marginTop:6}}>Negative docks pay, positive is a bonus. Leave blank or 0 to clear.</div>
                              <div style={{display:'flex',gap:8,marginTop:8}}>
                                <button onClick={()=>saveAdjustment(r,idx)} disabled={adjSaving} style={{padding:'6px 16px',borderRadius:6,background:'var(--accent)',color:'#fff',border:'none',fontSize:12,fontWeight:700,cursor:'pointer'}}>{adjSaving?'Saving...':'Save'}</button>
                                <button onClick={()=>setAdjEditing(null)} style={{padding:'6px 14px',borderRadius:6,background:'var(--bg-card)',color:'var(--text-secondary)',border:'1px solid var(--border)',fontSize:12,fontWeight:600,cursor:'pointer'}}>Cancel</button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    <StarEditor
                      current={currentScore}
                      pending={r._pendingScore}
                      onChange={score => setScore(idx, score)}
                      locked={!!(r.rating_visible_after && new Date(r.rating_visible_after) <= new Date())}
                    />
                    {r.auto_rated === 1 && !isDirty && (
                      <div style={{fontSize:10,color:'var(--text-muted)',marginTop:4}}>Auto-rated 5 stars — tap to adjust</div>
                    )}
                  </div>
                );
              })}
            </div>
            );
          })}
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'18px 0 2px'}}>
            <span style={{fontWeight:800,fontSize:14,color:'var(--text-primary)'}}>Total to pay</span>
            <span style={{fontSize:16,fontWeight:800,color:'var(--green)'}}>R{fmtMoney(eventTotal)}{eventTotalPartial ? '+' : ''}</span>
          </div>
          {eventTotalPartial && <div style={{fontSize:10,color:'var(--text-muted)',textAlign:'right'}}>Some shifts are missing a rate or times — total is partial</div>}
          </>
        )}
      </div>
    </div>
  );
}

// ── Events List ───────────────────────────────────────────────────────────────
// ── BOSSE Step 3: attendance roster row (desktop + iPad — big touch targets) ──
// Manager mark is the authoritative record for pay (Step 4); self-check-in is
// shown as an indicator only. Tapping the active state clears it (mis-tap fix).
// Step 3.5 Pass A: plain text labels (no icons) — compact rows for venue use.
const ATT_STYLES = {
  arrived: { label: 'Arrived', color: '#16a34a', bg: 'rgba(22,163,74,0.12)', border: 'rgba(22,163,74,0.45)' },
  late:    { label: 'Late',    color: '#ca8a04', bg: 'rgba(202,138,4,0.12)',  border: 'rgba(202,138,4,0.45)' },
  no_show: { label: 'No-show', color: '#dc2626', bg: 'rgba(220,38,38,0.12)',  border: 'rgba(220,38,38,0.45)' },
};

function AttendanceRow({ row, canMark, onChange, onNoteFocus, onNoteBlur, onUnsettle }) {
  const [busy, setBusy] = useState(false);
  const [noteDraft, setNoteDraft] = useState(row.attendance_note || '');
  const [noteSaving, setNoteSaving] = useState(false);
  const noteFocusedRef = useRef(false);

  // Step 3.5 Pass A: if another manager's note lands via auto-refresh and this
  // manager ISN'T typing in the field, follow the remote value. While focused,
  // the parent skips this row entirely on merge, so a half-typed note survives.
  useEffect(() => {
    if (!noteFocusedRef.current) setNoteDraft(row.attendance_note || '');
  }, [row.attendance_note]);

  // Session R: lock on money, not on clock. The roster stays open after the
  // event ends; a row freezes only once that shift is settled in Payroll (the
  // server 409s on the same condition). Un-settling re-opens it.
  const locked = row.paid === 1 || row.paid === true;
  const editable = canMark && !locked;

  const mark = async (value) => {
    if (!editable || busy) return;
    const next = row.attendance === value ? null : value; // tap active again = clear
    const note = next === 'no_show' ? (row.attendance_note || 'Did not show up')
               : next === null ? null : (row.attendance_note || null);
    setBusy(true);
    try {
      const r = await axios.patch(`/api/agent/applications/${row.application_id}/attendance`, { attendance: next, attendance_note: note });
      onChange(row.application_id, { attendance: r.data.attendance, attendance_note: r.data.attendance_note });
      setNoteDraft(r.data.attendance_note || '');
    } catch (e) {
      alert(e.response?.data?.error || 'Could not update attendance');
    }
    setBusy(false);
  };

  const saveNote = async () => {
    setNoteSaving(true);
    try {
      const r = await axios.patch(`/api/agent/applications/${row.application_id}/attendance`, { attendance: row.attendance, attendance_note: noteDraft.trim() || null });
      onChange(row.application_id, { attendance_note: r.data.attendance_note });
      // 17 Jul: confirm the save visibly — same ✓ Saved flash as adjustments
      setNoteSaving('saved');
      setTimeout(() => setNoteSaving(false), 2000);
    } catch (e) {
      alert(e.response?.data?.error || 'Could not save note');
      setNoteSaving(false);
    }
  };

  const showNote = row.attendance === 'late' || row.attendance === 'no_show';
  // Step 3.5 Pass A: one dense row — buttons on the same line as the name
  // (right-aligned), tap targets kept ≥42px, note field still expands below.
  return (
    <div style={{padding:'6px 10px',background:'var(--bg-surface)',border:'1px solid var(--border)',borderRadius:8,marginBottom:6}}>
      <div style={{display:'flex',alignItems:'center',gap:10}}>
        <div style={{width:32,height:32,borderRadius:'50%',background:'var(--accent)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:13,fontWeight:700,color:'#fff',overflow:'hidden',flexShrink:0}}>
          {row.picture ? <img src={thumbUrl(row.picture, 64)} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}}/> : (row.name||'S').charAt(0).toUpperCase()}
        </div>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontWeight:600,fontSize:13.5,color:'var(--text-primary)',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{row.name}{row.tier && <> <TierBadge tier={row.tier}/></>}</div>
          <div style={{fontSize:11,color:'var(--text-secondary)',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
            {row.role}
            {row.checked_in ? <span style={{color:'#16a34a'}}>{' \u00B7 self \u2713'}{row.checked_in_at ? ` ${new Date(row.checked_in_at).toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit'})}` : ''}</span> : ''}
            {row.phone && <>{' \u00B7 '}<a href={`tel:${row.phone}`} style={{color:'var(--accent)',fontWeight:600,textDecoration:'none'}}>Call</a></>}
          </div>
        </div>
        <div style={{display:'flex',gap:6,flexShrink:0,alignItems:'center'}}>
          {/* Session R: a settled shift shows its mark, frozen — the buttons are
              replaced by the lock chip plus a deliberate Un-settle. */}
          {locked ? (
            <>
              <span title="Settled in Payroll — attendance is locked. Un-settle to correct it."
                style={{display:'inline-flex',alignItems:'center',minHeight:42,padding:'0 12px',borderRadius:8,fontSize:12,fontWeight:800,letterSpacing:'0.2px',
                  background:'var(--bg-card)',border:'1.5px solid var(--border)',color:'var(--text-secondary)',whiteSpace:'nowrap'}}>
                {'\u{1F512}'} SETTLED{row.attendance ? ` \u00B7 ${(ATT_STYLES[row.attendance]||{}).label || row.attendance}` : ' \u00B7 Unmarked'}
              </span>
              {onUnsettle && (
                <button onClick={()=>onUnsettle(row)}
                  style={{minHeight:42,padding:'0 12px',borderRadius:8,fontSize:12,fontWeight:700,cursor:'pointer',fontFamily:'var(--font)',
                    background:'transparent',border:'1.5px solid var(--border)',color:'var(--text-secondary)',whiteSpace:'nowrap'}}>
                  Un-settle
                </button>
              )}
            </>
          ) : ['arrived','late','no_show'].map(v => {
            const s = ATT_STYLES[v];
            const active = row.attendance === v;
            return (
              <button key={v} onClick={()=>mark(v)} disabled={!editable||busy}
                style={{minHeight:42,padding:'0 12px',borderRadius:8,fontSize:13,fontWeight:700,cursor:editable?'pointer':'not-allowed',fontFamily:'var(--font)',
                  background: active ? s.bg : 'transparent',
                  border: `1.5px solid ${active ? s.border : 'var(--border)'}`,
                  color: active ? s.color : 'var(--text-secondary)',
                  opacity: editable ? 1 : 0.5}}>
                {s.label}
              </button>
            );
          })}
        </div>
      </div>
      {showNote && !locked && (
        <div style={{display:'flex',gap:8,marginTop:8}}>
          <input value={noteDraft} onChange={e=>setNoteDraft(e.target.value)}
            onFocus={()=>{noteFocusedRef.current=true; onNoteFocus && onNoteFocus(row.application_id);}}
            onBlur={()=>{noteFocusedRef.current=false; onNoteBlur && onNoteBlur(row.application_id);}}
            placeholder={row.attendance==='late'?'Note (optional) — e.g. "50 min late"':'Note'}
            style={{flex:1,padding:'9px 12px',borderRadius:8,border:'1px solid var(--border)',background:'var(--bg-card)',color:'var(--text-primary)',fontSize:13,fontFamily:'var(--font)'}}/>
          <button onClick={saveNote} disabled={!!noteSaving||!editable}
            style={{padding:'0 16px',borderRadius:8,border:'none',
              background: noteSaving==='saved' ? 'rgba(22,163,74,0.12)' : 'var(--accent)',
              color: noteSaving==='saved' ? '#16a34a' : '#fff',
              fontSize:13,fontWeight:700,cursor:'pointer',fontFamily:'var(--font)'}}>
            {noteSaving==='saved' ? '✓ Saved' : noteSaving ? 'Saving…' : 'Save note'}
          </button>
        </div>
      )}
      {/* Session R: a settled row keeps its note on the record, read-only. */}
      {showNote && locked && row.attendance_note && (
        <div style={{marginTop:8,fontSize:12,color:'var(--text-secondary)',fontStyle:'italic'}}>Note: {row.attendance_note}</div>
      )}
    </div>
  );
}

// ── Series card (Session M1) ──────────────────────────────────────────────────
// Groups the day-events of one multi-day series under a single expandable
// card. Collapsed: full-event overview — one compact staffing row per day plus
// a whole-series bar. Expanded: every day rendered as its normal full event
// card, so Edit / Fast-Assign / Roster / per-day publish all work exactly as
// on a standalone event — a day IS an event. Defined at module level so the
// open/closed state survives the 20s background refresh of the parent list.
function SeriesGroupCard({ days, renderDay, onPublishSeries, seriesBusy }) {
  const [open, setOpen] = useState(false);
  const first = days[0];
  const last = days[days.length-1];
  const pubCount = days.filter(d=>d.published).length;
  const allPublished = pubCount===days.length;
  const totFilled = days.reduce((a,d)=>a+(d.slots_filled||0),0);
  const totSlots  = days.reduce((a,d)=>a+(d.slots_total||0),0);
  const totStandby= days.reduce((a,d)=>a+(d.standby_count||0),0);
  const totPending= days.reduce((a,d)=>a+(d.pending_count||0),0);
  const endedDays = Math.max(0,(first.series_size||days.length)-days.length);
  const busy = seriesBusy===(first.parent_event_id||first.id);
  return (
    <div className="agent-section-card" style={{borderLeft:'3px solid var(--blue,#2563eb)'}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:10}}>
        <div style={{cursor:'pointer',flex:1}} onClick={()=>setOpen(v=>!v)}>
          <div style={{fontWeight:700,fontSize:16,color:'var(--text-primary)',display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
            {first.title}
            <span className="badge badge-series">MULTI-DAY</span>
            {pubCount===0 && <span style={{fontSize:12,color:'#ca8a04',fontWeight:600}}>(Draft)</span>}
            {pubCount>0 && !allPublished && <span style={{fontSize:12,color:'#ca8a04',fontWeight:600}}>({pubCount}/{days.length} published)</span>}
          </div>
          <div style={{fontSize:13,color:'var(--text-secondary)',marginTop:2}}>
            {formatDate(first.date)} – {formatDate(last.date)} · {days.length} day{days.length>1?'s':''} upcoming{endedDays>0?` (${endedDays} ended — see Past Events)`:''} · {[first.location, first.street_address].filter(Boolean).join(', ')}
          </div>
        </div>
        <div style={{display:'flex',gap:8,marginLeft:12,alignItems:'center'}}>
          <button className="btn btn-secondary btn-sm" disabled={busy}
            onClick={()=>onPublishSeries(first, !allPublished, days.length)}>
            {busy ? 'Working…' : allPublished ? 'Unpublish all days' : 'Publish all days'}
          </button>
          <button onClick={()=>setOpen(v=>!v)} style={{background:'none',border:'none',cursor:'pointer',color:'var(--text-secondary)',fontSize:11,padding:4}}>
            <span style={{display:'inline-block',transition:'transform 0.2s',transform:open?'rotate(180deg)':'rotate(0deg)'}}>▼</span>
          </button>
        </div>
      </div>

      {/* Per-day staffing overview (collapsed view of the whole event) */}
      <div style={{background:'var(--bg-surface)',border:'1px solid var(--border)',borderRadius:8,padding:12,marginBottom:12}}>
        {days.map(d=>{
          const pct = fillPercent(d.slots_filled, d.slots_total);
          return (
            <div key={d.id} style={{marginBottom:8}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:2}}>
                <div style={{fontSize:12,color:'var(--text-primary)'}}>
                  <strong>Day {d.day_number}</strong>
                  <span style={{color:'var(--text-secondary)',marginLeft:8}}>{formatDate(d.date)} · {d.time_start}–{d.time_end}</span>
                  {!d.published && <span style={{fontSize:10,color:'#ca8a04',fontWeight:700,marginLeft:8}}>DRAFT</span>}
                </div>
                <span style={{fontSize:11,color:'var(--text-secondary)'}}>{d.slots_filled||0}/{d.slots_total||0}</span>
              </div>
              <div style={{height:5,background:'var(--bg-elevated)',borderRadius:3,overflow:'hidden'}}>
                <div style={{height:'100%',width:`${pct}%`,background:fillColor(d.slots_filled, d.slots_total),borderRadius:3,transition:'width 0.4s'}}/>
              </div>
            </div>
          );
        })}
      </div>
      <StaffingBar filled={totFilled} total={totSlots} standby={totStandby} pending={totPending}/>

      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginTop:12,paddingTop:10,borderTop:'1px solid var(--border)'}}>
        <button onClick={()=>setOpen(v=>!v)} style={{background:'none',border:'none',cursor:'pointer',color:'var(--accent)',fontSize:13,fontWeight:700,padding:0,fontFamily:'var(--font)'}}>
          {open ? '▲ Collapse days' : `▼ Manage the ${days.length} day${days.length>1?'s':''} individually`}
        </button>
        <span style={{fontSize:11,color:'var(--text-muted)'}}>Each day has its own roster, check-in and payroll line</span>
      </div>

      {open && (
        <div style={{display:'grid',gap:12,marginTop:12}}>
          {days.map(d=>renderDay(d))}
        </div>
      )}
    </div>
  );
}

function EventsList({ setActive }) {
  const [events,setEvents]=useState([]);
  const [loading,setLoading]=useState(true);
  const [editEvent,setEditEvent]=useState(null);
  const [fastEvent,setFastEvent]=useState(null);
  const [showPast,setShowPast]=useState(false);
  const [pastSearch,setPastSearch]=useState('');
  const [ratingEvent,setRatingEvent]=useState(null);
  const [deleteConfirm,setDeleteConfirm]=useState(null);
  const [deleting,setDeleting]=useState(false);
  // Session AK: the event being previewed exactly as staff see it, or null.
  const [previewEvent,setPreviewEvent]=useState(null);
  const [checkinEvent,setCheckinEvent]=useState(null);
  const [checkins,setCheckins]=useState([]);
  const [checkinsLoading,setCheckinsLoading]=useState(false);
  const [checkinsRefreshing,setCheckinsRefreshing]=useState(false); // Step 3.5 Pass A
  const [exportingId,setExportingId]=useState(null); // BOSSE Step 2
  const [refreshing,setRefreshing]=useState(false); // My Events auto-refresh (16 Jul)
  const pastRef = useRef(null);

  // BOSSE Step 2: authenticated xlsx download — axios carries the JWT header,
  // then we hand the blob to the browser (a plain link/window.open would send no token).
  const exportStaffInfo = async (ev) => {
    setExportingId(ev.id);
    try {
      const r = await axios.get(`/api/agent/events/${ev.id}/staff-export`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = (`Staff - ${ev.title} - ${ev.date}`.replace(/[^a-zA-Z0-9 \-_.]/g, '').trim() || 'staff-export') + '.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      alert('Could not export staff info. Make sure at least one applicant is accepted.');
    }
    setExportingId(null);
  };

  // Session P: past-event shortcut into the same bank export the Payroll page
  // uses — one implementation, two entry points.
  const [paymentExportingId, setPaymentExportingId] = useState(null);
  const exportPayments = async (ev) => {
    setPaymentExportingId(ev.id);
    try {
      const r = await axios.get(`/api/agent/events/${ev.id}/payment-export?format=csv`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = (`Payments - ${ev.title}${(ev.series_size||1)>1?` - Day ${ev.day_number}`:''} - ${ev.date}`.replace(/[^a-zA-Z0-9 \-_.]/g, '').trim() || 'payment-export') + '.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      let msg = 'Could not generate the payment export.';
      try { msg = JSON.parse(await err.response.data.text()).error || msg; } catch {}
      alert(msg);
    }
    setPaymentExportingId(null);
  };

  const scrollToPast = () => {
    setShowPast(true);
    setTimeout(() => pastRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  const load=()=>axios.get('/api/events').then(r=>setEvents(r.data));
  useEffect(()=>{ load().finally(()=>setLoading(false)); },[]);

  // My Events auto-refresh (16 Jul): the roster panel already polls itself, but
  // the cards BEHIND it (self-check-in tallies, staffing bars, application
  // counts) only updated on page reload. Refresh the list every 20s — silently
  // (no loading flash), skipping while any modal/panel is open so a background
  // reorder never yanks state out from under an edit, delete-confirm, roster,
  // rating or fast-assign in progress (their close handlers already re-load).
  useEffect(()=>{
    const iv=setInterval(()=>{
      if(document.hidden) return; // tab in background — don't poll for nobody
      if(editEvent||fastEvent||deleteConfirm||checkinEvent||ratingEvent) return;
      load();
    },20000);
    return ()=>clearInterval(iv);
  },[editEvent,fastEvent,deleteConfirm,checkinEvent,ratingEvent]);

  const manualLoad=async()=>{
    if(refreshing) return;
    setRefreshing(true);
    await load();
    setRefreshing(false);
  };

  const now = new Date();
  const eventEnd = e => {
    try {
      const start = e.time_start || '00:00';
      const end = e.time_end || '23:59';
      const d = new Date(`${e.date}T${end}`);
      const [sh, sm] = start.split(':').map(Number);
      const [eh, em] = end.split(':').map(Number);
      if ((eh * 60 + em) < (sh * 60 + sm)) d.setDate(d.getDate() + 1); // overnight — ends next day
      return d;
    } catch { return new Date(e.date); }
  };
  const upcomingEvents = events
    .filter(e=>{ try{ return eventEnd(e)>=now; }catch{return true;} })
    .sort((a,b)=>new Date(`${a.date}T${a.time_start||'00:00'}`)-new Date(`${b.date}T${b.time_start||'00:00'}`));
  const pastEvents = events
    .filter(e=>{ try{ return eventEnd(e)<now; }catch{return false;} })
    .sort((a,b)=>eventEnd(b)-eventEnd(a)); // newest past first

  // Session R: the roster modal is the SAME modal for live and past events —
  // this only changes the wording and which nudges show.
  const rosterEnded = checkinEvent ? eventEnd(checkinEvent) < new Date() : false;
  // Confirmed staff on an ended event whose attendance was never marked. They
  // are paid IN FULL (deliberate fail-open — better to pay someone who was
  // there than stiff them because nobody ticked the box), so this is a nudge
  // to reconcile before the bank export, never a block. The count is the true
  // unmarked total; it only RENDERS while at least one of them is still
  // unsettled (unmarked_open_count), so a fully settled event stops nagging
  // about a gap that can no longer be closed.
  const unmarkedCount = e => Math.max(0, (e.slots_filled||0) - (e.attendance_marked_count||0));

  const togglePublish=async(e)=>{
    // Session C: unpublish needs a guard — one accidental click hid an event
    // from staff. Publish stays one-click.
    if (e.published && !window.confirm('Unpublish this event? Staff who have applied will no longer see it, and no new applications can come in until it is published again.')) return;
    await axios.patch(`/api/events/${e.id}/publish`,{published:!e.published});
    setEvents(ev=>ev.map(x=>x.id===e.id?{...x,published:x.published?0:1}:x));
  };

  // Session M1: whole-series publish/unpublish — one action across every day.
  // Unpublish keeps the Session C guard; publish stays one-click, matching the
  // per-day toggle.
  const [seriesBusy,setSeriesBusy]=useState(null);
  const publishSeries=async(anchor,publish,dayCount)=>{
    if (!publish && !window.confirm(`Unpublish all ${dayCount} days of this event? Staff who have applied will no longer see any of them, and no new applications can come in until they are published again.`)) return;
    setSeriesBusy(anchor.parent_event_id||anchor.id);
    try {
      await axios.patch(`/api/events/${anchor.id}/publish-series`,{published:publish});
    } catch(err) {
      alert(err.response?.data?.error||'Could not update the series. Please try again.');
    }
    setSeriesBusy(null);
    await load();
  };

  // Session M1: group upcoming day-events into series (series_size is computed
  // server-side over the FULL series). Groups keep the position of their first
  // upcoming day in the chronological order; ended days live in Past Events as
  // normal per-day cards — a day IS an event, so its payroll/rating flows are
  // untouched.
  const groupedUpcoming=(()=>{
    const out=[]; const byKey=new Map();
    for (const e of upcomingEvents) {
      if ((e.series_size||1)>1) {
        const k=e.parent_event_id||e.id;
        if (!byKey.has(k)) { const g={type:'series',key:k,days:[]}; byKey.set(k,g); out.push(g); }
        byKey.get(k).days.push(e);
      } else out.push({type:'single',e});
    }
    return out;
  })();

  const handleEdit=async(data)=>{
    await axios.patch(`/api/events/${editEvent.id}`,data);
    return editEvent.id;
  };

  const handleEditDone=()=>{
    setEditEvent(null); load();
  };

  const handleDelete=async()=>{
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      await axios.delete(`/api/events/${deleteConfirm.id}`);
    } catch (e) {
      // Session B: the server now refuses to delete ended events with confirmed
      // staff (payroll/rating history) — show its message instead of freezing.
      alert(e.response?.data?.error || 'Could not delete the event. Please try again.');
    }
    setDeleteConfirm(null);
    setDeleting(false);
    load();
  };

  const withinCheckinWindow = (e) => {
    const start = new Date(`${e.date}T${e.time_start||'00:00'}`);
    const hoursUntil = (start - new Date()) / (1000*60*60);
    // BOSSE Step 3: window now runs to the overnight-aware event END (was start+2h),
    // so the roster stays reachable for the whole shift.
    return hoursUntil <= 24 && new Date() <= eventEnd(e);
  };

  // Step 3.5 Pass A: Live Roster window — appears 1h before start, until the
  // overnight-aware event end (matches the server-side marking gate).
  const withinRosterWindow = (e) => {
    const start = new Date(`${e.date}T${e.time_start||'00:00'}`);
    const now = new Date();
    return now >= new Date(start.getTime() - 60*60*1000) && now <= eventEnd(e);
  };

  // Step 3.5 Pass A: multiple managers mark simultaneously from different gates —
  // poll the checkins endpoint every 20s while the roster is open and merge marks.
  // Clobber guard: a row whose note editor is focused by THIS manager is skipped
  // on merge, so a half-typed note is never wiped by someone else's update.
  const noteEditingRef = useRef(new Set());

  const fetchCheckins = async (eventId) => {
    try {
      const r = await axios.get(`/api/agent/events/${eventId}/checkins`);
      setCheckins(prev => r.data.map(f =>
        noteEditingRef.current.has(f.application_id)
          ? (prev.find(p => p.application_id === f.application_id) || f)
          : f
      ));
    } catch {}
  };

  useEffect(() => {
    if (!checkinEvent) return;
    const iv = setInterval(() => fetchCheckins(checkinEvent.id), 20000);
    return () => clearInterval(iv);
  }, [checkinEvent]);

  const manualRefresh = async () => {
    if (!checkinEvent || checkinsRefreshing) return;
    setCheckinsRefreshing(true);
    await fetchCheckins(checkinEvent.id);
    setCheckinsRefreshing(false);
  };

  const closeCheckins = () => {
    setCheckinEvent(null);
    setCheckins([]);
    noteEditingRef.current = new Set();
    load(); // Session R: marks made in the roster change the card's "not marked" count
  };

  // Session R: the escape hatch the settlement lock requires. Re-opens ONE
  // person's shift so their attendance can be corrected; it goes back onto the
  // outstanding list in Payroll and must be settled again.
  const unsettleOne = async (row) => {
    if (!window.confirm(
      `Un-settle ${row.name}'s shift?\n\n`
      + `This unlocks their attendance so you can correct it. The shift returns to Outstanding in Payroll and must be settled again once it is right.\n\n`
      + `Money already paid into their account is NOT reversed by this — that stays a bank matter.`
    )) return;
    try {
      await axios.patch('/api/agent/payroll/unsettle', { application_ids: [row.application_id] });
      setCheckins(cs => cs.map(c => c.application_id===row.application_id ? { ...c, paid: 0, paid_at: null } : c));
    } catch (e) {
      alert(e.response?.data?.error || 'Could not un-settle this shift.');
    }
  };

  const openCheckins = async (e) => {
    setCheckinEvent(e);
    setCheckinsLoading(true);
    noteEditingRef.current = new Set();
    try {
      const r = await axios.get(`/api/agent/events/${e.id}/checkins`);
      setCheckins(r.data);
    } catch { setCheckins([]); }
    setCheckinsLoading(false);
  };

  return (
    <>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div><div className="agent-page-title">My Events</div><div className="agent-page-sub">Click an event to fast-assign applicants. Use Edit to update details.</div></div>
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          {/* Manual refresh — same control as the roster header; auto-refresh runs every 20s */}
          <button title="Refresh events" onClick={manualLoad} disabled={refreshing}
            style={{background:'none',border:'none',cursor:'pointer',color:'var(--text-secondary)',padding:4,fontSize:18,lineHeight:1,fontFamily:'var(--font)',opacity:refreshing?0.4:1}}>
            {'\u21BB'}
          </button>
          <button className="btn btn-secondary btn-sm" onClick={scrollToPast}>📂 Past Events</button>
          <button className="btn btn-primary btn-sm" onClick={()=>setActive('post')}>+ Post Event</button>
        </div>
      </div>

      {/* Edit modal */}
      {editEvent&&(
        <div onClick={e=>{if(e.target===e.currentTarget)setEditEvent(null);}} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:100,padding:'20px',backdropFilter:'blur(2px)'}}>
          <div style={{background:'var(--bg-card)',borderRadius:'var(--radius-lg)',padding:'28px 32px',width:'100%',maxWidth:640,maxHeight:'90vh',overflowY:'auto',boxShadow:'0 8px 40px rgba(0,0,0,0.25)'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
              <div><div className="modal-title">Edit Event{(editEvent.series_size||1)>1?` — Day ${editEvent.day_number} of ${editEvent.series_size}`:''}</div><div className="modal-sub">Changes will be sent to all applicants automatically.</div></div>
              <button style={{background:'none',border:'none',cursor:'pointer',color:'var(--text-secondary)'}} onClick={()=>setEditEvent(null)}><Icon name="x" size={20}/></button>
            </div>
            <EventForm
              isEdit
              seriesInfo={(editEvent.series_size||1)>1?{day_number:editEvent.day_number,series_size:editEvent.series_size}:null}
              eventId={editEvent.id}
              existingBriefUrl={editEvent.brief_url||null}
              initial={{title:editEvent.title,date:editEvent.date,time_start:editEvent.time_start,time_end:editEvent.time_end,location:editEvent.location,street_address:editEvent.street_address||'',dress_code:editEvent.dress_code||'',description:editEvent.description||'',transport:editEvent.transport||''}}
              initialRoles={editEvent.roles?.map(r=>({role:r.role,spots:r.spots_available,pay_amount:r.pay_amount??'',pay_unit:r.pay_unit||'hourly',pay_rate:r.pay_rate||'',required_tiers:r.required_tiers?r.required_tiers.split(',').filter(Boolean):[]}))||[{role:'',spots:1,pay_amount:'',pay_unit:'hourly',pay_rate:'',required_tiers:[]}]}
              onSave={handleEdit}
              onDone={handleEditDone}
              onCancel={()=>setEditEvent(null)}
            />
          </div>
        </div>
      )}

      {fastEvent&&<FastAssignPanel event={fastEvent} onClose={()=>setFastEvent(null)} onUpdate={load}/>}
      {ratingEvent&&<PastEventRatingPanel event={ratingEvent} onClose={()=>setRatingEvent(null)}/>}

      {/* Check-in roster modal */}
      {checkinEvent&&(
        <div onClick={e=>{if(e.target===e.currentTarget)closeCheckins();}} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:100,padding:'20px',backdropFilter:'blur(2px)'}}>
          <div style={{background:'var(--bg-card)',borderRadius:'var(--radius-lg)',padding:'24px 28px',width:'100%',maxWidth:640,maxHeight:'85vh',overflowY:'auto',boxShadow:'0 8px 40px rgba(0,0,0,0.25)'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:16}}>
              <div>
                <div className="modal-title">{checkinEvent.title}</div>
                <div className="modal-sub">{formatDate(checkinEvent.date)} · {checkinEvent.time_start}–{checkinEvent.time_end} · Attendance roster{rosterEnded ? ' · event ended' : ''}</div>
              </div>
              <div style={{display:'flex',alignItems:'center',gap:4}}>
                {/* Step 3.5 Pass A: manual refresh (auto-refresh runs every 20s while open) */}
                <button title="Refresh roster" onClick={manualRefresh} disabled={checkinsRefreshing}
                  style={{background:'none',border:'none',cursor:'pointer',color:'var(--text-secondary)',padding:4,fontSize:18,lineHeight:1,fontFamily:'var(--font)',opacity:checkinsRefreshing?0.4:1}}>
                  {'\u21BB'}
                </button>
                <button style={{background:'none',border:'none',cursor:'pointer',color:'var(--text-secondary)',padding:4}} onClick={closeCheckins}><Icon name="x" size={20}/></button>
              </div>
            </div>

            {checkinsLoading ? <div className="loading">Loading roster...</div> : checkins.length===0 ? (
              <div className="empty-state">No confirmed staff for this event yet.</div>
            ) : (() => {
              // BOSSE Step 3: manager attendance roster. Marking opens 1h before start
              // (server-enforced too); self-check-in shows as a per-row indicator.
              const canMark = new Date() >= new Date(new Date(`${checkinEvent.date}T${checkinEvent.time_start||'00:00'}`).getTime() - 60*60*1000);
              const count = v => checkins.filter(c=>c.attendance===v).length;
              const unmarked = checkins.filter(c=>!c.attendance).length;
              const syncRow = (appId, fields) => setCheckins(cs => cs.map(c => c.application_id===appId ? { ...c, ...fields } : c));
              return (
                <>
                  {!canMark && (
                    <div style={{marginBottom:14,padding:'10px 14px',background:'rgba(202,138,4,0.08)',border:'1px solid rgba(202,138,4,0.3)',borderRadius:8,fontSize:13,color:'#ca8a04'}}>
                      Roster opens 1 hour before the shift starts — marking is disabled until then.
                    </div>
                  )}
                  {/* Session R: ended events stay editable — settlement is the only lock. */}
                  {rosterEnded && (
                    <div style={{marginBottom:14,padding:'10px 14px',background:'rgba(37,99,235,0.07)',border:'1px solid rgba(37,99,235,0.28)',borderRadius:8,fontSize:13,color:'#2563eb'}}>
                      This event has ended — attendance is still editable. A row locks only once that shift is settled in Payroll.
                    </div>
                  )}
                  {rosterEnded && unmarked > 0 && checkins.some(c=>!c.attendance&&!(c.paid===1||c.paid===true)) && (
                    <div style={{marginBottom:14,padding:'10px 14px',background:'rgba(202,138,4,0.08)',border:'1px solid rgba(202,138,4,0.3)',borderRadius:8,fontSize:13,color:'#ca8a04'}}>
                      ⚠ <strong>{unmarked} of {checkins.length} not marked.</strong> Unmarked staff are paid in full — reconcile before you export the bank file.
                    </div>
                  )}
                  {/* Summary bar */}
                  <div style={{display:'flex',gap:16,marginBottom:20,padding:'14px 16px',background:'var(--bg-surface)',border:'1px solid var(--border)',borderRadius:10}}>
                    <div style={{textAlign:'center',flex:1}}>
                      <div style={{fontSize:22,fontWeight:800,color:'#16a34a'}}>{count('arrived')}</div>
                      <div style={{fontSize:11,color:'var(--text-muted)',marginTop:2}}>Arrived</div>
                    </div>
                    <div style={{width:1,background:'var(--border)'}}/>
                    <div style={{textAlign:'center',flex:1}}>
                      <div style={{fontSize:22,fontWeight:800,color:count('late')>0?'#ca8a04':'var(--text-muted)'}}>{count('late')}</div>
                      <div style={{fontSize:11,color:'var(--text-muted)',marginTop:2}}>Late</div>
                    </div>
                    <div style={{width:1,background:'var(--border)'}}/>
                    <div style={{textAlign:'center',flex:1}}>
                      <div style={{fontSize:22,fontWeight:800,color:count('no_show')>0?'#dc2626':'var(--text-muted)'}}>{count('no_show')}</div>
                      <div style={{fontSize:11,color:'var(--text-muted)',marginTop:2}}>No-show</div>
                    </div>
                    <div style={{width:1,background:'var(--border)'}}/>
                    <div style={{textAlign:'center',flex:1}}>
                      <div style={{fontSize:22,fontWeight:800,color:'var(--text-primary)'}}>{unmarked}</div>
                      <div style={{fontSize:11,color:'var(--text-muted)',marginTop:2}}>Unmarked</div>
                    </div>
                  </div>
                  {checkins.map(c => (
                    <AttendanceRow key={c.application_id} row={c} canMark={canMark} onChange={syncRow}
                      onUnsettle={unsettleOne}
                      onNoteFocus={id=>noteEditingRef.current.add(id)}
                      onNoteBlur={id=>noteEditingRef.current.delete(id)}/>
                  ))}
                </>
              );
            })()}
          </div>
        </div>
      )}

      {loading?<div className="loading">Loading...</div>:(
        <div style={{display:'grid',gap:14}}>
          {upcomingEvents.length===0&&pastEvents.length===0&&<div className="empty-state">No events yet.</div>}
          {upcomingEvents.length===0&&pastEvents.length>0&&<div className="empty-state" style={{fontSize:13}}>No upcoming events. Check past events below.</div>}

          {(()=>{
          // Session M1: one card = one day. Rendered directly for standalone
          // events and inside the series card for multi-day events, so every
          // control (Edit / Delete / Fast-Assign / Roster / publish toggle)
          // behaves identically in both places.
          // Session AK: preview state. Declared here beside the card renderer
          // that uses it.
          const renderUpcomingCard = (e) => (
            <div key={e.id} className="agent-section-card">
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:12}}>
                <div style={{cursor:'pointer',flex:1}} onClick={()=>setFastEvent(e)}>
                  <div style={{fontWeight:700,fontSize:16,color:'var(--text-primary)'}}>
                    {e.title}
                    {(e.series_size||1)>1&&<span className="badge badge-series" style={{marginLeft:8}}>DAY {e.day_number}/{e.series_size}</span>}
                    {!e.published&&<span style={{fontSize:12,color:'#ca8a04',fontWeight:600,marginLeft:8}}>(Draft)</span>}
                  </div>
                  <div style={{fontSize:13,color:'var(--text-secondary)',marginTop:2}}>{formatDate(e.date)} · {e.time_start}–{e.time_end} · {[e.location, e.street_address].filter(Boolean).join(', ')}</div>
                  <div style={{fontSize:11,fontWeight:600,color:'var(--accent)',marginTop:6}}>Click to fast-assign applicants →</div>
                </div>
                <div style={{display:'flex',gap:8,marginLeft:12}}>
                  <button
                    className="btn btn-secondary btn-sm"
                    disabled={!(e.slots_filled>0)||exportingId===e.id}
                    title={e.slots_filled>0?'Download an xlsx of accepted staff for this event':'No accepted staff yet — accept at least one applicant first'}
                    style={!(e.slots_filled>0)?{opacity:0.45,cursor:'not-allowed'}:{}}
                    onClick={()=>exportStaffInfo(e)}
                  >
                    {exportingId===e.id?'Exporting…':'Export staff info'}
                  </button>
                  {/* Session AK: this slot used to hold a "Map" button that built
                      the SAME maps.google.com URL, character for character, as
                      "Get me there" inside the preview sheet. Two affordances for
                      one action, and "Map" as a label says nothing about what
                      pressing it does.
                      ⚠️ Living in the ACTION ROW rather than on the title line also
                      removes the stopPropagation requirement entirely - this row
                      sits OUTSIDE the onClick={()=>setFastEvent(e)} block that
                      wraps the title. One less way to be wrong.
                      ⚠️ NOT gated on (e.location||e.street_address) the way Map
                      was. Map hid itself with no address because it had nothing to
                      open; Preview always applies - and an event MISSING an address
                      is precisely one worth previewing, since that absence is what
                      the staff member will see. */}
                  <button className="btn btn-secondary btn-sm" title="See exactly what your staff see" onClick={()=>setPreviewEvent(e)}>
                    Preview
                  </button>
                  <button className="btn btn-secondary btn-sm" style={{display:'flex',alignItems:'center',gap:6}} onClick={()=>setEditEvent(e)}>
                    <Icon name="edit" size={13}/> Edit
                  </button>
                  <button className="btn btn-sm" style={{display:'flex',alignItems:'center',gap:4,color:'var(--danger)',border:'1px solid rgba(var(--danger-rgb),0.3)',background:'rgba(var(--danger-rgb),0.05)'}} onClick={()=>setDeleteConfirm(e)}>
                    Delete
                  </button>
                </div>
              </div>
              {e.roles&&e.roles.length>0&&(
                <div style={{background:'var(--bg-surface)',border:'1px solid var(--border)',borderRadius:8,padding:12,marginBottom:12}}>
                  {e.roles.map(r=><RoleStaffingBar key={r.id} role={r}/>)}
                </div>
              )}
              <StaffingBar filled={e.slots_filled||0} total={e.slots_total||0} standby={e.standby_count||0} pending={e.pending_count||0}/>
              {(e.slots_remaining>0)&&e.published===1&&(
                <div onClick={()=>setFastEvent(e)} style={{marginTop:10,padding:'8px 12px',background:'rgba(var(--danger-rgb),0.06)',border:'1px solid rgba(var(--danger-rgb),0.15)',borderRadius:6,fontSize:12,color:'var(--danger)',cursor:'pointer'}}>
                  ⚠ {e.slots_remaining} slot{e.slots_remaining>1?'s':''} still unfilled
                </div>
              )}
              {/* Step 3.5 Pass A: two entry points, side by side. "Check-in" (24h before
                  start → event end, self-check-in tally) and "Live Roster" (1h before
                  start → event end, attendance-marked tally). Both open the same roster
                  view — the labels, tallies, and windows are the differentiation. */}
              {(withinCheckinWindow(e) || withinRosterWindow(e)) && (
                <div style={{display:'flex',gap:8,marginTop:10}}>
                  {withinCheckinWindow(e) && (
                    <button
                      onClick={()=>openCheckins(e)}
                      style={{flex:1,padding:'9px 14px',background:'var(--bg-surface)',border:'1px solid var(--border)',borderRadius:8,cursor:'pointer',textAlign:'left',fontFamily:'var(--font)'}}
                    >
                      <div style={{fontSize:13,fontWeight:700,color:'var(--text-primary)',marginBottom:1}}>Check-in</div>
                      <div style={{fontSize:12,color:'var(--text-secondary)'}}>
                        <span style={{fontWeight:700,color:e.checkin_count>0?'#16a34a':'var(--text-primary)'}}>{e.checkin_count||0}</span>
                        <span>/{e.slots_filled||0} self-checked-in</span>
                      </div>
                    </button>
                  )}
                  {withinRosterWindow(e) && (
                    <button
                      onClick={()=>openCheckins(e)}
                      style={{flex:1,padding:'9px 14px',background:'rgba(22,163,74,0.08)',border:'1.5px solid rgba(22,163,74,0.4)',borderRadius:8,cursor:'pointer',textAlign:'left',fontFamily:'var(--font)'}}
                    >
                      <div style={{fontSize:13,fontWeight:800,color:'#16a34a',marginBottom:1}}>Live Roster</div>
                      <div style={{fontSize:12,color:'var(--text-secondary)'}}>
                        <span style={{fontWeight:700,color:e.attendance_marked_count>0?'#16a34a':'var(--text-primary)'}}>{e.attendance_marked_count||0}</span>
                        <span>/{e.slots_filled||0} marked</span>
                      </div>
                    </button>
                  )}
                </div>
              )}
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginTop:14,paddingTop:12,borderTop:'1px solid var(--border)'}}>
                <div className="toggle-wrap">
                  <div className={`toggle ${e.published?'on':''}`} onClick={()=>togglePublish(e)}/>
                  <span>{e.published?'Published — visible to approved staff':'Draft — not visible to staff'}</span>
                </div>
                {e.dress_code&&<span style={{fontSize:12,color:'var(--text-secondary)'}}>Dress code: {e.dress_code}</span>}
              </div>
            </div>
          );
          return (<>
          {groupedUpcoming.map(item=>item.type==='single'
            ? renderUpcomingCard(item.e)
            : <SeriesGroupCard key={`series-${item.key}`} days={item.days} renderDay={renderUpcomingCard} onPublishSeries={publishSeries} seriesBusy={seriesBusy}/>)}
          </>);
          })()}

          {/* Past Events collapsible */}
          {pastEvents.length>0&&(
            <div ref={pastRef}>
              <button
                onClick={()=>setShowPast(v=>!v)}
                style={{width:'100%',display:'flex',justifyContent:'space-between',alignItems:'center',background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:'var(--radius)',padding:'14px 20px',cursor:'pointer',boxShadow:'var(--shadow)',marginBottom:showPast?14:0}}
              >
                <div style={{display:'flex',alignItems:'center',gap:10}}>
                  <span style={{fontSize:15,fontWeight:700,color:'var(--text-secondary)'}}>📂 Past Events</span>
                  <span style={{fontSize:12,color:'var(--text-muted)'}}>({pastEvents.length})</span>
                </div>
                <span style={{fontSize:11,color:'var(--text-secondary)',display:'inline-block',transition:'transform 0.2s',transform:showPast?'rotate(180deg)':'rotate(0deg)'}}>▼</span>
              </button>
              {showPast&&(
                <div style={{display:'grid',gap:10}}>
                  <input
                    className="form-input"
                    placeholder="Search past events..."
                    value={pastSearch}
                    onChange={e=>setPastSearch(e.target.value)}
                    style={{marginBottom:4,fontSize:13,padding:'9px 14px'}}
                  />
                  {pastEvents
                    .filter(e=>!pastSearch.trim()||e.title.toLowerCase().includes(pastSearch.toLowerCase()))
                    .map(e=>(
                    <div key={e.id} className="agent-section-card" style={{opacity:0.75,borderLeft:'3px solid var(--border)'}}>
                      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:8}}>
                        <div style={{cursor:'pointer',flex:1}} onClick={()=>setRatingEvent(e)}>
                          <div style={{fontWeight:700,fontSize:15,color:'var(--text-primary)'}}>{e.title}{(e.series_size||1)>1&&<span className="badge badge-series" style={{marginLeft:8}}>DAY {e.day_number}/{e.series_size}</span>}</div>
                          <div style={{fontSize:12,color:'var(--text-secondary)',marginTop:2}}>{formatDate(e.date)} · {e.time_start}–{e.time_end} · {[e.location, e.street_address].filter(Boolean).join(', ')}</div>
                          <div style={{fontSize:11,fontWeight:600,color:'var(--accent)',marginTop:6}}>Click to rate staff & adjust pay →</div>
                        </div>
                        <div style={{display:'flex',gap:8,marginLeft:12}}>
                          {/* Session R: rosters stay reachable after the event ends —
                              same controls as the live roster, settled rows locked. */}
                          <button className="btn btn-secondary btn-sm" style={{display:'flex',alignItems:'center',gap:6}}
                            onClick={()=>openCheckins(e)}>
                            📋 View / Edit Roster
                          </button>
                          {/* Session P: same bank export as Payroll → Per event */}
                          <button className="btn btn-secondary btn-sm" style={{display:'flex',alignItems:'center',gap:6}}
                            disabled={paymentExportingId===e.id} onClick={()=>exportPayments(e)}>
                            {paymentExportingId===e.id ? 'Exporting…' : '🏦 Payments CSV'}
                          </button>
                          <button className="btn btn-secondary btn-sm" style={{display:'flex',alignItems:'center',gap:6}} onClick={()=>setEditEvent(e)}>
                            <Icon name="edit" size={13}/> Edit
                          </button>
                          <button className="btn btn-sm" style={{display:'flex',alignItems:'center',gap:4,color:'var(--danger)',border:'1px solid rgba(var(--danger-rgb),0.3)',background:'rgba(var(--danger-rgb),0.05)'}} onClick={()=>setDeleteConfirm(e)}>
                            Delete
                          </button>
                        </div>
                      </div>
                      {e.roles&&e.roles.length>0&&(
                        <div style={{background:'var(--bg-surface)',border:'1px solid var(--border)',borderRadius:8,padding:12,marginBottom:8}}>
                          {e.roles.map(r=><RoleStaffingBar key={r.id} role={r}/>)}
                        </div>
                      )}
                      <StaffingBar filled={e.slots_filled||0} total={e.slots_total||0} standby={e.standby_count||0} pending={e.pending_count||0}/>
                      {/* Session R: unmarked-attendance nudge. Unmarked staff are PAID
                          IN FULL — this never blocks or withholds, it just makes the
                          gap visible before the bank export goes out. */}
                      {(e.unmarked_open_count||0)>0&&unmarkedCount(e)>0&&(
                        <div style={{marginTop:10,padding:'8px 12px',background:'rgba(202,138,4,0.08)',border:'1px solid rgba(202,138,4,0.3)',borderRadius:6,fontSize:12,color:'#ca8a04'}}>
                          ⚠ {unmarkedCount(e)} of {e.slots_filled||0} not marked — unmarked staff are paid in full. Open the roster to reconcile.
                        </div>
                      )}
                    </div>
                  ))}
                  {pastEvents.filter(e=>!pastSearch.trim()||e.title.toLowerCase().includes(pastSearch.toLowerCase())).length===0&&(
                    <div className="empty-state" style={{fontSize:13}}>No past events match "{pastSearch}"</div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {deleteConfirm&&(
        <div onClick={e=>{if(e.target===e.currentTarget)setDeleteConfirm(null);}} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.55)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:200,padding:'20px',backdropFilter:'blur(2px)'}}>
          <div style={{background:'var(--bg-card)',borderRadius:'var(--radius-lg)',padding:'32px',width:'100%',maxWidth:460,boxShadow:'0 8px 40px rgba(0,0,0,0.25)'}}>
            <div style={{fontWeight:700,fontSize:18,color:'var(--text-primary)',marginBottom:8}}>Delete Event</div>
            <div style={{fontSize:14,color:'var(--text-secondary)',marginBottom:6,lineHeight:1.6}}>
              Are you sure you want to delete <strong>{deleteConfirm.title}</strong>?
            </div>
            <div style={{fontSize:13,color:'var(--text-secondary)',marginBottom:24,padding:'10px 14px',background:'rgba(var(--danger-rgb),0.06)',border:'1px solid rgba(var(--danger-rgb),0.2)',borderRadius:8,lineHeight:1.6}}>
              All confirmed and pending staff will receive a cancellation notification. This action cannot be undone.
            </div>
            <div style={{display:'flex',gap:12}}>
              <button onClick={()=>setDeleteConfirm(null)} style={{flex:1,padding:'11px',borderRadius:8,border:'1px solid var(--border)',background:'var(--bg-surface)',fontSize:14,fontWeight:600,cursor:'pointer',color:'var(--text-secondary)'}}>
                Cancel
              </button>
              <button onClick={handleDelete} disabled={deleting} style={{flex:2,padding:'11px',borderRadius:8,border:'none',background:'var(--danger)',color:'#fff',fontSize:14,fontWeight:700,cursor:'pointer'}}>
                {deleting ? 'Deleting...' : 'Delete & Notify Staff'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Session AK: the agent preview. SAME component Book a Shift renders —
          ⚠️ never a copy. `transport` was invisible on every staff screen for
          months precisely because nothing tied the agent's view of the data to
          the staff's. previewNote adds the explanatory banner staff never see. */}
      <EventDetailSheet event={previewEvent} onClose={() => setPreviewEvent(null)} previewNote />
    </>
  );
}

// ── Post Event ────────────────────────────────────────────────────────────────
function PostEvent({ setActive }) {
  const [done,setDone]=useState(false);
  const handleSave=async(data)=>{
    const r = await axios.post('/api/events',data);
    setDone(true); setTimeout(()=>{setDone(false);setActive('events');},1500);
    return r.data?.id;
  };
  return (
    <>
      <div className="agent-page-title">Post an Event</div>
      <div className="agent-page-sub">Add multiple roles for the same event — e.g. 5 bartenders + 10 waiters.</div>
      <div className="agent-section-card" style={{maxWidth:640}}>
        {done&&<div style={{padding:12,background:'rgba(22,163,74,0.1)',border:'1px solid rgba(22,163,74,0.3)',borderRadius:8,color:'var(--green)',marginBottom:16,fontSize:13}}>✓ Event created! Redirecting...</div>}
        <EventForm onSave={handleSave} onCancel={()=>setActive('events')}/>
      </div>
    </>
  );
}

// ── Send Update ───────────────────────────────────────────────────────────────
// Message rendering in sent history: overflow fix + 3-line clamp + linkified
// URLs — mirrors what staff see in Updates.js so the agent's view matches.
const URL_RE = /((?:https?:\/\/|www\.)[^\s]+)/gi;
const linkifyMsg = text => String(text || '').split(URL_RE).map((part, i) => {
  if (i % 2 === 1) {
    const href = part.startsWith('www.') ? `https://${part}` : part;
    return <a key={i} href={href} target="_blank" rel="noopener noreferrer" onClick={e=>e.stopPropagation()} style={{color:'var(--accent)',fontWeight:600,textDecoration:'underline'}}>{part}</a>;
  }
  return part;
});

function UpdateMessage({ text, style }) {
  const [expanded, setExpanded] = useState(false);
  const [clamped, setClamped] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) setClamped(ref.current.scrollHeight > ref.current.clientHeight + 1);
  }, [text]);
  return (
    <>
      <div ref={ref}
        onClick={()=>{ if (clamped && !expanded) setExpanded(true); }}
        style={{
          ...style,
          overflowWrap:'anywhere', wordBreak:'break-word', whiteSpace:'pre-wrap',
          cursor: clamped && !expanded ? 'pointer' : 'default',
          ...(expanded ? {} : {display:'-webkit-box', WebkitLineClamp:3, WebkitBoxOrient:'vertical', overflow:'hidden'}),
        }}>
        {linkifyMsg(text)}
      </div>
      {(clamped || expanded) && (
        <button onClick={e=>{e.stopPropagation(); setExpanded(v=>!v);}}
          style={{background:'none',border:'none',padding:0,marginTop:2,fontSize:12,fontWeight:600,color:'var(--accent)',cursor:'pointer',fontFamily:'inherit'}}>
          {expanded ? 'Show less' : 'Read more…'}
        </button>
      )}
    </>
  );
}

function SendUpdate() {
  const [form,setForm]=useState({title:'',message:'',type:'info'});
  const [saving,setSaving]=useState(false);
  const [done,setDone]=useState(false);
  const [sendToAll,setSendToAll]=useState(true);
  const [sendToEvent,setSendToEvent]=useState(false);
  const [events,setEvents]=useState([]);
  const [selectedEvent,setSelectedEvent]=useState('');
  const [showEvents,setShowEvents]=useState(false);
  const [sentUpdates,setSentUpdates]=useState([]);
  const [loadingSent,setLoadingSent]=useState(true);
  const [showSent,setShowSent]=useState(false);
  // Session S: SMS nudge is an explicit opt-in with a visible recipient count
  const [smsNudge,setSmsNudge]=useState(false);
  const [smsCount,setSmsCount]=useState(null);
  // Session M2: send to every day of a multi-day event in one action
  const [wholeSeries,setWholeSeries]=useState(false);

  useEffect(()=>{
    axios.get('/api/events').then(r=>setEvents(r.data));
    axios.get('/api/agent/updates/sent').then(r=>setSentUpdates(r.data)).finally(()=>setLoadingSent(false));
  },[]);

  // Recount whenever the target event changes; untick on any audience change
  // so a previous decision never silently carries over to a new audience.
  useEffect(()=>{
    setSmsNudge(false);
    if(sendToEvent && selectedEvent){
      setSmsCount(null);
      axios.post('/api/agent/sms/preview',{audience:{type:'event',event_id:parseInt(selectedEvent),series:wholeSeries}})
        .then(r=>setSmsCount(r.data.count))
        .catch(()=>setSmsCount(null));
    } else {
      setSmsCount(null);
    }
  },[sendToEvent,selectedEvent,wholeSeries]);

  // Session M2: untick "all days" whenever the target changes — a series
  // decision must never silently carry over to a different event.
  useEffect(()=>{ setWholeSeries(false); },[selectedEvent,sendToEvent]);

  const set=k=>e=>setForm(f=>({...f,[k]:e.target.value}));

  const handleSendToAll=()=>{ setSendToAll(true); setSendToEvent(false); setShowEvents(false); setSelectedEvent(''); };
  const handleSendToEvent=()=>{ setSendToEvent(v=>{ const next=!v; setShowEvents(next); if(!next)setSelectedEvent(''); return next; }); setSendToAll(false); };

  const submit=async e=>{
    e.preventDefault();
    if(sendToEvent && !selectedEvent) return alert('Please select an event.');
    setSaving(true);
    const payload={...form, ...(sendToEvent ? {event_id:selectedEvent, send_sms: smsNudge, series: wholeSeries} : {})};
    const res=await axios.post('/api/agent/updates',payload);
    // Refresh sent history
    axios.get('/api/agent/updates/sent').then(r=>setSentUpdates(r.data));
    setSaving(false);setDone(true);
    setForm({title:'',message:'',type:'info'});
    setSelectedEvent('');setSendToAll(true);setSendToEvent(false);setShowEvents(false);setSmsNudge(false);setWholeSeries(false);
    setTimeout(()=>setDone(false),3000);
  };

  const btnLabel = saving ? 'Sending...' : sendToEvent && selectedEvent ? 'Send to Event Applicants' : 'Send to All Staff';

  const typeColor = type => {
    if(type==='urgent') return {bg:'rgba(var(--danger-rgb),0.08)',border:'rgba(var(--danger-rgb),0.25)',text:'var(--danger)'};
    if(type==='event') return {bg:'rgba(37,99,235,0.08)',border:'rgba(37,99,235,0.2)',text:'#2563eb'};
    return {bg:'var(--bg-surface)',border:'var(--border)',text:'var(--text-secondary)'};
  };

  return (
    <>
      <div className="agent-page-title">Send Update</div>
      <div className="agent-page-sub">Sends an <strong>in-app update</strong> to the staff Updates tab — no SMS by default. Event updates can add an SMS nudge with the checkbox (uses credits, always shows the count first).</div>
      <div className="agent-section-card" style={{maxWidth:560}}>
        {done&&<div style={{padding:12,background:'rgba(22,163,74,0.1)',border:'1px solid rgba(22,163,74,0.3)',borderRadius:8,color:'var(--green)',marginBottom:16,fontSize:13}}>✓ Update sent.</div>}
        <form onSubmit={submit}>
          <div className="form-group"><label className="form-label">Type</label>
            <select className="form-select" value={form.type} onChange={set('type')}>
              <option value="info">General Info</option>
              <option value="event">Event Brief Change</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>
          <div className="form-group"><label className="form-label">Title</label><input className="form-input" placeholder="e.g. Uniform policy update" value={form.title} onChange={set('title')} required/></div>
          <div className="form-group"><label className="form-label">Message</label><textarea className="form-textarea" rows={5} value={form.message} onChange={set('message')} required/></div>

          {/* Audience targeting */}
          <div className="form-group">
            <label className="form-label">Send to</label>
            <div style={{display:'flex',flexDirection:'column',gap:10,background:'var(--bg-surface)',border:'1px solid var(--border)',borderRadius:8,padding:14}}>
              <label style={{display:'flex',alignItems:'center',gap:10,cursor:'pointer',fontSize:13,color:'var(--text-primary)'}}>
                <input type="checkbox" checked={sendToAll} onChange={handleSendToAll} style={{width:16,height:16,cursor:'pointer'}}/>
                <span style={{fontWeight:600}}>All Staff</span>
                <span style={{fontSize:11,color:'var(--text-muted)'}}>— broadcasts to every approved staff member</span>
              </label>
              <label style={{display:'flex',alignItems:'center',gap:10,cursor:'pointer',fontSize:13,color:'var(--text-primary)'}}>
                <input type="checkbox" checked={sendToEvent} onChange={handleSendToEvent} style={{width:16,height:16,cursor:'pointer'}}/>
                <span style={{fontWeight:600}}>Event Applicants</span>
                <span style={{fontSize:11,color:'var(--text-muted)'}}>— notify only staff linked to a specific event</span>
              </label>
              {showEvents && (
                <div style={{marginTop:4,paddingLeft:26}}>
                  <select className="form-select" value={selectedEvent} onChange={e=>setSelectedEvent(e.target.value)} required={sendToEvent}>
                    <option value="">Select an event...</option>
                    {events.filter(e=>{const today=new Date().toISOString().slice(0,10);return e.date>=today;}).map(e=><option key={e.id} value={e.id}>{e.title} — {formatDate(e.date)}</option>)}
                  </select>
                  {/* Session M2: multi-day events can target every day at once —
                      recipients are the DISTINCT union across the series, one
                      update per person, anchored on the series thread. */}
                  {selectedEvent && (()=>{ const sel=events.find(e=>String(e.id)===String(selectedEvent)); return sel && (sel.series_size||1)>1; })() && (
                    <label style={{display:'flex',alignItems:'center',gap:8,marginTop:10,cursor:'pointer',fontSize:12,color:'var(--text-primary)'}}>
                      <input type="checkbox" checked={wholeSeries} onChange={e=>setWholeSeries(e.target.checked)} style={{width:15,height:15,cursor:'pointer'}}/>
                      <span style={{fontWeight:600}}>All {(events.find(e=>String(e.id)===String(selectedEvent))||{}).series_size} days of this event</span>
                      <span style={{fontSize:11,color:'var(--text-muted)'}}>— one update per person across the whole series</span>
                    </label>
                  )}
                  {/* Session S: explicit SMS opt-in, default OFF, count visible */}
                  {selectedEvent && (
                    <label style={{display:'flex',alignItems:'center',gap:8,marginTop:10,cursor:'pointer',fontSize:12,color:'var(--text-primary)'}}>
                      <input type="checkbox" checked={smsNudge} onChange={e=>setSmsNudge(e.target.checked)} style={{width:15,height:15,cursor:'pointer'}}/>
                      <span style={{fontWeight:600}}>Also send SMS{smsCount!=null?` — ${smsCount} recipient${smsCount===1?'':'s'}`:''}</span>
                      <span style={{fontSize:11,color:'var(--text-muted)'}}>({smsCount!=null?`uses ~${smsCount} credit${smsCount===1?'':'s'}`:'counting…'})</span>
                    </label>
                  )}
                </div>
              )}
            </div>
          </div>

          <button className="btn btn-primary" type="submit" disabled={saving}>{btnLabel}</button>
        </form>
      </div>

      {/* ── Sent History ── */}
      <div style={{maxWidth:560,marginTop:24}}>
        <button
          onClick={()=>setShowSent(v=>!v)}
          style={{width:'100%',display:'flex',justifyContent:'space-between',alignItems:'center',background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:'var(--radius)',padding:'14px 20px',cursor:'pointer',boxShadow:'var(--shadow)'}}
        >
          <div style={{display:'flex',alignItems:'center',gap:10}}>
            <span style={{fontSize:15,fontWeight:700,color:'var(--text-primary)'}}>📤 Sent Updates</span>
            {!loadingSent&&<span style={{fontSize:12,color:'var(--text-muted)'}}>({sentUpdates.length} total)</span>}
          </div>
          <span style={{fontSize:11,color:'var(--text-secondary)',transform:showSent?'rotate(180deg)':'rotate(0deg)',display:'inline-block',transition:'transform 0.2s'}}>▼</span>
        </button>

        {showSent&&(
          <div style={{background:'var(--bg-card)',border:'1px solid var(--border)',borderTop:'none',borderRadius:'0 0 var(--radius) var(--radius)',padding:'0 20px 16px',boxShadow:'var(--shadow)'}}>
            {loadingSent ? <div className="loading">Loading...</div>
            : sentUpdates.length===0 ? <div className="empty-state">No updates sent yet.</div>
            : sentUpdates.map(u=>{
              const c=typeColor(u.type);
              return (
                <div key={u.id} style={{padding:'14px 0',borderBottom:'1px solid var(--border)'}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:6}}>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontWeight:700,fontSize:14,color:'var(--text-primary)',marginBottom:3,overflowWrap:'anywhere'}}>{u.title}</div>
                      <UpdateMessage text={u.message} style={{fontSize:13,color:'var(--text-secondary)',lineHeight:1.5}}/>
                    </div>
                    <span style={{fontSize:10,fontWeight:700,padding:'3px 8px',borderRadius:4,background:c.bg,border:`1px solid ${c.border}`,color:c.text,marginLeft:12,whiteSpace:'nowrap'}}>
                      {u.type.toUpperCase()}
                    </span>
                  </div>
                  <div style={{display:'flex',gap:12,alignItems:'center',flexWrap:'wrap',marginTop:6}}>
                    {/* Who it was sent to */}
                    <span style={{fontSize:11,color:'var(--text-muted)',display:'flex',alignItems:'center',gap:4}}>
                      {u.event_title
                        ? <><span style={{color:'var(--text-secondary)',fontWeight:600}}>📅 {u.event_title}</span> applicants</>
                        : <><span>📣</span> All staff</>
                      }
                    </span>
                    {/* Timestamp */}
                    <span style={{fontSize:11,color:'var(--text-muted)'}}>
                      {u.created_at && new Date(u.created_at).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'})}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Session S: admin SMS composer lives on the same Comms page */}
      <SmsComposer/>
    </>
  );
}

// ── Session S: Admin SMS composer ─────────────────────────────────────────────
// Deliberate, targeted, counted sends — the only broadcast-capable SMS path
// after the trigger prune. Two-step flow: compose + live recipient count, then
// an explicit confirmation showing count × segments ≈ credits before anything
// sends. History reads from sms_log.
function SmsComposer(){
  const [message,setMessage]=useState('');
  const [audType,setAudType]=useState('event');
  const [staff,setStaff]=useState([]);
  const [events,setEvents]=useState([]);
  const [userId,setUserId]=useState('');
  const [userIds,setUserIds]=useState([]);
  const [staffFilter,setStaffFilter]=useState('');
  const [skill,setSkill]=useState('');
  const [tier,setTier]=useState('');
  const [eventId,setEventId]=useState('');
  const [count,setCount]=useState(null);
  const [noPhone,setNoPhone]=useState(0);
  const [confirming,setConfirming]=useState(false);
  const [sending,setSending]=useState(false);
  const [sentFlash,setSentFlash]=useState('');
  const [smsLog,setSmsLog]=useState([]);
  const [showLog,setShowLog]=useState(false);

  useEffect(()=>{
    axios.get('/api/agent/staff').then(r=>setStaff(r.data.filter(s=>s.status==='approved')));
    axios.get('/api/events').then(r=>setEvents(r.data));
    axios.get('/api/agent/sms/log').then(r=>setSmsLog(r.data)).catch(()=>{});
  },[]);

  const audience=()=>{
    switch(audType){
      case 'user': return {type:'user', user_id: parseInt(userId)};
      case 'users': return {type:'users', user_ids: userIds};
      case 'skill': return {type:'skill', skill};
      case 'tier': return {type:'tier', tier};
      case 'event':
      case 'past_event': return {type:'event', event_id: parseInt(eventId)}; // same server audience — the split is a UI filter
      case 'all': return {type:'all'};
      default: return null;
    }
  };
  const audienceReady =
    (audType==='user' && userId) || (audType==='users' && userIds.length>0) ||
    (audType==='skill' && skill) || (audType==='tier' && tier) ||
    ((audType==='event'||audType==='past_event') && eventId) || audType==='all';

  // Live recipient count; any audience change also cancels a pending confirm
  // so the number on the confirm panel can never be stale.
  useEffect(()=>{
    setConfirming(false);
    if(!audienceReady){ setCount(null); return; }
    setCount(null);
    const a={type:(audType==='past_event'?'event':audType),user_id:parseInt(userId)||undefined,user_ids:userIds,skill,tier,event_id:parseInt(eventId)||undefined};
    axios.post('/api/agent/sms/preview',{audience:a})
      .then(r=>{setCount(r.data.count);setNoPhone(r.data.no_phone);})
      .catch(()=>setCount(null));
  },[audType,userId,userIds,skill,tier,eventId,audienceReady]);

  // Same brand-in-body the server applies — the preview matches what lands.
  // Part counting mirrors the server's smsParts: GSM extended characters
  // ( ^ { } \ [ ~ ] | € ) cost double, anything outside GSM flips to UCS-2.
  const brand=(`${process.env.REACT_APP_BRAND_PREFIX||''}${process.env.REACT_APP_BRAND_SUFFIX||''}`.trim())||'SHIFTon';
  const preview=`${brand}: ${message}`;
  const clientSmsInfo=(txt)=>{
    const t=String(txt).replace(/[\u2018\u2019]/g,"'").replace(/[\u201C\u201D]/g,'"').replace(/[\u2013\u2014]/g,'-').replace(/\u00A0/g,' ');
    const GSM='@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà';
    const EXT='^{}\\[~]|€';
    let units=0;
    for(const ch of t){
      if(GSM.includes(ch)) units+=1;
      else if(EXT.includes(ch)) units+=2;
      else { const len=[...t].length; return {ucs2:true, units:len, single:70, parts: len<=70?1:Math.ceil(len/67)}; }
    }
    return {ucs2:false, units, single:160, parts: units<=160?1:Math.ceil(units/153)};
  };
  const info=message ? clientSmsInfo(preview) : null;
  const segments=info ? info.parts : 0;
  // Amber early warning: within 25 chars of the branded body flipping to a
  // second part (per-brand accurate — a fixed message-length threshold would
  // be wrong for every brand except one).
  const partRoom=info && info.parts===1 ? info.single - info.units : 0;
  const nearBoundary=info ? (info.parts===1 && partRoom<=25) : false;
  const credits=(count!=null&&segments>0)?segments*count:null;

  const skills=[...new Set(staff.flatMap(s=>(s.skills||'').split(',').map(x=>x.trim()).filter(Boolean)))].sort();
  const filteredStaff=staff.filter(s=>(s.name||'').toLowerCase().includes(staffFilter.toLowerCase()));
  const toggleUser=id=>setUserIds(ids=>ids.includes(id)?ids.filter(x=>x!==id):[...ids,id]);

  const doSend=async()=>{
    setSending(true);
    try{
      const r=await axios.post('/api/agent/sms/send',{message,audience:audience()});
      setSentFlash(`✓ SMS sent to ${r.data.sent} recipient${r.data.sent===1?'':'s'}.`);
      setMessage('');setConfirming(false);
      axios.get('/api/agent/sms/log').then(x=>setSmsLog(x.data)).catch(()=>{});
      setTimeout(()=>setSentFlash(''),4000);
    }catch(err){
      alert(err.response?.data?.error||'SMS send failed.');
    }
    setSending(false);
  };

  const selStyle={width:16,height:16,cursor:'pointer'};

  return (
    <>
      <div className="agent-page-title" style={{marginTop:32}}>SMS Composer</div>
      <div className="agent-page-sub">Send a direct SMS to chosen staff. Every SMS uses credits from the monthly allowance — the recipient count always shows before you send.</div>
      <div className="agent-section-card" style={{maxWidth:560}}>
        {sentFlash&&<div style={{padding:12,background:'rgba(22,163,74,0.1)',border:'1px solid rgba(22,163,74,0.3)',borderRadius:8,color:'var(--green)',marginBottom:16,fontSize:13}}>{sentFlash}</div>}

        <div className="form-group"><label className="form-label">Send to</label>
          <select className="form-select" value={audType} onChange={e=>{setAudType(e.target.value);setEventId('');}}>
            <option value="user">One staff member</option>
            <option value="users">Selected staff</option>
            <option value="skill">A skill group</option>
            <option value="tier">A tier</option>
            <option value="event">Everyone confirmed on an upcoming event</option>
            <option value="past_event">Everyone confirmed on a past event</option>
            <option value="all">Entire approved database</option>
          </select>
        </div>

        {audType==='user' && (
          <div className="form-group">
            <select className="form-select" value={userId} onChange={e=>setUserId(e.target.value)}>
              <option value="">Select staff member...</option>
              {staff.map(s=><option key={s.id} value={s.id}>{s.name}{s.phone?'':' (no phone)'}</option>)}
            </select>
          </div>
        )}

        {audType==='users' && (
          <div className="form-group">
            <input className="form-input" placeholder="Filter by name..." value={staffFilter} onChange={e=>setStaffFilter(e.target.value)} style={{marginBottom:8}}/>
            <div style={{maxHeight:180,overflowY:'auto',border:'1px solid var(--border)',borderRadius:8,padding:'6px 10px',background:'var(--bg-surface)'}}>
              {filteredStaff.length===0 && <div style={{fontSize:12,color:'var(--text-muted)',padding:'6px 0'}}>No matching staff.</div>}
              {filteredStaff.map(s=>(
                <label key={s.id} style={{display:'flex',alignItems:'center',gap:8,padding:'5px 0',cursor:'pointer',fontSize:13,color:'var(--text-primary)'}}>
                  <input type="checkbox" checked={userIds.includes(s.id)} onChange={()=>toggleUser(s.id)} style={selStyle}/>
                  <span>{s.name}</span>
                  {!s.phone&&<span style={{fontSize:10,color:'#ca8a04',fontWeight:700}}>no phone</span>}
                </label>
              ))}
            </div>
            {userIds.length>0&&<div style={{fontSize:11,color:'var(--text-muted)',marginTop:6}}>{userIds.length} selected</div>}
          </div>
        )}

        {audType==='skill' && (
          <div className="form-group">
            <select className="form-select" value={skill} onChange={e=>setSkill(e.target.value)}>
              <option value="">Select skill...</option>
              {skills.map(sk=><option key={sk} value={sk}>{sk}</option>)}
            </select>
          </div>
        )}

        {audType==='tier' && (
          <div className="form-group">
            <select className="form-select" value={tier} onChange={e=>setTier(e.target.value)} style={{textTransform:'capitalize'}}>
              <option value="">Select tier...</option>
              {TIER_OPTIONS.map(t=><option key={t} value={t}>{t.charAt(0).toUpperCase()+t.slice(1)}</option>)}
            </select>
          </div>
        )}

        {(audType==='event'||audType==='past_event') && (
          <div className="form-group">
            <select className="form-select" value={eventId} onChange={e=>setEventId(e.target.value)}>
              <option value="">Select {audType==='past_event'?'a past':'an upcoming'} event...</option>
              {events
                .filter(e=>{const today=new Date().toISOString().slice(0,10);return audType==='past_event' ? e.date<today : e.date>=today;})
                .sort((a,b)=>audType==='past_event' ? (b.date||'').localeCompare(a.date||'') : (a.date||'').localeCompare(b.date||''))
                .map(e=><option key={e.id} value={e.id}>{e.title} — {formatDate(e.date)}</option>)}
            </select>
            {audType==='past_event'&&<div style={{fontSize:11,color:'var(--text-muted)',marginTop:5}}>Useful for post-event messages like thanks or payment notes.</div>}
          </div>
        )}

        {audType==='all' && (
          <div style={{marginBottom:14,padding:'10px 12px',background:'rgba(202,138,4,0.08)',border:'1px solid rgba(202,138,4,0.4)',borderRadius:8,fontSize:12,color:'var(--text-secondary)'}}>
            ⚠️ This messages <strong>every approved staff member</strong> in the database — the most expensive audience there is.
          </div>
        )}

        <div className="form-group"><label className="form-label">Message</label>
          <textarea className="form-textarea" rows={4} value={message} onChange={e=>{setMessage(e.target.value);setConfirming(false);}} placeholder="Keep it short — every 153 characters after the first 160 adds a full extra credit per recipient." maxLength={450}/>
          {message&&(
            <div style={{marginTop:8,padding:'8px 12px',background:'var(--bg-surface)',border:'1px solid var(--border)',borderRadius:8}}>
              <div style={{fontSize:11,fontWeight:700,color:'var(--text-muted)',marginBottom:3}}>WILL SEND AS</div>
              <div style={{fontSize:12,color:'var(--text-primary)',overflowWrap:'anywhere'}}>{preview}</div>
              <div style={{fontSize:11,color:segments>3?'var(--danger)':(segments>1||nearBoundary)?'#ca8a04':'var(--text-muted)',marginTop:4}}>
                {segments===1
                  ? `${info.units}/${info.single} chars incl. branding${info.ucs2?' (emoji — shorter limit)':''} · 1 part${nearBoundary?` — only ${partRoom} left before it becomes 2 parts (2× credits)`:''}`
                  : `${info.units} chars incl. branding · ${segments} SMS parts${segments>3?' — over the 3-part limit, shorten the message (brackets and special characters count double)':` — each recipient costs ${segments} credits`}`}
              </div>
            </div>
          )}
        </div>

        <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:14,fontSize:13,color:'var(--text-primary)'}}>
          <span style={{fontWeight:700}}>Recipients: {audienceReady ? (count!=null?count:'counting…') : '—'}</span>
          {noPhone>0&&audienceReady&&count!=null&&<span style={{fontSize:11,color:'#ca8a04'}}>({noPhone} without phone numbers excluded)</span>}
        </div>

        {!confirming ? (
          <button className="btn btn-primary" type="button"
            disabled={!message.trim()||!audienceReady||count===null||count===0||segments>3}
            onClick={()=>setConfirming(true)}>
            Review &amp; Send
          </button>
        ) : (
          <div style={{border:'1px solid rgba(202,138,4,0.5)',background:'rgba(202,138,4,0.06)',borderRadius:8,padding:14}}>
            <div style={{fontSize:13,fontWeight:700,color:'var(--text-primary)',marginBottom:6}}>Confirm this send</div>
            <div style={{fontSize:12,color:'var(--text-secondary)',lineHeight:1.7,marginBottom:10}}>
              <strong>{count}</strong> recipient{count===1?'':'s'} × <strong>{segments}</strong> SMS part{segments===1?'':'s'} ≈ <strong>{credits}</strong> credit{credits===1?'':'s'} from the monthly allowance.
            </div>
            <div style={{display:'flex',gap:8}}>
              <button className="btn btn-secondary btn-sm" type="button" onClick={()=>setConfirming(false)} disabled={sending}>Cancel</button>
              <button className="btn btn-primary btn-sm" type="button" onClick={doSend} disabled={sending}>{sending?'Sending…':`Confirm — send to ${count}`}</button>
            </div>
          </div>
        )}
      </div>

      {/* ── SMS send history (sms_log) ── */}
      <div style={{maxWidth:560,marginTop:24}}>
        <button
          onClick={()=>setShowLog(v=>!v)}
          style={{width:'100%',display:'flex',justifyContent:'space-between',alignItems:'center',background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:'var(--radius)',padding:'14px 20px',cursor:'pointer',boxShadow:'var(--shadow)'}}
        >
          <div style={{display:'flex',alignItems:'center',gap:10}}>
            <span style={{fontSize:15,fontWeight:700,color:'var(--text-primary)'}}>📱 Sent SMS</span>
            <span style={{fontSize:12,color:'var(--text-muted)'}}>({smsLog.length} recorded)</span>
          </div>
          <span style={{fontSize:11,color:'var(--text-secondary)',transform:showLog?'rotate(180deg)':'rotate(0deg)',display:'inline-block',transition:'transform 0.2s'}}>▼</span>
        </button>
        {showLog&&(
          <div style={{background:'var(--bg-card)',border:'1px solid var(--border)',borderTop:'none',borderRadius:'0 0 var(--radius) var(--radius)',padding:'0 20px 16px',boxShadow:'var(--shadow)'}}>
            {smsLog.length===0 ? <div className="empty-state">No SMS sends recorded yet.</div>
            : smsLog.map(l=>(
              <div key={l.id} style={{padding:'12px 0',borderBottom:'1px solid var(--border)'}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:10}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontWeight:700,fontSize:13,color:'var(--text-primary)',marginBottom:2}}>{l.audience} · {l.recipient_count} recipient{l.recipient_count===1?'':'s'}</div>
                    <UpdateMessage text={l.message} style={{fontSize:12,color:'var(--text-secondary)',lineHeight:1.5}}/>
                  </div>
                  <div style={{fontSize:11,color:'var(--text-muted)',whiteSpace:'nowrap'}}>{new Date(l.created_at).toLocaleDateString('en-ZA',{day:'2-digit',month:'short'})}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
// ── Payroll page (step 4): outstanding balances, settle, paid history ─────────
function Payroll({ focusStaff, clearFocus }) {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);       // user_id whose shifts are open
  const [historyFor, setHistoryFor] = useState(null);   // user_id whose paid history is open
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [settling, setSettling] = useState(false);

  // ── Session P: per-event view ──
  // Per-staff and per-event are two groupings of the SAME application rows
  // (to_pay + paid) — settling in either view updates both. One flag, no
  // second ledger.
  const [view, setView] = useState('staff');                // 'staff' | 'event'
  const [evList, setEvList] = useState([]);
  const [evLoading, setEvLoading] = useState(false);
  const [evLoaded, setEvLoaded] = useState(false);
  const [evExpanded, setEvExpanded] = useState(null);       // event_id open
  const [evDownloading, setEvDownloading] = useState(null); // `${event_id}-${format}`
  const [evFilter, setEvFilter] = useState('outstanding');  // 'outstanding' | 'settled' — 21 July tweak

  // ── Session Y: payroll search ───────────────────────────────────────────────
  // One box, the only two questions asked on payment day: "what do I owe Sipho?"
  // and "what did Saturday cost me?". Filtering is a VIEW concern and nothing
  // else. The paid flag, every server-side settle guard, and the id lists the
  // Settle buttons build are all untouched — those buttons read from the card
  // being rendered, so a Settle All under a search already settles exactly what
  // is on screen and nothing behind it.
  const [q, setQ] = useState('');
  const [autoSwitched, setAutoSwitched] = useState(''); // last term auto-switched for

  const fmtMoney = n => { const v = Number(n); return Number.isInteger(v) ? String(v) : v.toFixed(2); };
  const fmtHours = n => { const v = Number(n); return Number.isInteger(v) ? String(v) : String(Math.round(v * 100) / 100); };
  const fmtDate = d => { if (!d) return '—'; const dt = new Date(d); return isNaN(dt.getTime()) ? String(d) : dt.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' }); };

  const load = async () => {
    setLoading(true);
    const r = await axios.get('/api/agent/payroll');
    setStaff(r.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  // Manual refresh (17 Jul) — deliberately NO auto-refresh on Payroll: the page
  // holds live money inputs (inline adjustments), and a background refetch
  // could wipe a half-typed value. The button is the consent. Silent fetch —
  // no loading flash, so expanded rows keep their place; also refreshes the
  // open paid-history panel if there is one.
  const [refreshing, setRefreshing] = useState(false);
  const manualLoad = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const r = await axios.get('/api/agent/payroll');
      setStaff(r.data);
      if (evLoaded) await loadEvents(true); // Session P: keep the per-event view current too
      if (historyFor) {
        const h = await axios.get(`/api/agent/payroll/${historyFor}/history`);
        setHistory(h.data);
      }
    } finally { setRefreshing(false); }
  };

  // Session Y: search reads BOTH datasets. The per-event one is lazy — it only
  // fetches when the Per event tab is opened — so the first keystroke pulls it.
  const onSearch = (v) => {
    setQ(v);
    if (v.trim() && !evLoaded && !evLoading) loadEvents();
  };

  // Deep-link from Staff Portal "View pay": auto-expand that person once loaded
  useEffect(() => {
    if (focusStaff && staff.some(s => s.user_id === focusStaff)) {
      setExpanded(focusStaff);
      if (clearFocus) clearFocus();
    }
  }, [focusStaff, staff]);

  const openHistory = async (userId) => {
    if (historyFor === userId) { setHistoryFor(null); return; }
    setHistoryFor(userId); setHistoryLoading(true); setHistory([]);
    const r = await axios.get(`/api/agent/payroll/${userId}/history`);
    setHistory(r.data); setHistoryLoading(false);
  };

  const settle = async (ids) => {
    if (!ids.length) return;
    setSettling(true);
    try {
      await axios.patch('/api/agent/payroll/settle', { application_ids: ids });
      await load();
      if (evLoaded) await loadEvents(true); // Session P: per-event view reads the same flag
      if (historyFor) {
        const r = await axios.get(`/api/agent/payroll/${historyFor}/history`);
        setHistory(r.data);
      }
    } finally { setSettling(false); }
  };

  // ── Session P: per-event payroll functions ──
  const loadEvents = async (silent) => {
    if (!silent) setEvLoading(true);
    try {
      const r = await axios.get('/api/agent/payroll/by-event');
      setEvList(r.data); setEvLoaded(true);
    } finally { if (!silent) setEvLoading(false); }
  };
  useEffect(() => { if (view === 'event' && !evLoaded) loadEvents(); }, [view]);

  // Blob download — axios carries the JWT header (a plain link would send no token).
  const downloadPayments = async (ev, format) => {
    const key = `${ev.event_id}-${format}`;
    setEvDownloading(key);
    try {
      const r = await axios.get(`/api/agent/events/${ev.event_id}/payment-export?format=${format}`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = (`Payments - ${ev.title}${(ev.series_size||1)>1?` - Day ${ev.day_number}`:''} - ${ev.date}`.replace(/[^a-zA-Z0-9 \-_.]/g, '').trim() || 'payment-export') + `.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      // The server refuses a bank CSV with zero payable rows — surface its reason.
      let msg = 'Could not generate the export.';
      try { msg = JSON.parse(await e.response.data.text()).error || msg; } catch {}
      alert(msg);
    }
    setEvDownloading(null);
  };

  // Export ≠ settle: settling is the explicit, confirmed step AFTER the bank run.
  // Session M2: settle every unpaid day of a series in one action — the SAME
  // paid flag per application, just the union of the day rows' ids. Per-day
  // rows stay visible underneath (Catherine's per-day payment records).
  const settleSeries = async (days) => {
    const ids = days.flatMap(d => d.staff.filter(s => !s.paid && s.attendance !== 'no_show' && s.to_pay != null).map(s => s.application_id));
    if (ids.length === 0) return;
    const total = days.reduce((a, d) => a + Number(d.total_due || 0), 0);
    if (!window.confirm(
      `Mark ${ids.length} payment${ids.length === 1 ? '' : 's'} totalling R${fmtMoney(total)} as settled across ${days.length} day${days.length === 1 ? '' : 's'}?\n\nEvent: ${days[0].title} — Days ${days.map(d => d.day_number).join(', ')}\n\nSettled shifts drop off the per-staff outstanding view.`
    )) return;
    setSettling(true);
    try {
      await axios.patch('/api/agent/payroll/settle', { application_ids: ids });
      await Promise.all([loadEvents(true), load()]);
    } finally { setSettling(false); }
  };

  // Session M2: combined export — every day's rows in one bank file, each row's
  // OWN REFERENCE carrying its own day's date.
  const downloadSeries = async (anchor, days, format) => {
    setEvDownloading(`series-${anchor.parent_event_id||anchor.event_id}-${format}`);
    try {
      const r = await axios.get(`/api/agent/events/${anchor.event_id}/payment-export?format=${format}&series=1`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      // M2 fix (24 July): the server exports ENDED days only — name the file
      // from every ended day of this series in payroll (both tabs), not just
      // the rows visible under the current filter.
      const sk = anchor.parent_event_id || anchor.event_id;
      const ended = evList.filter(x => (x.series_size||1)>1 && (x.parent_event_id||x.event_id)===sk).sort((a2,b2)=>a2.day_number-b2.day_number);
      const nums = ended.map(d=>d.day_number);
      const contiguous = nums.every((n,i2)=>i2===0||n===nums[i2-1]+1);
      const label = nums.length===anchor.series_size ? `All ${anchor.series_size} days`
        : nums.length===1 ? `Day ${nums[0]} of ${anchor.series_size}`
        : `Days ${contiguous ? `${nums[0]}-${nums[nums.length-1]}` : nums.join('+')} of ${anchor.series_size}`;
      a.download = (`Payments - ${anchor.title} - ${label} - ${ended[0].date} to ${ended[ended.length-1].date}`.replace(/[^a-zA-Z0-9 \-_.]/g, '').trim() || 'payment-export') + `.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      // Session V (V-x): the per-event exports above parse the server's reason
      // out of the blob; this one threw it away and printed a fixed sentence,
      // so a zero-payable series read as a broken button.
      let msg = 'Could not generate the combined export. The XLSX version lists rows with issues.';
      try { msg = JSON.parse(await e.response.data.text()).error || msg; } catch (_) {}
      alert(msg);
    }
    setEvDownloading(null);
  };

  const settleEvent = async (ev) => {
    const ids = ev.staff.filter(s => !s.paid && s.attendance !== 'no_show' && s.to_pay != null).map(s => s.application_id);
    if (ids.length === 0) return;
    if (!window.confirm(
      `Mark ${ids.length} payment${ids.length === 1 ? '' : 's'} totalling R${fmtMoney(ev.total_due)} as settled?\n\nEvent: ${ev.title}${(ev.series_size||1)>1?` — Day ${ev.day_number} of ${ev.series_size}`:''}\n\nSettled shifts drop off the per-staff outstanding view.`
    )) return;
    setSettling(true);
    try {
      await axios.patch('/api/agent/payroll/settle', { application_ids: ids });
      await Promise.all([loadEvents(true), load()]);
    } finally { setSettling(false); }
  };
  const settleOne = async (s) => {
    setSettling(true);
    try {
      await axios.patch('/api/agent/payroll/settle', { application_ids: [s.application_id] });
      await Promise.all([loadEvents(true), load()]);
    } finally { setSettling(false); }
  };

  // Session P: per-event status chips. Amber = money waiting, blue = part-way,
  // green = done. Deliberately NOT var(--danger) — that stays destructive-only.
  const EV_STATUS = {
    unsettled: { label: 'Unsettled',         color: '#ca8a04',      bg: 'rgba(202,138,4,0.12)', border: 'rgba(202,138,4,0.45)' },
    partial:   { label: 'Partially settled', color: '#2563eb',      bg: 'rgba(37,99,235,0.10)', border: 'rgba(37,99,235,0.40)' },
    settled:   { label: 'Settled',           color: 'var(--green)', bg: 'rgba(22,163,74,0.10)', border: 'rgba(22,163,74,0.35)' },
    none:      { label: 'No pay due',        color: 'var(--text-muted)', bg: 'var(--bg-elevated)', border: 'var(--border)' },
  };

  // ── Session Y: the two match kinds ─────────────────────────────────────────
  const term = q.trim().toLowerCase();
  const searching = term.length > 0;
  const hit = (v) => String(v || '').toLowerCase().includes(term);

  // /api/agent/payroll deliberately drops anyone with a zero balance — correct
  // for a to-do list, wrong for a search box, where silence reads as a broken
  // search rather than "you owe them nothing". The per-event dataset carries
  // everyone who worked an ENDED event, paid or not, so a paid-up person is
  // synthesised from it with a zero balance and no shifts: the card then renders
  // its Paid history button and nothing settleable.
  // KNOWN LIMIT: someone on the books who has never worked an ended shift is in
  // neither dataset and will not be found here.
  const paidUpPeople = (() => {
    if (!searching) return [];
    const owed = new Set(staff.map(x => x.user_id));
    const seen = new Map();
    for (const ev of evList) for (const x of ev.staff) {
      if (owed.has(x.user_id) || seen.has(x.user_id) || !hit(x.name)) continue;
      seen.set(x.user_id, { user_id: x.user_id, name: x.name, picture: x.picture,
                            outstanding: 0, shifts: [], no_shows: [], paid_up: true });
    }
    return [...seen.values()].sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
  })();
  const peopleShown = searching ? [...staff.filter(x => hit(x.name)), ...paidUpPeople] : staff;

  // 21 July tweak: Outstanding = unsettled + partially settled (the default —
  // it IS the to-do pile: settling an event moves it off this list). Settled =
  // fully settled + nothing-due (the archive).
  const evOutstanding = evList.filter(ev => ev.status === 'unsettled' || ev.status === 'partial');
  const evSettled = evList.filter(ev => ev.status === 'settled' || ev.status === 'none');
  // Session Y: an event search IGNORES the Outstanding/Settled pill on purpose.
  // "What did Saturday cost me?" is answered by the whole event — filter to the
  // unpaid days only and the screen prints the leftover where the total belongs.
  // Settled days ride along marked Paid; they render no Settle button, and the
  // settle route refuses an already-paid row regardless of what is on screen.
  const evShown = searching ? evList.filter(ev => hit(ev.title))
    : evFilter === 'outstanding' ? evOutstanding : evSettled;

  // Put the agent on whichever side matched — ONCE per term, and only after the
  // per-event data has landed, or an in-flight fetch decides it wrongly. A manual
  // switch back must stick.
  useEffect(() => {
    if (!searching) { setAutoSwitched(''); return; }
    if (!evLoaded || autoSwitched === term) return;
    if (peopleShown.length > 0 && evShown.length === 0) setView('staff');
    else if (evShown.length > 0 && peopleShown.length === 0) setView('event');
    setAutoSwitched(term);
  }, [term, evLoaded, peopleShown.length, evShown.length]);

  const LBL = { fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 2 };
  const VAL = { fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' };
  const grandTotal = staff.reduce((s, x) => s + (x.outstanding || 0), 0);
  const shownTotal = searching ? peopleShown.reduce((s, x) => s + (x.outstanding || 0), 0) : grandTotal;

  return (
    <>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div className="agent-page-title">Payroll</div>
        <button title="Refresh" onClick={manualLoad} disabled={refreshing}
          style={{background:'none',border:'none',cursor:'pointer',color:'var(--text-secondary)',padding:4,fontSize:18,lineHeight:1,fontFamily:'var(--font)',opacity:refreshing?0.4:1}}>
          {'\u21BB'}
        </button>
      </div>
      {/* Session Y: one box, both questions. A person match and an event match
          are counted separately on the toggle below. */}
      <div style={{position:'relative',margin:'2px 0 10px',maxWidth:420}}>
        <input value={q} onChange={e=>onSearch(e.target.value)}
          placeholder="Search a person or an event..."
          style={{width:'100%',padding:'9px 30px 9px 12px',fontSize:13,fontFamily:'var(--font)',borderRadius:8,border:'1px solid var(--border)',background:'var(--bg-card)',color:'var(--text-primary)'}}/>
        {searching && (
          <button type="button" onClick={()=>onSearch('')} title="Clear search"
            style={{position:'absolute',right:6,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',color:'var(--text-muted)',fontSize:18,lineHeight:1,padding:4,fontFamily:'var(--font)'}}>&times;</button>
        )}
      </div>
      {/* Session P: two views, one paid flag — settle in either, both update */}
      <div style={{display:'flex',border:'1px solid var(--border)',borderRadius:8,overflow:'hidden',width:'fit-content',margin:'2px 0 10px'}}>
        {[['staff','Per staff'],['event','Per event']].map(([k,lbl])=>(
          <button key={k} type="button" onClick={()=>setView(k)}
            style={{padding:'8px 18px',fontSize:12,fontWeight:700,border:'none',cursor:'pointer',fontFamily:'var(--font)',background:view===k?'var(--accent)':'var(--bg-card)',color:view===k?'#fff':'var(--text-secondary)'}}>
            {lbl}{searching ? ` (${k==='staff'?peopleShown.length:evShown.length})` : ''}
          </button>
        ))}
      </div>
      <div className="agent-page-sub">{searching
        ? `${peopleShown.length} ${peopleShown.length===1?'person':'people'} and ${evShown.length} event${evShown.length===1?'':'s'} match "${q.trim()}"${evLoaded?'':' — still loading events...'}. Searching an event shows the whole event, settled days included.`
        : view==='staff'
        ? "Outstanding pay for confirmed shifts. Settle a single shift or everything at once — settled shifts move to that person's paid history."
        : "Ended events with their staff payments. Export the bank file, pay, then mark the event settled — settled amounts drop off the per-staff view automatically."}</div>

      {view === 'staff' ? (loading ? <div className="loading">Loading...</div> : peopleShown.length === 0 ? (
        <div className="empty-state">{searching
          ? `No one matching "${q.trim()}" has worked an ended shift.`
          : "Nothing outstanding — everyone's paid up."}</div>
      ) : (
        <>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',background:'var(--bg-surface)',border:'1px solid var(--border)',borderRadius:10,padding:'12px 16px',marginBottom:16}}>
            <span style={{fontSize:13,color:'var(--text-secondary)'}}>{peopleShown.length} {peopleShown.length===1?'person':'people'} {searching?'matching':'awaiting payment'}</span>
            <span style={{fontSize:14,fontWeight:800,color:'var(--text-primary)'}}>{searching?'Shown':'Total'} outstanding: <span style={{color:'var(--green)'}}>R{fmtMoney(shownTotal)}</span></span>
          </div>

          {peopleShown.map(person => {
            const open = expanded === person.user_id;
            const initials = (person.name||'?').trim().charAt(0).toUpperCase();
            return (
              <div key={person.user_id} style={{background:'var(--bg-surface)',border:'1px solid var(--border)',borderRadius:10,marginBottom:12,overflow:'hidden'}}>
                <div style={{display:'flex',alignItems:'center',gap:12,padding:'12px 16px',cursor:'pointer'}} onClick={()=>setExpanded(open?null:person.user_id)}>
                  {person.picture
                    ? <img src={thumbUrl(person.picture, 72)} alt="" style={{width:36,height:36,borderRadius:'50%',objectFit:'cover'}}/>
                    : <span style={{width:36,height:36,borderRadius:'50%',background:'var(--accent)',color:'#fff',display:'inline-flex',alignItems:'center',justifyContent:'center',fontSize:15,fontWeight:700,flexShrink:0}}>{initials}</span>}
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontWeight:700,fontSize:14,color:'var(--text-primary)'}}>{person.name||'—'}</div>
                    <div style={{fontSize:12,color:'var(--text-secondary)'}}>{person.paid_up
                      ? 'Paid up — nothing outstanding'
                      : `${person.shifts.length} unpaid shift${person.shifts.length===1?'':'s'}`}</div>
                  </div>
                  <div style={{textAlign:'right'}}>
                    <div style={LBL}>Outstanding</div>
                    <div style={{fontSize:16,fontWeight:800,color:'var(--green)'}}>R{fmtMoney(person.outstanding)}</div>
                  </div>
                  <Icon name="arrow" size={16}/>
                </div>

                {open && (
                  <div style={{borderTop:'1px solid var(--border)',padding:'8px 16px 16px'}}>
                    <div style={{display:'flex',justifyContent:'flex-end',gap:8,margin:'10px 0'}}>
                      <button className="btn btn-ghost btn-sm" onClick={()=>openHistory(person.user_id)}>
                        {historyFor===person.user_id ? 'Hide paid history' : 'Paid history'}
                      </button>
                      {person.shifts.length > 0 && (
                        <button className="btn btn-primary btn-sm" disabled={settling}
                          onClick={()=>settle(person.shifts.map(s=>s.application_id))}>
                          {settling ? 'Settling…' : `Settle all (R${fmtMoney(person.outstanding)})`}
                        </button>
                      )}
                    </div>

                    {person.shifts.map(s => (
                      <div key={s.application_id} style={{border:'1px solid var(--border)',borderRadius:8,padding:'10px 12px',marginBottom:8}}>
                        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8,gap:8,flexWrap:'wrap'}}>
                          <div>
                            <span style={{fontWeight:700,fontSize:13,color:'var(--text-primary)'}}>{s.title}</span>
                            <span style={{fontSize:12,color:'var(--text-secondary)',marginLeft:8}}>{fmtDate(s.date)}{s.role?` · ${s.role}`:''}</span>
                            {s.attendance==='late' && Number(s.adjustment)===0 && (
                              <span style={{marginLeft:8,padding:'3px 8px',borderRadius:6,background:'rgba(202,138,4,0.12)',border:'1px solid rgba(202,138,4,0.45)',color:'#ca8a04',fontSize:10,fontWeight:700,whiteSpace:'nowrap'}}>
                                ⏰ Late — review pay{s.attendance_note ? ` · ${s.attendance_note}` : ''}
                              </span>
                            )}
                          </div>
                          <button className="btn btn-accept btn-sm" disabled={settling}
                            onClick={()=>settle([s.application_id])}>Settle</button>
                        </div>
                        <div style={{display:'flex',gap:18,flexWrap:'wrap'}}>
                          <div><div style={LBL}>Rate</div><div style={VAL}>{s.rate_display||'—'}</div></div>
                          <div><div style={LBL}>{(s.pay_unit==='daily'||s.pay_unit==='per_event')?'Freq':'Hours'}</div><div style={VAL}>{s.pay_unit==='daily'?'1 day':s.pay_unit==='per_event'?'Flat (event)':(s.hours!=null?`${fmtHours(s.hours)}h`:'—')}</div></div>
                          <div><div style={LBL}>Earned</div><div style={VAL}>{s.earned!=null?`R${fmtMoney(s.earned)}`:'—'}</div></div>
                          <div>
                            <div style={LBL}>Adjustment</div>
                            <div style={{...VAL,color:Number(s.adjustment)<0?'var(--danger)':Number(s.adjustment)>0?'var(--green)':'var(--text-primary)'}}>
                              {Number(s.adjustment)?(Number(s.adjustment)>0?`+R${fmtMoney(s.adjustment)}`:`−R${fmtMoney(Math.abs(s.adjustment))}`):'—'}
                            </div>
                            {s.adjustment_note?<div style={{fontSize:10,color:'var(--text-muted)',maxWidth:160}}>{s.adjustment_note}</div>:null}
                          </div>
                          <div><div style={LBL}>To Pay</div><div style={{...VAL,color:'var(--green)',fontWeight:700}}>{s.to_pay!=null?`R${fmtMoney(s.to_pay)}`:'—'}</div></div>
                        </div>
                      </div>
                    ))}

                    {/* BOSSE Step 4: no-shows shown for the record only — never in outstanding or settled totals */}
                    {person.no_shows && person.no_shows.length > 0 && (
                      <div style={{marginTop:4,marginBottom:8,padding:'8px 12px',background:'rgba(220,38,38,0.05)',border:'1px solid rgba(220,38,38,0.2)',borderRadius:8,fontSize:12,color:'var(--text-secondary)'}}>
                        <span style={{fontWeight:700,color:'#dc2626'}}>No-shows ({person.no_shows.length}):</span>{' '}
                        {person.no_shows.map(n => `${n.title} (${fmtDate(n.date)}${n.role ? `, ${n.role}` : ''})`).join(' · ')}
                        <span style={{color:'var(--text-muted)'}}> — not owed, excluded from totals</span>
                      </div>
                    )}

                    {historyFor===person.user_id && (
                      <div style={{marginTop:10,borderTop:'1px dashed var(--border)',paddingTop:10}}>
                        <div style={{fontSize:12,fontWeight:700,color:'var(--text-secondary)',marginBottom:6}}>Paid history</div>
                        {historyLoading ? <div style={{fontSize:12,color:'var(--text-muted)'}}>Loading…</div>
                          : history.length===0 ? <div style={{fontSize:12,color:'var(--text-muted)'}}>No payments recorded yet.</div>
                          : history.map(h=>(
                            <div key={h.application_id} style={{display:'flex',justifyContent:'space-between',fontSize:12,padding:'4px 0',color:'var(--text-secondary)',gap:10}}>
                              <span style={{minWidth:0}}>{h.title}{h.role?` · ${h.role}`:''} <span style={{color:'var(--text-muted)'}}>({fmtDate(h.date)})</span></span>
                              <span style={{whiteSpace:'nowrap'}}><span style={{color:'var(--text-muted)',marginRight:10}}>{fmtDate(h.paid_at)}</span><strong style={{color:'var(--text-primary)'}}>{h.to_pay!=null?`R${fmtMoney(h.to_pay)}`:'—'}</strong></span>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </>
      )) : (
        /* ── Session P: per-event view ── */
        (evLoading && !evLoaded) ? <div className="loading">Loading...</div>
        : evList.length === 0 ? (
          <div className="empty-state">No ended events yet — payments appear here once an event finishes.</div>
        ) : (
          <>
            {/* 21 July tweak: Outstanding | Settled pills with counts.
                Session Y: hidden during a search — a search deliberately spans
                both, so leaving the pills on screen would misrepresent what is
                being shown. */}
            {!searching && (
            <div style={{display:'flex',gap:8,marginBottom:12}}>
              {[['outstanding',`Outstanding (${evOutstanding.length})`],['settled',`Settled (${evSettled.length})`]].map(([k,lbl])=>(
                <button key={k} type="button" onClick={()=>setEvFilter(k)}
                  style={{padding:'6px 14px',borderRadius:20,fontSize:11,fontWeight:700,cursor:'pointer',fontFamily:'var(--font)',border:`1px solid ${evFilter===k?'var(--accent)':'var(--border)'}`,background:evFilter===k?'rgba(var(--accent-rgb),0.08)':'var(--bg-card)',color:evFilter===k?'var(--accent)':'var(--text-secondary)'}}>
                  {lbl}
                </button>
              ))}
            </div>
            )}
            {evShown.length === 0 && (
              <div className="empty-state">{searching
                ? `No ended event matching "${q.trim()}".`
                : evFilter==='outstanding' ? 'Nothing outstanding — every ended event is settled.' : 'No settled events yet.'}</div>
            )}
            {(()=>{
              // Session M2: one card = one day (unchanged); days of a series
              // additionally roll up under a series wrapper with combined
              // totals, a combined bank export and settle-whole-series. The
              // per-day rows are KEPT — they ARE the payment records.
              const renderEvCard = (ev) => {
              const open = evExpanded === ev.event_id;
              const st = EV_STATUS[ev.status] || EV_STATUS.none;
              const missingBank = ev.staff.filter(s => !s.bank_ok && s.attendance !== 'no_show').length;
              return (
                <div key={ev.event_id} style={{background:'var(--bg-surface)',border:'1px solid var(--border)',borderRadius:10,marginBottom:12,overflow:'hidden'}}>
                  <div style={{display:'flex',alignItems:'center',gap:12,padding:'12px 16px',cursor:'pointer',flexWrap:'wrap'}} onClick={()=>setEvExpanded(open?null:ev.event_id)}>
                    <div style={{flex:1,minWidth:180}}>
                      <div style={{fontWeight:700,fontSize:14,color:'var(--text-primary)',display:'flex',alignItems:'center',flexWrap:'wrap',gap:6}}>
                        <span>{ev.title}</span>
                        {(ev.series_size||1)>1&&<span className="badge badge-series" style={{fontSize:9}}>DAY {ev.day_number}/{ev.series_size}</span>}
                      </div>
                      <div style={{fontSize:12,color:'var(--text-secondary)'}}>{fmtDate(ev.date)}{ev.time_start?` · ${ev.time_start}–${ev.time_end}`:''} · {ev.payable_count} payment{ev.payable_count===1?'':'s'}</div>
                    </div>
                    <span style={{padding:'4px 10px',borderRadius:6,fontSize:10,fontWeight:700,background:st.bg,border:`1px solid ${st.border}`,color:st.color,whiteSpace:'nowrap'}}>{st.label}</span>
                    <div style={{textAlign:'right'}}>
                      {/* Session Y: under a search the question is "what did this
                          cost me?", so the figure is the WHOLE event — settled plus
                          still-due. Outside a search the card keeps its to-do
                          reading: what is still owed. */}
                      <div style={LBL}>{searching?'Event total':ev.status==='settled'?'Settled':'Due'}</div>
                      <div style={{fontSize:16,fontWeight:800,color:(!searching&&ev.status==='settled')?'var(--text-secondary)':'var(--green)'}}>R{fmtMoney(searching?(Number(ev.total_due||0)+Number(ev.total_settled||0)):ev.status==='settled'?ev.total_settled:ev.total_due)}</div>
                    </div>
                    <Icon name="arrow" size={16}/>
                  </div>

                  {open && (
                    <div style={{borderTop:'1px solid var(--border)',padding:'8px 16px 16px'}}>
                      <div style={{display:'flex',justifyContent:'flex-end',gap:8,margin:'10px 0',flexWrap:'wrap'}}>
                        <button className="btn btn-ghost btn-sm" disabled={evDownloading===`${ev.event_id}-csv`} onClick={()=>downloadPayments(ev,'csv')}>
                          {evDownloading===`${ev.event_id}-csv` ? 'Exporting…' : '🏦 Bank CSV'}
                        </button>
                        <button className="btn btn-ghost btn-sm" disabled={evDownloading===`${ev.event_id}-xlsx`} onClick={()=>downloadPayments(ev,'xlsx')}>
                          {evDownloading===`${ev.event_id}-xlsx` ? 'Exporting…' : '📊 XLSX'}
                        </button>
                        {ev.total_due > 0 && (
                          <button className="btn btn-primary btn-sm" disabled={settling} onClick={()=>settleEvent(ev)}>
                            {settling ? 'Settling…' : `Mark event settled (R${fmtMoney(ev.total_due)})`}
                          </button>
                        )}
                      </div>

                      {missingBank > 0 && (
                        <div style={{marginBottom:8,padding:'8px 12px',background:'rgba(202,138,4,0.08)',border:'1px solid rgba(202,138,4,0.4)',borderRadius:8,fontSize:12,color:'var(--text-secondary)'}}>
                          ⚠️ {missingBank} staff member{missingBank===1?' is':'s are'} missing bank details — excluded from the bank CSV, flagged in the XLSX.
                        </div>
                      )}

                      {ev.staff.map(s => {
                        const noShow = s.attendance === 'no_show';
                        return (
                          <div key={s.application_id} style={{display:'flex',alignItems:'center',gap:10,border:'1px solid var(--border)',borderRadius:8,padding:'8px 12px',marginBottom:6,opacity:noShow?0.6:1}}>
                            <div style={{flex:1,minWidth:0}}>
                              <span style={{fontWeight:600,fontSize:13,color:'var(--text-primary)'}}>{s.name||'—'}</span>
                              {s.role && <span style={{fontSize:11,color:'var(--text-muted)',marginLeft:8}}>{s.role}</span>}
                              {noShow && <span style={{fontSize:10,fontWeight:700,color:'#dc2626',marginLeft:8}}>NO-SHOW — not owed</span>}
                              {!noShow && !s.bank_ok && <span style={{fontSize:10,fontWeight:700,color:'#ca8a04',marginLeft:8}}>⚠️ Missing bank details</span>}
                              {/* Session Y: same columns as the per-staff view, from the
                                  breakdown /payroll/by-event now returns. Search-only, so
                                  the Per event tab is unchanged when nobody is searching —
                                  drop the `searching &&` to show it always. */}
                              {searching && !noShow && (
                                <div style={{display:'flex',gap:16,flexWrap:'wrap',marginTop:6}}>
                                  <div><div style={LBL}>Rate</div><div style={VAL}>{s.rate_display||'—'}</div></div>
                                  <div><div style={LBL}>{(s.pay_unit==='daily'||s.pay_unit==='per_event')?'Freq':'Hours'}</div><div style={VAL}>{s.pay_unit==='daily'?'1 day':s.pay_unit==='per_event'?'Flat (event)':(s.hours!=null?`${fmtHours(s.hours)}h`:'—')}</div></div>
                                  <div><div style={LBL}>Earned</div><div style={VAL}>{s.earned!=null?`R${fmtMoney(s.earned)}`:'—'}</div></div>
                                  <div>
                                    <div style={LBL}>Adjustment</div>
                                    <div style={{...VAL,color:Number(s.adjustment)<0?'var(--danger)':Number(s.adjustment)>0?'var(--green)':'var(--text-primary)'}}>
                                      {Number(s.adjustment)?(Number(s.adjustment)>0?`+R${fmtMoney(s.adjustment)}`:`−R${fmtMoney(Math.abs(s.adjustment))}`):'—'}
                                    </div>
                                    {s.adjustment_note?<div style={{fontSize:10,color:'var(--text-muted)',maxWidth:160}}>{s.adjustment_note}</div>:null}
                                  </div>
                                </div>
                              )}
                            </div>
                            <span style={{fontSize:13,fontWeight:700,color:noShow?'var(--text-muted)':'var(--text-primary)',textDecoration:noShow?'line-through':'none'}}>{s.to_pay!=null?`R${fmtMoney(s.to_pay)}`:'—'}</span>
                            {!noShow && (s.paid
                              ? <span style={{fontSize:11,fontWeight:700,color:'var(--green)',whiteSpace:'nowrap'}}>✓ Paid{s.paid_at?` ${fmtDate(s.paid_at)}`:''}</span>
                              : <button className="btn btn-accept btn-sm" disabled={settling || s.to_pay==null} onClick={()=>settleOne(s)}>Settle</button>)}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
              };
              const groups = (()=>{ const out=[]; const map=new Map();
                for (const ev of evShown) {
                  if ((ev.series_size||1)>1) {
                    const k = ev.parent_event_id||ev.event_id;
                    if (!map.has(k)) { const g={type:'series',key:k,days:[]}; map.set(k,g); out.push(g); }
                    map.get(k).days.push(ev);
                  } else out.push({type:'single',ev});
                }
                return out; })();
              return (<>
                {groups.map(item => {
                  if (item.type==='single') return renderEvCard(item.ev);
                  const days = item.days;
                  const first = days[0], last = days[days.length-1];
                  const totalDue = days.reduce((a,d)=>a+Number(d.total_due||0),0);
                  const totalSettled = days.reduce((a,d)=>a+Number(d.total_settled||0),0);
                  const busyKey = `series-${first.parent_event_id||first.event_id}`;
                  return (
                    <div key={`s-${item.key}`} style={{border:'1px solid var(--border)',borderLeft:'3px solid var(--blue,#2563eb)',borderRadius:12,padding:'12px 14px',marginBottom:14,background:'var(--bg-card)'}}>
                      <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap',marginBottom:10}}>
                        <div style={{flex:1,minWidth:180}}>
                          <div style={{fontWeight:800,fontSize:14,color:'var(--text-primary)',display:'flex',alignItems:'center',flexWrap:'wrap',gap:6}}>
                            <span>{first.title}</span>
                            <span className="badge badge-series" style={{fontSize:9}}>MULTI-DAY</span>
                          </div>
                          <div style={{fontSize:12,color:'var(--text-secondary)'}}>{days.length} day{days.length===1?'':'s'} {searching?'in this event':evFilter==='outstanding'?'outstanding':'settled'} here · {fmtDate(first.date)} – {fmtDate(last.date)}</div>
                        </div>
                        <div style={{textAlign:'right'}}>
                          {/* Session Y: whole-series cost under a search, same reason
                              as the single-day card above. */}
                          <div style={LBL}>{searching?'Series total':evFilter==='settled'?'Settled (series)':'Due (series)'}</div>
                          <div style={{fontSize:16,fontWeight:800,color:(!searching&&evFilter==='settled')?'var(--text-secondary)':'var(--green)'}}>R{fmtMoney(searching?(totalDue+totalSettled):evFilter==='settled'?totalSettled:totalDue)}</div>
                        </div>
                      </div>
                      <div style={{display:'flex',justifyContent:'flex-end',gap:8,marginBottom:10,flexWrap:'wrap'}}>
                        <button className="btn btn-ghost btn-sm" disabled={evDownloading===`${busyKey}-csv`} onClick={()=>downloadSeries(first,days,'csv')}>
                          {evDownloading===`${busyKey}-csv` ? 'Exporting…' : '🏦 Combined bank CSV (all days)'}
                        </button>
                        <button className="btn btn-ghost btn-sm" disabled={evDownloading===`${busyKey}-xlsx`} onClick={()=>downloadSeries(first,days,'xlsx')}>
                          {evDownloading===`${busyKey}-xlsx` ? 'Exporting…' : '📊 Combined XLSX'}
                        </button>
                        {totalDue > 0 && (
                          <button className="btn btn-primary btn-sm" disabled={settling} onClick={()=>settleSeries(days)}>
                            {settling ? 'Settling…' : `Settle whole series (R${fmtMoney(totalDue)})`}
                          </button>
                        )}
                      </div>
                      {days.map(d => renderEvCard(d))}
                    </div>
                  );
                })}
              </>);
            })()}
          </>
        )
      )}
    </>
  );
}

export default function AgentDashboard() {
  const {logout}=useAuth();
  const [active,setActive]=useState('dashboard');
  const [payrollFocus,setPayrollFocus]=useState(null);
  const pages={dashboard:<DashboardHome setActive={setActive}/>,applications:<Applications/>,staff:<StaffList setActive={setActive} setPayrollFocus={setPayrollFocus}/>,events:<EventsList setActive={setActive}/>,payroll:<Payroll focusStaff={payrollFocus} clearFocus={()=>setPayrollFocus(null)}/>,post:<PostEvent setActive={setActive}/>,updates:<SendUpdate/>};
  return (
    <div className="agent-grid">
      <Sidebar active={active} setActive={setActive} logout={logout}/>
      <main className="agent-content">{pages[active]||pages.dashboard}</main>
    </div>
  );
}
